import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core';
import { z } from 'zod';

type ApprovalDashboardResponse = {
  id: number;
  transaction_id: string;
  date_time: string;
  section: string;
  amount: number;
  customer_name: string;
  description: string;
  cashier_name: string;
  reason_for_approval: string;
  transaction_limit: number;
  status: string;
  created_by: string;
  created_at: string;
  approved_by?: ((string | null)) | undefined;
  approved_at?: ((string | null)) | undefined;
  rejection_reason?: ((string | null)) | undefined;
  approved_count?: ((number | null)) | undefined;
  transactions?:
    | (
        | (Array<ApprovalDashboardResponse> | null)
      )
    | undefined;
};
type MobilePosBranchResponse = {
  branch_id: number;
  branch_name: string;
  accounts: Array<MobilePosAccountResponse>;
  user_name: string;
  branches: Array<MobilePosBranchResponse>;
};
type MobilePosAccountResponse = {
  account_id: number;
  account_number: string;
  amount: number;
  status: number;
  date: string;
  customer_name?: ((string | null)) | undefined;
  account_name?: ((string | null)) | undefined;
  transaction_number?: ((string | null)) | undefined;
  transaction_id?: ((number | null)) | undefined;
  gl_account_id?: ((number | null)) | undefined;
  ci_customer_id?: ((number | null)) | undefined;
};
type PlAccountResponse = Partial<{
  id: (number | null);
  ci_customer_id: (number | null);
  pl_account_type_id: (number | null);
  branch_id: (number | null);
  gl_account_id: (number | null);
  gl_interest_acc: (number | null);
  gl_penalty_acc: (number | null);
  gl_insurance_acc: (number | null);
  gl_fee_acc: (number | null);
  gl_interest_res_acc: (number | null);
  gl_tax_account: (number | null);
  joint_customer: (number | null);
  ref_account_number: (string | null);
  account_number: (string | null);
  product_name: (string | null);
  loan_deduction_savings_id: (number | null);
  interest_update_account: (number | null);
  open_date: (string | null);
  reneve_date: (string | null);
  approved: (number | null);
  capital: (number | null);
  loan_capital?: (number | null);
  balance: (number | null);
  lowest_balance: (number | null);
  minimum_balance: (number | null);
  no_of_interest_pay: (number | null);
  period: (number | null);
  capital_installment: (number | null);
  total_installment: (number | null);
  save_interest_temp: (number | null);
  interest_rate: (number | null);
  penalty_rate: (number | null);
  insurance_rate: (number | null);
  fee_rate: (number | null);
  interest: (number | null);
  due_interest: (number | null);
  penalty: (number | null);
  insurance: (number | null);
  fee: (number | null);
  past_due: (number | null);
  past_due_days: (number | null);
  past_due_amount: (number | null);
  note: (string | null);
  pb_page_no: (number | null);
  pb_line_no: (number | null);
  pb: (number | null);
  standing_order_amount: (number | null);
  tax: (number | null);
  tax_rate: (number | null);
  mobile_pos_update: (number | null);
  last_transaction_date: (string | null);
  withdraw_instruction: (string | null);
  status: (number | null);
  opening_date: (string | null);
  opening_balance: (number | null);
  institute_name: (string | null);
  account_type: (PlAccountType | null);
  category:
    | (PlAccountTypeCategory | null);
  branch: (GlBranch | null);
  existing_loans:
    | (Array<PlAccountResponse> | null);
  pawning_items:
    | (Array<PlPawningItemResponse> | null);
  gold_weight_gram2: (number | null);
  market_value2: (number | null);
  accessed_value2: (number | null);
  fd_account_id_for_loan: (number | null);
}>;
type PlAccountType = Partial<{
  id: (number | null);
  it_institute_id: (number | null);
  gl_coa_id: (number | null);
  gl_interest_id: (number | null);
  gl_penalty_id: (number | null);
  gl_insurance_id: (number | null);
  gl_fee_id: (number | null);
  gl_interest_res_id: (number | null);
  gl_tax_account: (number | null);
  last_number: (number | null);
  ref_number: (string | null);
  name_ln1: (string | null);
  name_ln2: (string | null);
  name_ln3: (string | null);
  period_policy: (number | null);
  period_max: (number | null);
  period_min: (number | null);
  interest_policy: (number | null);
  interest_update_policy: (number | null);
  interest_rate_max: (number | null);
  interest_rate_min: (number | null);
  penalty_policy: (number | null);
  penalty_rate: (number | null);
  insurance_policy: (number | null);
  insurance_rate: (number | null);
  fee_policy: (number | null);
  fee_rate: (number | null);
  depreciation_rate: (number | null);
  pl_account_category_id: (number | null);
  pl_operating_type_id: (number | null);
  minimum_balance: (number | null);
  field_collection: (number | null);
  allow_interest_edit: (number | null);
  loan_schedule_type: (number | null);
  tax_minimum_balance: (number | null);
  tax_rate: (number | null);
  interest_benefit_max_int: (number | null);
  auto_number_creation: (boolean | null);
  allow_manual_interest: (number | null);
  no_of_loan_guarantor: (number | null);
  passdue_policy: (number | null);
  fd_loan_margin: (number | null);
  fd_loan_inte_gap: (number | null);
  pb_type: (number | null);
  decimal_number: (number | null);
  status: (number | null);
  c_at: (string | null);
  c_by: (number | null);
  m_at: (string | null);
  m_by: (number | null);
}>;
type PlAccountTypeCategory = Partial<{
  id: (number | null);
  category_name: (string | null);
  status: (number | null);
}>;
type GlBranch = Partial<{
  id: (number | null);
  it_institute_id: (number | null);
  ref_number: (string | null);
  name_ln1: (string | null);
  name_ln2: (string | null);
  name_ln3: (string | null);
  address_ln1: (string | null);
  address_ln2: (string | null);
  address_ln3: (string | null);
  e_mail: (string | null);
  telephone: (string | null);
  sms_status: (number | null);
  client_app_status: (number | null);
  c_at: (string | null);
  c_by: (number | null);
  m_at: (string | null);
  m_by: (number | null);
  status: (number | null);
}>;
type PlPawningItemResponse = Partial<{
  id: (number | null);
  pl_account_id: (number | null);
  pl_pawning_item_types_id: (number | null);
  weight_gram: (number | null);
  gold_weight_gram: (number | null);
  carat: (unknown | null);
  accessed_value: (number | null);
  market_value: (number | null);
  quantity: (number | null);
  capital: (number | null);
  description: (string | null);
  pawning_item_type:
    | (PlPawningItemTypeResponse | null);
}>;
type PlPawningItemTypeResponse = Partial<{
  id: (number | null);
  name_ln1: (string | null);
  name_ln2: (string | null);
  name_ln3: (string | null);
}>;
type SystemMenuWithChildrenResponse = Partial<{
  id: (number | null);
  label: (string | null);
  to_url: (string | null);
  parent_id: (number | null);
  order_num: (number | null);
  component: (string | null);
  icon: (string | null);
  has_menu: (boolean | null);
  c_at: (string | null);
  c_by: (number | null);
  m_at: (string | null);
  m_by: (number | null);
  children: Array<SystemMenuWithChildrenResponse>;
}>;

const LoginRequest = z
  .object({
    user_name: z.string().min(3).max(100),
    password: z.string().min(8).max(128),
  })
  .passthrough();
const GlBranch = z
  .object({
    id: z.union([z.number(), z.null()]),
    it_institute_id: z.union([z.number(), z.null()]),
    ref_number: z.union([z.string(), z.null()]),
    name_ln1: z.union([z.string(), z.null()]),
    name_ln2: z.union([z.string(), z.null()]),
    name_ln3: z.union([z.string(), z.null()]),
    address_ln1: z.union([z.string(), z.null()]),
    address_ln2: z.union([z.string(), z.null()]),
    address_ln3: z.union([z.string(), z.null()]),
    e_mail: z.union([z.string(), z.null()]),
    telephone: z.union([z.string(), z.null()]),
    sms_status: z.union([z.number(), z.null()]),
    client_app_status: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const ItUserGroup = z
  .object({
    id: z.union([z.number(), z.null()]),
    group_name: z.union([z.string(), z.null()]),
    access_level: z.union([z.number(), z.null()]).optional(),
    level: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const ItInstitute = z
  .object({
    id: z.union([z.number(), z.null()]),
    name_ln1: z.union([z.string(), z.null()]),
    name_ln2: z.union([z.string(), z.null()]),
    name_ln3: z.union([z.string(), z.null()]),
    address_ln1: z.union([z.string(), z.null()]),
    address_ln2: z.union([z.string(), z.null()]),
    address_ln3: z.union([z.string(), z.null()]),
    email: z.union([z.string(), z.null()]),
    phone: z.union([z.string(), z.null()]),
    reg_num: z.union([z.string(), z.null()]),
    reg_date: z.union([z.string(), z.null()]),
    sms_mask: z.union([z.string(), z.null()]),
    sms_mask_id: z.union([z.string(), z.null()]),
    sms_active: z.union([z.number(), z.null()]).default(0),
    swift_code: z.union([z.string(), z.null()]),
    coop_fund: z.union([z.number(), z.null()]),
    statu_reserves: z.union([z.number(), z.null()]),
    profit: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
    age_min: z.union([z.number(), z.null()]),
    age_max: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const LoginResponse = z
  .object({
    id: z.number().int(),
    user_id: z.number().int(),
    user_name: z.string(),
    name: z.string(),
    user_role: z.union([z.string(), z.null()]).optional(),
    active_branch_id: z.union([z.number(), z.null()]),
    user_group_id: z.union([z.number(), z.null()]),
    active_branch: z.union([GlBranch, z.null()]),
    user_group: z.union([ItUserGroup, z.null()]),
    institute: z.union([ItInstitute, z.null()]),
    ref_number: z.union([z.string(), z.null()]).optional(),
    access_token: z.string(),
    refresh_token: z.string(),
  })
  .passthrough();
const APIResponse_LoginResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([LoginResponse, z.null()]),
  })
  .partial()
  .passthrough();
const ValidationError = z
  .object({
    loc: z.array(z.union([z.string(), z.number()])),
    msg: z.string(),
    type: z.string(),
  })
  .passthrough();
const HTTPValidationError = z
  .object({ detail: z.array(ValidationError) })
  .partial()
  .passthrough();
const APIResponse = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.unknown(), z.null()]),
  })
  .partial()
  .passthrough();
const RefreshTokenResponse = z
  .object({
    id: z.number().int(),
    user_id: z.number().int(),
    user_name: z.string(),
    name: z.string(),
    user_role: z.union([z.string(), z.null()]).optional(),
    active_branch_id: z.union([z.number(), z.null()]),
    user_group_id: z.union([z.number(), z.null()]),
    active_branch: z.union([GlBranch, z.null()]),
    user_group: z.union([ItUserGroup, z.null()]),
    institute: z.union([ItInstitute, z.null()]),
    ref_number: z.union([z.string(), z.null()]).optional(),
    access_token: z.string(),
  })
  .passthrough();
const APIResponse_RefreshTokenResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([RefreshTokenResponse, z.null()]),
  })
  .partial()
  .passthrough();
const FirstTimeLoginRequest = z
  .object({
    user_id: z.number().int(),
    user_name: z.string().min(3).max(100),
    updated_user_name: z.string().min(3).max(100),
    password: z.string().min(8).max(128),
    token: z.string(),
  })
  .passthrough();
const AuthResponse = z
  .object({
    id: z.number().int(),
    user_id: z.number().int(),
    user_name: z.string(),
    name: z.string(),
    user_role: z.union([z.string(), z.null()]).optional(),
    active_branch_id: z.union([z.number(), z.null()]),
    user_group_id: z.union([z.number(), z.null()]),
    active_branch: z.union([GlBranch, z.null()]),
    user_group: z.union([ItUserGroup, z.null()]),
    institute: z.union([ItInstitute, z.null()]),
    ref_number: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_AuthResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([AuthResponse, z.null()]),
  })
  .partial()
  .passthrough();
const CashBookResponse = z
  .object({ cash_book_id: z.number().int(), cash_book_name: z.string() })
  .passthrough();
const APIResponse_list_CashBookResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(CashBookResponse), z.null()]),
  })
  .partial()
  .passthrough();
const CashierSignInRequest = z
  .object({
    amount: z.union([z.number(), z.string()]),
    cash_book: z.number().int(),
  })
  .passthrough();
const CashierSignInResponse = z
  .object({
    it_cashier_session_id: z.number().int(),
    it_cashier_session_cash_book_id: z.number().int(),
    it_cashier_session_details_id: z.number().int(),
  })
  .passthrough();
const APIResponse_CashierSignInResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([CashierSignInResponse, z.null()]),
  })
  .partial()
  .passthrough();
const CashierSignOffDataResponse = z
  .object({
    it_cashier_session_id: z.number().int(),
    it_cashier_session_cash_book_id: z.number().int(),
    cash_book_name: z.string(),
    cash_in: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    cash_out: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    cash_receipt: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    cash_payment: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    cheque_receipt: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    cheque_payment: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    opening_cash: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    closing_cash: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
  })
  .passthrough();
const APIResponse_CashierSignOffDataResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([CashierSignOffDataResponse, z.null()]),
  })
  .partial()
  .passthrough();
const CashierSignOffRequest = z
  .object({
    it_cashier_session_id: z.number().int(),
    it_cashier_session_cash_book_id: z.number().int(),
    cash_in: z.union([z.number(), z.string()]),
    cash_out: z.union([z.number(), z.string()]),
    cash_receipt: z.union([z.number(), z.string()]),
    cash_payment: z.union([z.number(), z.string()]),
    cheque_receipt: z.union([z.number(), z.string()]),
    cheque_payment: z.union([z.number(), z.string()]),
    closing_cash: z.union([z.number(), z.string()]),
  })
  .passthrough();
const CashierSignOffResponse = z
  .object({
    it_cashier_session_id: z.number().int(),
    status: z.string(),
    sign_off_time: z.string().datetime({ offset: true }),
    message: z.string(),
  })
  .passthrough();
const APIResponse_CashierSignOffResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([CashierSignOffResponse, z.null()]),
  })
  .partial()
  .passthrough();
const CashierStatusResponse = z
  .object({
    signed_in: z.boolean(),
    cash_book: z.union([CashBookResponse, z.null()]),
  })
  .passthrough();
const APIResponse_CashierStatusResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([CashierStatusResponse, z.null()]),
  })
  .partial()
  .passthrough();
const paymentOnly = z.union([z.boolean(), z.null()]).optional();
const GlTransactionHeader = z
  .object({
    id: z.union([z.number(), z.null()]),
    branch_id: z.union([z.number(), z.null()]),
    transaction_number: z.union([z.string(), z.null()]),
    enterd_date: z.union([z.string(), z.null()]),
    approved_date: z.union([z.string(), z.null()]),
    updated_date: z.union([z.string(), z.null()]),
    entered_by: z.union([z.number(), z.null()]),
    approved_by: z.union([z.number(), z.null()]),
    updated_by: z.union([z.number(), z.null()]),
    amount: z.union([z.number(), z.null()]),
    name: z.union([z.string(), z.null()]),
    address: z.union([z.string(), z.null()]),
    description: z.union([z.string(), z.null()]),
    receipt_print: z.union([z.number(), z.null()]),
    it_transaction_type_id: z.union([z.number(), z.null()]),
    ref_1: z.union([z.string(), z.null()]),
    status: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
    ref_ib: z.union([z.string(), z.null()]),
    from_mobile: z.union([z.boolean(), z.null()]).default(false),
    is_batch: z.union([z.boolean(), z.null()]).default(false),
  })
  .partial()
  .passthrough();
const APIResponse_GlTransactionHeader_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([GlTransactionHeader, z.null()]),
  })
  .partial()
  .passthrough();
const PlAccountType = z
  .object({
    id: z.union([z.number(), z.null()]),
    it_institute_id: z.union([z.number(), z.null()]),
    gl_coa_id: z.union([z.number(), z.null()]),
    gl_interest_id: z.union([z.number(), z.null()]),
    gl_penalty_id: z.union([z.number(), z.null()]),
    gl_insurance_id: z.union([z.number(), z.null()]),
    gl_fee_id: z.union([z.number(), z.null()]),
    gl_interest_res_id: z.union([z.number(), z.null()]),
    gl_tax_account: z.union([z.number(), z.null()]),
    last_number: z.union([z.number(), z.null()]),
    ref_number: z.union([z.string(), z.null()]),
    name_ln1: z.union([z.string(), z.null()]),
    name_ln2: z.union([z.string(), z.null()]),
    name_ln3: z.union([z.string(), z.null()]),
    period_policy: z.union([z.number(), z.null()]),
    period_max: z.union([z.number(), z.null()]),
    period_min: z.union([z.number(), z.null()]),
    interest_policy: z.union([z.number(), z.null()]),
    interest_update_policy: z.union([z.number(), z.null()]),
    interest_rate_max: z.union([z.number(), z.null()]),
    interest_rate_min: z.union([z.number(), z.null()]),
    penalty_policy: z.union([z.number(), z.null()]),
    penalty_rate: z.union([z.number(), z.null()]),
    insurance_policy: z.union([z.number(), z.null()]),
    insurance_rate: z.union([z.number(), z.null()]),
    fee_policy: z.union([z.number(), z.null()]),
    fee_rate: z.union([z.number(), z.null()]),
    depreciation_rate: z.union([z.number(), z.null()]),
    pl_account_category_id: z.union([z.number(), z.null()]),
    pl_operating_type_id: z.union([z.number(), z.null()]),
    minimum_balance: z.union([z.number(), z.null()]),
    field_collection: z.union([z.number(), z.null()]),
    allow_interest_edit: z.union([z.number(), z.null()]),
    loan_schedule_type: z.union([z.number(), z.null()]),
    tax_minimum_balance: z.union([z.number(), z.null()]),
    tax_rate: z.union([z.number(), z.null()]),
    interest_benefit_max_int: z.union([z.number(), z.null()]),
    auto_number_creation: z.union([z.boolean(), z.null()]).default(true),
    no_of_loan_guarantor: z.union([z.number(), z.null()]),
    passdue_policy: z.union([z.number(), z.null()]),
    fd_loan_margin: z.union([z.number(), z.null()]),
    fd_loan_inte_gap: z.union([z.number(), z.null()]),
    pb_type: z.union([z.number(), z.null()]),
    decimal_number: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const PlAccountTypeCategory = z
  .object({
    id: z.union([z.number(), z.null()]),
    category_name: z.union([z.string(), z.null()]),
    status: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const PlPawningItemTypeResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    name_ln1: z.union([z.string(), z.null()]),
    name_ln2: z.union([z.string(), z.null()]),
    name_ln3: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const PlPawningItemResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    pl_account_id: z.union([z.number(), z.null()]),
    pl_pawning_item_types_id: z.union([z.number(), z.null()]),
    weight_gram: z.union([z.number(), z.null()]),
    gold_weight_gram: z.union([z.number(), z.null()]),
    carat: z.union([z.unknown(), z.null()]),
    accessed_value: z.union([z.number(), z.null()]),
    market_value: z.union([z.number(), z.null()]),
    quantity: z.union([z.number(), z.null()]),
    capital: z.union([z.number(), z.null()]),
    description: z.union([z.string(), z.null()]),
    pawning_item_type: z.union([PlPawningItemTypeResponse, z.null()]),
  })
  .partial()
  .passthrough();
const PlAccountResponse: z.ZodType<PlAccountResponse> = z.lazy(() =>
  z
    .object({
      id: z.union([z.number(), z.null()]),
      ci_customer_id: z.union([z.number(), z.null()]),
      pl_account_type_id: z.union([z.number(), z.null()]),
      branch_id: z.union([z.number(), z.null()]),
      gl_account_id: z.union([z.number(), z.null()]),
      gl_interest_acc: z.union([z.number(), z.null()]),
      gl_penalty_acc: z.union([z.number(), z.null()]),
      gl_insurance_acc: z.union([z.number(), z.null()]),
      gl_fee_acc: z.union([z.number(), z.null()]),
      gl_interest_res_acc: z.union([z.number(), z.null()]),
      gl_tax_account: z.union([z.number(), z.null()]),
      joint_customer: z.union([z.number(), z.null()]),
      ref_account_number: z.union([z.string(), z.null()]),
      account_number: z.union([z.string(), z.null()]),
      product_name: z.union([z.string(), z.null()]),
      loan_deduction_savings_id: z.union([z.number(), z.null()]),
      interest_update_account: z.union([z.number(), z.null()]),
      open_date: z.union([z.string(), z.null()]),
      reneve_date: z.union([z.string(), z.null()]),
      approved: z.union([z.number(), z.null()]),
      capital: z.union([z.number(), z.null()]),
      loan_capital: z.union([z.number(), z.null()]).optional(),
      balance: z.union([z.number(), z.null()]),
      lowest_balance: z.union([z.number(), z.null()]),
      minimum_balance: z.union([z.number(), z.null()]),
      no_of_interest_pay: z.union([z.number(), z.null()]),
      period: z.union([z.number(), z.null()]),
      capital_installment: z.union([z.number(), z.null()]),
      total_installment: z.union([z.number(), z.null()]),
      save_interest_temp: z.union([z.number(), z.null()]),
      interest_rate: z.union([z.number(), z.null()]),
      penalty_rate: z.union([z.number(), z.null()]),
      insurance_rate: z.union([z.number(), z.null()]),
      fee_rate: z.union([z.number(), z.null()]),
      interest: z.union([z.number(), z.null()]),
      due_interest: z.union([z.number(), z.null()]),
      penalty: z.union([z.number(), z.null()]),
      insurance: z.union([z.number(), z.null()]),
      fee: z.union([z.number(), z.null()]),
      past_due: z.union([z.number(), z.null()]),
      past_due_days: z.union([z.number(), z.null()]),
      past_due_amount: z.union([z.number(), z.null()]),
      note: z.union([z.string(), z.null()]),
      pb_page_no: z.union([z.number(), z.null()]),
      pb_line_no: z.union([z.number(), z.null()]),
      pb: z.union([z.number(), z.null()]),
      standing_order_amount: z.union([z.number(), z.null()]),
      tax: z.union([z.number(), z.null()]),
      tax_rate: z.union([z.number(), z.null()]),
      mobile_pos_update: z.union([z.number(), z.null()]),
      last_transaction_date: z.union([z.string(), z.null()]),
      withdraw_instruction: z.union([z.string(), z.null()]),
      status: z.union([z.number(), z.null()]),
      opening_date: z.union([z.string(), z.null()]),
      opening_balance: z.union([z.number(), z.null()]),
      institute_name: z.union([z.string(), z.null()]),
      account_type: z.union([PlAccountType, z.null()]),
      category: z.union([PlAccountTypeCategory, z.null()]),
      branch: z.union([GlBranch, z.null()]),
      existing_loans: z.union([z.array(PlAccountResponse), z.null()]),
      pawning_items: z.union([z.array(PlPawningItemResponse), z.null()]),
      gold_weight_gram2: z.union([z.number(), z.null()]),
      market_value2: z.union([z.number(), z.null()]),
      accessed_value2: z.union([z.number(), z.null()]),
    })
    .partial()
    .passthrough()
);
const ItTransactionType = z
  .object({
    id: z.union([z.number(), z.null()]),
    type_name: z.union([z.string(), z.null()]),
    prefix: z.union([z.string(), z.null()]),
    note: z.union([z.string(), z.null()]),
    status: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const PlTransactionResponse = z
  .object({
    id: z.number().int(),
    pl_account_id: z.number().int(),
    transaction_header_id: z.number().int(),
    entered_date: z.union([z.string(), z.null()]),
    updated_date: z.union([z.string(), z.null()]),
    entered_by: z.union([z.number(), z.null()]),
    approved_by: z.union([z.number(), z.null()]),
    updated_by: z.union([z.number(), z.null()]),
    entered_by_code: z.union([z.string(), z.null()]).optional(),
    updated_by_code: z.union([z.string(), z.null()]).optional(),
    debit: z.union([z.number(), z.null()]),
    credit: z.union([z.number(), z.null()]),
    balance: z.union([z.number(), z.null()]),
    interest: z.union([z.number(), z.null()]),
    penalty: z.union([z.number(), z.null()]),
    fund: z.union([z.number(), z.null()]),
    fee: z.union([z.number(), z.null()]),
    description: z.union([z.string(), z.null()]),
    cheque_num: z.union([z.string(), z.null()]),
    page_num: z.union([z.number(), z.null()]),
    line_num: z.union([z.number(), z.null()]),
    pb_print: z.union([z.number(), z.null()]),
    tax_amount: z.union([z.number(), z.null()]),
    it_transaction_type_id: z.union([z.number(), z.null()]),
    passdue_amount: z.union([z.number(), z.null()]),
    passdue_days: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
    transaction_number: z.union([z.string(), z.null()]),
    code: z.union([z.record(z.string(), z.any()), z.null()]).optional(),
    pl_account: z.union([PlAccountResponse, z.null()]).optional(),
    transaction_header: z.union([GlTransactionHeader, z.null()]).optional(),
    transaction_type: z.union([ItTransactionType, z.null()]).optional(),
  })
  .passthrough();
const APIResponse_PlTransactionResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PlTransactionResponse, z.null()]),
  })
  .partial()
  .passthrough();
const limit = z.union([z.number(), z.null()]).optional().default(10);
const APIResponse_list_PlTransactionResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PlTransactionResponse), z.null()]),
  })
  .partial()
  .passthrough();
const AccountTypeEnum = z.enum([
  'pl_account',
  'gl_account',
  'pl_equipment',
  'pl_investment',
  'invoice',
  'purchasing',
  'pay_to_creditor',
]);
const Accounts = z
  .object({
    account_id: z.number().int(),
    amount: z.number().gt(0),
    account_type: AccountTypeEnum,
    temp_transaction_id: z.union([z.number(), z.null()]).optional(),
    description: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const ReceiptRequest = z
  .object({
    member_id: z.number().int(),
    cash_book_id: z.number().int(),
    accounts: z.array(Accounts),
    total_amount: z.number().gt(0),
    cheque_num: z.union([z.string(), z.null()]).optional(),
    bank_name: z.union([z.number(), z.null()]).optional(),
    note: z.union([z.string(), z.null()]).optional(),
    ci_customer_id: z.union([z.number(), z.null()]).optional(),
    non_customer_name: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const TransactionResponse = z
  .object({
    queue_id: z.string(),
    type: z.string(),
    status: z.string().optional().default('success'),
    message: z
      .string()
      .optional()
      .default('Transaction added to queue successfully.'),
  })
  .passthrough();
const APIResponse_TransactionResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([TransactionResponse, z.null()]),
  })
  .partial()
  .passthrough();
const WithdrawRequest = z
  .object({
    member_id: z.number().int(),
    cash_book_id: z.number().int(),
    account_id: z.number().int(),
    amount: z.number().gt(0),
    note: z.union([z.string(), z.null()]).optional(),
    cheque_num: z.union([z.string(), z.null()]).optional(),
    bank_name: z.union([z.number(), z.null()]).optional(),
    cus_nic_no: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const GlCoa = z
  .object({
    id: z.union([z.number(), z.null()]),
    gl_coa_type_id: z.union([z.number(), z.null()]),
    ref_number: z.union([z.string(), z.null()]),
    name_ln1: z.union([z.string(), z.null()]),
    name_ln2: z.union([z.string(), z.null()]),
    name_ln3: z.union([z.string(), z.null()]),
    gl_operation_type_id: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const GlCoaType = z
  .object({
    id: z.union([z.number(), z.null()]),
    it_institute_id: z.union([z.number(), z.null()]),
    gl_coa_type_group_id: z.union([z.number(), z.null()]),
    gl_note_policy_id: z.union([z.number(), z.null()]),
    cf_note_id: z.union([z.number(), z.null()]),
    cc_note_id: z.union([z.number(), z.null()]),
    operation_filter: z.union([z.number(), z.null()]),
    cc_description: z.union([z.string(), z.null()]),
    name_ln1: z.union([z.string(), z.null()]),
    name_ln2: z.union([z.string(), z.null()]),
    name_ln3: z.union([z.string(), z.null()]),
    status: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const GlSection = z
  .object({
    id: z.union([z.number(), z.null()]),
    it_institute_id: z.union([z.number(), z.null()]),
    ref_number: z.union([z.string(), z.null()]),
    section_name_ln1: z.union([z.string(), z.null()]),
    section_name_ln2: z.union([z.string(), z.null()]),
    section_name_ln3: z.union([z.string(), z.null()]),
    note: z.union([z.string(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const GLAccountResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    gl_coa_id: z.union([z.number(), z.null()]),
    branch_id: z.union([z.number(), z.null()]),
    section_id: z.union([z.number(), z.null()]),
    ref_number: z.union([z.string(), z.null()]),
    budget: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
    gl_coa: z.union([GlCoa, z.null()]),
    gl_coa_type: z.union([GlCoaType, z.null()]),
    section: z.union([GlSection, z.null()]),
    branch: z.union([GlBranch, z.null()]).optional(),
  })
  .passthrough();
const GlTransactionResponse = z
  .object({
    id: z.number().int(),
    gl_account_id: z.number().int(),
    transaction_header_id: z.number().int(),
    fiscal_year_id: z.union([z.number(), z.null()]).optional(),
    ci_customer_id: z.union([z.number(), z.null()]).optional(),
    enterd_date: z.union([z.string(), z.null()]).optional(),
    updated_date: z.union([z.string(), z.null()]).optional(),
    entered_by: z.union([z.number(), z.null()]).optional(),
    approved_by: z.union([z.number(), z.null()]).optional(),
    updated_by: z.union([z.number(), z.null()]).optional(),
    debit: z.number(),
    credit: z.number(),
    balance: z.union([z.number(), z.null()]),
    description: z.union([z.string(), z.null()]),
    it_transaction_type_id: z.union([z.number(), z.null()]).optional(),
    cash_bank_gl_account: z.union([z.number(), z.null()]).optional(),
    cheque_num: z.union([z.string(), z.null()]),
    cheque_date: z.union([z.string(), z.null()]),
    status: z.number().int(),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
    customer_number: z.union([z.string(), z.null()]),
    ini_name_ln1: z.union([z.string(), z.null()]),
    address_ln1: z.union([z.string(), z.null()]),
    ini_name_ln2: z.union([z.string(), z.null()]),
    address_ln2: z.union([z.string(), z.null()]),
    ref_1: z.union([z.string(), z.null()]),
    gl_account: z.union([GLAccountResponse, z.null()]),
    transaction_type: z.union([ItTransactionType, z.null()]).optional(),
    transaction_header: z.union([GlTransactionHeader, z.null()]).optional(),
  })
  .passthrough();
const GlTransactionHeaderResponse = z
  .object({
    id: z.number().int(),
    branch_id: z.number().int(),
    transaction_number: z.string(),
    enterd_date: z.string().datetime({ offset: true }),
    approved_date: z.union([z.string(), z.null()]),
    updated_date: z.union([z.string(), z.null()]),
    entered_by: z.number().int(),
    approved_by: z.number().int(),
    updated_by: z.union([z.number(), z.null()]),
    amount: z.number(),
    name: z.union([z.string(), z.null()]),
    address: z.union([z.string(), z.null()]),
    description: z.union([z.string(), z.null()]),
    receipt_print: z.union([z.number(), z.null()]),
    it_transaction_type_id: z.number().int(),
    ref_1: z.union([z.string(), z.null()]),
    status: z.number().int(),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
    ref_ib: z.union([z.string(), z.null()]),
    it_transaction_type: z.union([ItTransactionType, z.null()]).optional(),
    pl_transactions: z
      .union([z.array(PlTransactionResponse), z.null()])
      .optional(),
    gl_transactions: z
      .union([z.array(GlTransactionResponse), z.null()])
      .optional(),
    print_payload: z
      .union([z.object({}).partial().passthrough(), z.null()])
      .optional(),
    print_config: z
      .union([z.object({}).partial().passthrough(), z.null()])
      .optional(),
    is_invoice: z.union([z.boolean(), z.null()]).optional(),
    invoice_header_id: z.union([z.number().int(), z.null()]).optional(),
    branch: z.union([GlBranch, z.null()]).optional(),
    institute: z.union([ItInstitute, z.null()]).optional(),
  })
  .passthrough();
const APIResponse_GlTransactionHeaderResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([GlTransactionHeaderResponse, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_bool_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.boolean(), z.null()]),
  })
  .partial()
  .passthrough();
const category = z.union([z.string(), z.null()]).optional();
const passbook_type = z.union([z.number(), z.null()]).optional();
const APIResponse_dict_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.object({}).partial().passthrough(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_GlTransactionHeaderResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(GlTransactionHeaderResponse), z.null()]),
  })
  .partial()
  .passthrough();
const ApprovalDashboardResponse: z.ZodType<ApprovalDashboardResponse> = z.lazy(
  () =>
    z
      .object({
        id: z.number().int(),
        transaction_id: z.string(),
        date_time: z.string(),
        section: z.string(),
        amount: z.number(),
        customer_name: z.string(),
        description: z.string(),
        cashier_name: z.string(),
        reason_for_approval: z.string(),
        transaction_limit: z.number(),
        status: z.string(),
        created_by: z.string(),
        created_at: z.string(),
        approved_by: z.union([z.string(), z.null()]).optional(),
        approved_at: z.union([z.string(), z.null()]).optional(),
        rejection_reason: z.union([z.string(), z.null()]).optional(),
        approved_count: z.union([z.number(), z.null()]).optional(),
        transactions: z
          .union([z.array(ApprovalDashboardResponse), z.null()])
          .optional(),
      })
      .passthrough()
);
const APIResponse_list_ApprovalDashboardResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(ApprovalDashboardResponse), z.null()]),
  })
  .partial()
  .passthrough();
const ApprovalDashboardUpdateRequest = z
  .object({
    reject_reason: z.union([z.string(), z.null()]).optional(),
    status: z.string(),
    ids: z.array(z.number().int()),
  })
  .passthrough();
const JournalTransactionTypeEnum = z.enum(['debit', 'credit']);
const JournalAccountStandard = z
  .object({
    account_id: z.number().int(),
    account_type: z.enum(['pl_account', 'gl_account', 'pl_investment']),
    transaction_type: JournalTransactionTypeEnum,
    amount: z.number().gt(0),
    customer_id: z.union([z.number(), z.null()]).optional(),
    description: z.union([z.string(), z.null()]).optional(),
    account_number: z.union([z.string(), z.null()]).optional(),
    account_name: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const EquipmentItem = z
  .object({
    plAccountTypeId: z.union([z.number(), z.null()]),
    pl_equipment_id: z.union([z.number(), z.null()]),
    nameLn1: z.union([z.string(), z.null()]),
    nameLn2: z.union([z.string(), z.null()]),
    nameLn3: z.union([z.string(), z.null()]),
    serialNumber: z.union([z.string(), z.null()]),
    purchaseValue: z.union([z.number(), z.null()]),
    depreRate: z.union([z.number(), z.null()]),
    quantity: z.union([z.number(), z.null()]),
    supplierName: z.union([z.string(), z.null()]),
    supplierAddress: z.union([z.string(), z.null()]),
    warranty: z.union([z.string(), z.null()]),
    note: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const JournalAccountEquipment = z
  .object({
    account_id: z.union([z.number(), z.null()]).optional(),
    account_type: z.string(),
    transaction_type: JournalTransactionTypeEnum,
    amount: z.number().gt(0),
    equipment: EquipmentItem,
    description: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const JournalRequest = z
  .object({
    accounts: z
      .array(
        z.discriminatedUnion('account_type', [
          JournalAccountStandard,
          JournalAccountEquipment,
        ])
      )
      .min(2),
    note: z.union([z.string(), z.null()]).optional(),
    cheque_num: z.union([z.string(), z.null()]).optional(),
    current_date: z.union([z.boolean(), z.null()]).optional().default(true),
    require_approval: z.union([z.boolean(), z.null()]).optional(),
    branch_id: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const EquipmentTransactionRequest = z
  .object({
    cash_book_id: z.number().int(),
    transaction_type: z.number().int(),
    branch_id: z.number().int(),
    cheque_num: z.union([z.string(), z.null()]).optional(),
    bank_name: z.union([z.number(), z.null()]).optional(),
    note: z.union([z.string(), z.null()]).optional(),
    equipments: z.array(EquipmentItem),
  })
  .passthrough();
const InvestmentItem = z
  .object({
    plAccountId: z.union([z.number(), z.null()]),
    plAccountTypeId: z.union([z.number(), z.null()]),
    customerId: z.union([z.number(), z.null()]),
    interestUpdateAccount: z.union([z.string(), z.null()]),
    branchId: z.union([z.number(), z.null()]),
    refAccountNumber: z.union([z.string(), z.null()]),
    amount: z.union([z.number(), z.null()]),
    interestRate: z.union([z.number(), z.null()]),
    period: z.union([z.number(), z.null()]),
    note: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const InvestmentTransactionRequest = z
  .object({
    cash_book_id: z.number().int(),
    transaction_type: z.number().int(),
    branch_id: z.number().int(),
    cheque_num: z.union([z.string(), z.null()]).optional(),
    bank_name: z.union([z.number(), z.null()]).optional(),
    note: z.union([z.string(), z.null()]).optional(),
    investments: z.array(InvestmentItem),
  })
  .passthrough();
const APIResponse_list_Annotated_Union_JournalAccountStandard__JournalAccountEquipment___FieldInfo_annotation_NoneType__required_True__discriminator__account_type_____ =
  z
    .object({
      status: z.union([z.number(), z.null()]).default(200),
      message: z.union([z.string(), z.null()]).default('Success'),
      data: z.union([
        z.array(
          z.discriminatedUnion('account_type', [
            JournalAccountStandard,
            JournalAccountEquipment,
          ])
        ),
        z.null(),
      ]),
    })
    .partial()
    .passthrough();
const save_journal_draft_api_transaction_journal_draft_post_Body = z.array(
  z.discriminatedUnion('account_type', [
    JournalAccountStandard,
    JournalAccountEquipment,
  ])
);
const PrevMonthRequest = z
  .object({ branch_id: z.number().int() })
  .passthrough();
const JournalPrevMonthResponse = z
  .object({
    show: z.boolean(),
    prev_month_end_date: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_JournalPrevMonthResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([JournalPrevMonthResponse, z.null()]),
  })
  .partial()
  .passthrough();
const MobilePosAccountResponse = z
  .object({
    account_id: z.number().int(),
    account_number: z.string(),
    amount: z.number(),
    status: z.number().int(),
    date: z.string(),
    customer_name: z.union([z.string(), z.null()]).optional(),
    account_name: z.union([z.string(), z.null()]).optional(),
    transaction_number: z.union([z.string(), z.null()]).optional(),
    transaction_id: z.union([z.number(), z.null()]).optional(),
    gl_account_id: z.union([z.number(), z.null()]).optional(),
    ci_customer_id: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const MobilePosBranchResponse: z.ZodType<MobilePosBranchResponse> = z.lazy(() =>
  z
    .object({
      branch_id: z.number().int(),
      branch_name: z.string(),
      accounts: z.array(MobilePosAccountResponse),
      user_name: z.string(),
      branches: z.array(MobilePosBranchResponse),
    })
    .passthrough()
);
const MobilePosUiDataResponse = z
  .object({
    user_id: z.number().int(),
    user_name: z.string(),
    branches: z.array(MobilePosBranchResponse),
  })
  .passthrough();
const APIResponse_list_MobilePosUiDataResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(MobilePosUiDataResponse), z.null()]),
  })
  .partial()
  .passthrough();
const ReverseJournalResponse = z
  .object({
    account_id: z.number().int(),
    account_number: z.string(),
    transaction_id: z.number().int(),
    transaction_number: z.string(),
    transaction_date: z.string().datetime({ offset: true }),
    amount: z.number(),
    interest: z.number(),
    interest_benefit_max_int: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_list_ReverseJournalResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(ReverseJournalResponse), z.null()]),
  })
  .partial()
  .passthrough();
const ReverseJournalUpdateRequest = z
  .object({
    transaction_id: z.number().int(),
    interest_benefit_percentage: z.number(),
  })
  .passthrough();
const LoanDisbursementRequest = z
  .object({
    member_id: z.number().int(),
    cash_book_id: z.number().int(),
    account_id: z.number().int(),
    amount: z.number().gt(0),
    note: z.union([z.string(), z.null()]).optional(),
    cheque_num: z.union([z.string(), z.null()]).optional(),
    bank_name: z.union([z.number(), z.null()]).optional(),
    cus_nic_no: z.union([z.string(), z.null()]).optional(),
    deposit_into_saving: z.boolean().optional().default(false),
    saving_account_id: z.union([z.number(), z.null()]).optional(),
    application_id: z.union([z.number(), z.null()]).optional(),
    recovery_mode: z.union([z.string(), z.null()]).optional().default('manual'),
    auto_recovery_account_id: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const GlTransactionListResponse = z
  .object({
    transaction_list: z.array(GlTransactionResponse),
    total_count: z.number().int(),
    total_pages: z.number().int(),
    current_page: z.number().int(),
  })
  .passthrough();
const APIResponse_GlTransactionListResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([GlTransactionListResponse, z.null()]),
  })
  .partial()
  .passthrough();
const ItTransactionTypeResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    type_name: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_ItTransactionTypeResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(ItTransactionTypeResponse), z.null()]),
  })
  .partial()
  .passthrough();
const GLMonthBalanceTResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    branch_month_id: z.union([z.number(), z.null()]),
    gl_account_id: z.union([z.number(), z.null()]),
    gl_coa_type_id: z.union([z.number(), z.null()]),
    user_id: z.union([z.number(), z.null()]),
    fiscal_year_id: z.union([z.number(), z.null()]),
    open_debit: z.union([z.number(), z.null()]),
    open_credit: z.union([z.number(), z.null()]),
    up_to_debit: z.union([z.number(), z.null()]),
    up_to_credit: z.union([z.number(), z.null()]),
    period_debit: z.union([z.number(), z.null()]),
    period_credit: z.union([z.number(), z.null()]),
    closing_debit: z.union([z.number(), z.null()]),
    closing_credit: z.union([z.number(), z.null()]),
    fix_open_debit: z.union([z.number(), z.null()]),
    fix_open_credit: z.union([z.number(), z.null()]),
    fix_closing_debit: z.union([z.number(), z.null()]),
    fix_closing_credit: z.union([z.number(), z.null()]),
    previous_month_debit: z.union([z.number(), z.null()]),
    previous_month_credit: z.union([z.number(), z.null()]),
    previous_year_debit: z.union([z.number(), z.null()]),
    previous_year_credit: z.union([z.number(), z.null()]),
  })
  .passthrough();
const GLAccountTResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    gl_coa_id: z.union([z.number(), z.null()]),
    branch_id: z.union([z.number(), z.null()]),
    section_id: z.union([z.number(), z.null()]),
    ref_number: z.union([z.string(), z.null()]),
    budget: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
    month_balances: z.array(GLMonthBalanceTResponse).optional().default([]),
  })
  .passthrough();
const GLCoaTResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    gl_coa_type_id: z.union([z.number(), z.null()]),
    ref_number: z.union([z.string(), z.null()]),
    name_ln1: z.union([z.string(), z.null()]),
    name_ln2: z.union([z.string(), z.null()]),
    name_ln3: z.union([z.string(), z.null()]),
    gl_operation_type_id: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
    accounts: z.array(GLAccountTResponse).optional().default([]),
  })
  .passthrough();
const GLCoaTypeTResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    it_institute_id: z.union([z.number(), z.null()]),
    gl_coa_type_group_id: z.union([z.number(), z.null()]),
    gl_note_policy_id: z.union([z.number(), z.null()]),
    cf_note_id: z.union([z.number(), z.null()]),
    cc_note_id: z.union([z.number(), z.null()]),
    operation_filter: z.union([z.number(), z.null()]),
    cc_description: z.union([z.string(), z.null()]),
    name_ln1: z.union([z.string(), z.null()]),
    name_ln2: z.union([z.string(), z.null()]),
    name_ln3: z.union([z.string(), z.null()]),
    status: z.union([z.number(), z.null()]),
    coas: z.array(GLCoaTResponse).optional().default([]),
  })
  .passthrough();
const GLNotePolicyTResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    note_name_ln1: z.union([z.string(), z.null()]),
    note_name_ln2: z.union([z.string(), z.null()]),
    note_name_ln3: z.union([z.string(), z.null()]),
    note: z.union([z.string(), z.null()]),
    coa_types: z.array(GLCoaTypeTResponse).optional().default([]),
  })
  .passthrough();
const APIResponse_list_GLNotePolicyTResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(GLNotePolicyTResponse), z.null()]),
  })
  .partial()
  .passthrough();
const CancellableTransactionResponse = z
  .object({
    id: z.number().int(),
    transaction_number: z.string(),
    date: z.string(),
    amount: z.number(),
    transaction_type: z.string(),
  })
  .passthrough();
const APIResponse_list_CancellableTransactionResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(CancellableTransactionResponse), z.null()]),
  })
  .partial()
  .passthrough();
const Body_upload_cancellation_attachment_api_transaction_upload_cancellation_attachment_post =
  z
    .object({ file: z.instanceof(File), transaction_number: z.string() })
    .passthrough();
const CancelTransactionRequest = z
  .object({
    transaction_number: z.string(),
    ref1: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const PlLoanAmotResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    installment_number: z.union([z.number(), z.null()]),
    pl_account_id: z.union([z.number(), z.null()]),
    payment_date: z.union([z.string(), z.null()]),
    capital_ins: z.union([z.string(), z.null()]),
    interest_ins: z.union([z.string(), z.null()]),
    balance: z.union([z.string(), z.null()]),
    capital_ins_balance: z.union([z.string(), z.null()]),
    interest_ins_balance: z.union([z.string(), z.null()]),
    status: z.union([z.number(), z.null()]),
  })
  .passthrough();
const APIResponse_list_PlLoanAmotResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PlLoanAmotResponse), z.null()]),
  })
  .partial()
  .passthrough();
const AutoRecoveryLoanDetail = z
  .object({
    loan_no: z.string(),
    loan_account_id: z.number().int(),
    capital: z.number(),
    balance: z.number(),
    interest: z.number(),
    penalty: z.number(),
    fee: z.number(),
    fund: z.number(),
    passdue: z.number(),
    issue_on: z.union([z.string(), z.null()]).optional(),
    payment: z.number(),
  })
  .passthrough();
const AutoRecoveryRow = z
  .object({
    customer_no: z.string(),
    customer_id: z.number().int(),
    name: z.string(),
    account_no: z.string(),
    savings_account_id: z.number().int(),
    savings_balance: z.number(),
    total_deduction: z.number(),
    balance_after_deduction: z.number(),
    no_of_loan: z.number().int(),
    loans: z.array(AutoRecoveryLoanDetail),
  })
  .passthrough();
const APIResponse_list_AutoRecoveryRow__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(AutoRecoveryRow), z.null()]),
  })
  .partial()
  .passthrough();
const AutoRecoveryBatchRequest = z
  .object({ recovery_data: z.array(AutoRecoveryRow) })
  .passthrough();
const APIResponse_Union_dict__NoneType__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.object({}).partial().passthrough(), z.null()]),
  })
  .partial()
  .passthrough();
const FinancialDateResponse = z
  .object({
    id: z.number().int(),
    business_date: z.string().datetime({ offset: true }),
    server_date: z.string().datetime({ offset: true }),
    day_open_status: z.number().int(),
    day_end_status: z.number().int(),
    status: z.number().int(),
  })
  .passthrough();
const APIResponse_FinancialDateResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([FinancialDateResponse, z.null()]),
  })
  .partial()
  .passthrough();
const BranchLookupResponse = z
  .object({ id: z.number().int(), name_ln1: z.string() })
  .passthrough();
const APIResponse_list_BranchLookupResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(BranchLookupResponse), z.null()]),
  })
  .partial()
  .passthrough();
const CustomerTypeResponse = z
  .object({ id: z.number().int(), type_ln1: z.string() })
  .passthrough();
const APIResponse_list_CustomerTypeResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(CustomerTypeResponse), z.null()]),
  })
  .partial()
  .passthrough();
const DeactivateOptionResponse = z
  .object({ id: z.number().int(), name: z.string() })
  .passthrough();
const APIResponse_list_DeactivateOptionResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(DeactivateOptionResponse), z.null()]),
  })
  .partial()
  .passthrough();
const CiCityResponse = z
  .object({ id: z.number().int(), city_ln1: z.string() })
  .passthrough();
const APIResponse_list_CiCityResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(CiCityResponse), z.null()]),
  })
  .partial()
  .passthrough();
const CiCustomerType = z
  .object({
    id: z.union([z.number(), z.null()]),
    prefix: z.union([z.string(), z.null()]),
    last_number: z.union([z.number(), z.null()]),
    type_ln1: z.union([z.string(), z.null()]),
    type_ln2: z.union([z.string(), z.null()]),
    type_ln3: z.union([z.string(), z.null()]),
    type: z.union([z.number(), z.null()]),
    no_of_sms: z.union([z.number(), z.null()]),
    nic_status: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
    nic_mand: z.union([z.number(), z.null()]).default(0),
    phone_mand: z.union([z.number(), z.null()]).default(0),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const CiNomineeResponse = z
  .object({
    id: z.number().int(),
    name_ln1: z.union([z.string(), z.null()]).optional(),
    name_ln2: z.union([z.string(), z.null()]).optional(),
    name_ln3: z.union([z.string(), z.null()]).optional(),
    relationship: z.union([z.string(), z.null()]).optional(),
    reg_id_num: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const CustomerResponse = z
  .object({
    id: z.number().int(),
    customer_number: z.string(),
    customer_type_id: z.union([z.number(), z.null()]).optional(),
    branch_id: z.union([z.number(), z.null()]).optional(),
    title_ln1: z.union([z.string(), z.null()]).optional(),
    title_ln2: z.union([z.string(), z.null()]).optional(),
    title_ln3: z.union([z.string(), z.null()]).optional(),
    full_name_ln1: z.union([z.string(), z.null()]).optional(),
    full_name_ln2: z.union([z.string(), z.null()]).optional(),
    full_name_ln3: z.union([z.string(), z.null()]).optional(),
    ini_name_ln1: z.union([z.string(), z.null()]).optional(),
    ini_name_ln2: z.union([z.string(), z.null()]).optional(),
    ini_name_ln3: z.union([z.string(), z.null()]).optional(),
    address_ln1: z.union([z.string(), z.null()]).optional(),
    address_ln2: z.union([z.string(), z.null()]).optional(),
    address_ln3: z.union([z.string(), z.null()]).optional(),
    postal_code: z.union([z.string(), z.null()]).optional(),
    ci_city_id: z.union([z.number(), z.null()]).optional(),
    group_id: z.union([z.number(), z.null()]).optional(),
    nic: z.union([z.string(), z.null()]).optional(),
    gender: z.union([z.string(), z.null()]).optional(),
    date_of_birth: z.union([z.string(), z.null()]).optional(),
    member_date: z.union([z.string(), z.null()]).optional(),
    married_status: z.union([z.string(), z.null()]).optional(),
    mobile_1: z.union([z.string(), z.null()]).optional(),
    mobile_2: z.union([z.string(), z.null()]).optional(),
    home_phone: z.union([z.string(), z.null()]).optional(),
    e_mail: z.union([z.string(), z.null()]).optional(),
    credit_limit: z.union([z.number(), z.null()]).optional(),
    credit_limit_date: z.union([z.string(), z.null()]).optional(),
    status: z.union([z.number(), z.null()]).optional(),
    inactive_reson: z.union([z.string(), z.null()]).optional(),
    photo: z.union([z.instanceof(File), z.null()]).optional(),
    signature: z.union([z.instanceof(File), z.null()]).optional(),
    sms_status: z.union([z.number(), z.null()]).optional(),
    dp_app_status: z.union([z.number(), z.null()]).optional(),
    pos_app_status: z.union([z.number(), z.null()]).optional(),
    c_at: z.union([z.string(), z.null()]).optional(),
    c_by: z.union([z.number(), z.null()]).optional(),
    m_at: z.union([z.string(), z.null()]).optional(),
    m_by: z.union([z.number(), z.null()]).optional(),
    field_officer_id: z.union([z.number(), z.null()]).optional(),
    branch: z.union([GlBranch, z.null()]).optional(),
    customer_type: z.union([CiCustomerType, z.null()]).optional(),
    nominees: z.union([z.array(CiNomineeResponse), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_CustomerResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([CustomerResponse, z.null()]),
  })
  .partial()
  .passthrough();
const CustomerUpdate = z
  .object({
    customer_type_id: z.union([z.number(), z.null()]).optional(),
    full_name_ln1: z.string(),
    ini_name_ln1: z.string(),
    title_ln1: z.string(),
    address_ln1: z.string(),
    ci_city_id: z.number().int(),
    married_status: z.string(),
    nic: z.union([z.string(), z.null()]).optional(),
    mobile_1: z.union([z.string(), z.null()]).optional(),
    title_ln2: z.union([z.string(), z.null()]).optional(),
    title_ln3: z.union([z.string(), z.null()]).optional(),
    full_name_ln2: z.union([z.string(), z.null()]).optional(),
    full_name_ln3: z.union([z.string(), z.null()]).optional(),
    ini_name_ln2: z.union([z.string(), z.null()]).optional(),
    ini_name_ln3: z.union([z.string(), z.null()]).optional(),
    address_ln2: z.union([z.string(), z.null()]).optional(),
    address_ln3: z.union([z.string(), z.null()]).optional(),
    postal_code: z.union([z.string(), z.null()]).optional(),
    gender: z.union([z.string(), z.null()]).optional(),
    date_of_birth: z.union([z.string(), z.null()]).optional(),
    member_date: z.union([z.string(), z.null()]).optional(),
    mobile_2: z.union([z.string(), z.null()]).optional(),
    home_phone: z.union([z.string(), z.null()]).optional(),
    e_mail: z.union([z.string(), z.null()]).optional(),
    credit_limit: z.union([z.number(), z.null()]).optional(),
    credit_limit_date: z.union([z.string(), z.null()]).optional(),
    inactive_reson: z.union([z.string(), z.null()]).optional(),
    photo: z.union([z.instanceof(File), z.null()]).optional(),
    signature: z.union([z.instanceof(File), z.null()]).optional(),
    sms_status: z.union([z.number(), z.null()]).optional().default(0),
    dp_app_status: z.union([z.number(), z.null()]).optional().default(0),
    pos_app_status: z.union([z.number(), z.null()]).optional().default(0),
    field_officer_id: z.union([z.number(), z.null()]).optional().default(0),
  })
  .passthrough();
const MobileCustomerResponse = z
  .object({
    customer_number: z.string(),
    customer_id: z.number().int(),
    customer_type_id: z.number().int(),
  })
  .passthrough();
const APIResponse_MobileCustomerResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([MobileCustomerResponse, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_PlAccountResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PlAccountResponse), z.null()]),
  })
  .partial()
  .passthrough();
const CustomerListDetail = z
  .object({
    id: z.number().int().optional(),
    name: z.string(),
    customer_number: z.string(),
    nic: z.string(),
    branch_name: z.string(),
    status: z.string(),
  })
  .passthrough();
const PaginatedCustomerListResponse = z
  .object({
    items: z.array(CustomerListDetail),
    total: z.number().int(),
    page: z.number().int(),
    page_size: z.number().int(),
    has_more: z.boolean(),
  })
  .passthrough();
const APIResponse_PaginatedCustomerListResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PaginatedCustomerListResponse, z.null()]),
  })
  .partial()
  .passthrough();
const CustomerCreate = z
  .object({
    customer_type_id: z.number().int(),
    full_name_ln1: z.string(),
    ini_name_ln1: z.string(),
    title_ln1: z.string(),
    address_ln1: z.string(),
    ci_city_id: z.number().int(),
    married_status: z.string(),
    nic: z.union([z.string(), z.null()]).optional(),
    mobile_1: z.union([z.string(), z.null()]).optional(),
    title_ln2: z.union([z.string(), z.null()]).optional(),
    title_ln3: z.union([z.string(), z.null()]).optional(),
    full_name_ln2: z.union([z.string(), z.null()]).optional(),
    full_name_ln3: z.union([z.string(), z.null()]).optional(),
    ini_name_ln2: z.union([z.string(), z.null()]).optional(),
    ini_name_ln3: z.union([z.string(), z.null()]).optional(),
    address_ln2: z.union([z.string(), z.null()]).optional(),
    address_ln3: z.union([z.string(), z.null()]).optional(),
    postal_code: z.union([z.string(), z.null()]).optional(),
    gender: z.union([z.string(), z.null()]).optional(),
    date_of_birth: z.union([z.string(), z.null()]).optional(),
    member_date: z.union([z.string(), z.null()]).optional(),
    mobile_2: z.union([z.string(), z.null()]).optional(),
    home_phone: z.union([z.string(), z.null()]).optional(),
    e_mail: z.union([z.string(), z.null()]).optional(),
    credit_limit: z.union([z.number(), z.null()]).optional(),
    credit_limit_date: z.union([z.string(), z.null()]).optional(),
    inactive_reson: z.union([z.string(), z.null()]).optional(),
    photo: z.union([z.instanceof(File), z.null()]).optional(),
    signature: z.union([z.instanceof(File), z.null()]).optional(),
    sms_status: z.union([z.number(), z.null()]).optional().default(0),
    dp_app_status: z.union([z.number(), z.null()]).optional().default(0),
    pos_app_status: z.union([z.number(), z.null()]).optional().default(0),
    field_officer_id: z.union([z.number(), z.null()]).optional().default(0),
  })
  .passthrough();
const CustomerReject = z
  .object({
    deactivate_id: z.number().int(),
    inactive_reason: z.string().min(1).max(500),
  })
  .passthrough();
const CustomerTypeFieldResponse = z
  .object({ id: z.number().int(), field_name: z.string() })
  .passthrough();
const APIResponse_list_CustomerTypeFieldResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(CustomerTypeFieldResponse), z.null()]),
  })
  .partial()
  .passthrough();
const CustomerTypeDocResponse = z
  .object({ id: z.number().int(), doc_name: z.string() })
  .passthrough();
const APIResponse_list_CustomerTypeDocResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(CustomerTypeDocResponse), z.null()]),
  })
  .partial()
  .passthrough();
const Body_create_customer_info_api_customer__customer_id__info_post = z
  .object({ ci_customer_type_info_id: z.number().int(), file: z.string() })
  .passthrough();
const APIResponse_list_dict__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(z.object({}).partial().passthrough()), z.null()]),
  })
  .partial()
  .passthrough();
const Body_upload_customer_document_api_customer__customer_id__docs_upload_post =
  z
    .object({
      ci_customer_type_doc_id: z.number().int(),
      file: z.instanceof(File),
    })
    .passthrough();
const Body_upload_customer_photo_api_customer__customer_number__photo_post = z
  .object({ photo: z.instanceof(File) })
  .passthrough();
const Body_upload_customer_signature_api_customer__customer_number__signature_post =
  z.object({ signature: z.instanceof(File) }).passthrough();
const CommonCustomerResponse = z
  .object({
    customer_number: z.string(),
    full_name_ln1: z.string(),
    full_name_ln2: z.union([z.string(), z.null()]),
    nic: z.string(),
    address_ln1: z.union([z.string(), z.null()]),
    address_ln2: z.union([z.string(), z.null()]),
    mobile_no: z.union([z.string(), z.null()]),
    branch_name: z.union([z.string(), z.null()]),
    photo: z.union([z.string(), z.null()]),
    signature: z.union([z.string(), z.null()]),
  })
  .passthrough();
const APIResponse_CommonCustomerResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([CommonCustomerResponse, z.null()]),
  })
  .partial()
  .passthrough();
const AccountBase = z
  .object({
    account_no: z.string(),
    account_name: z.string(),
    open_date: z.string(),
    last_transaction_date: z.string(),
    status: z.number().int(),
  })
  .passthrough();
const CommonAccountsResponse = z
  .object({
    savings: z.array(AccountBase),
    shares: z.array(AccountBase),
    fd: z.array(AccountBase),
    loan: z.array(AccountBase),
  })
  .passthrough();
const APIResponse_CommonAccountsResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([CommonAccountsResponse, z.null()]),
  })
  .partial()
  .passthrough();
const PlAccountTypeSimpleResponse = z
  .object({
    id: z.number().int(),
    name_ln1: z.union([z.string(), z.null()]),
    name_ln2: z.union([z.string(), z.null()]),
  })
  .passthrough();
const APIResponse_list_PlAccountTypeSimpleResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PlAccountTypeSimpleResponse), z.null()]),
  })
  .partial()
  .passthrough();
const TransactionTypeResponse = z
  .object({ id: z.number().int(), type_name: z.string(), prefix: z.string() })
  .passthrough();
const APIResponse_list_TransactionTypeResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(TransactionTypeResponse), z.null()]),
  })
  .partial()
  .passthrough();
const UserRole = z.enum([
  'ADMIN',
  'FIELD_OFFICER',
  'CLERK',
  'BRANCH_MANAGER',
  'CASHIER',
]);
const ItUserMaster = z
  .object({
    name: z.string().min(1).max(300),
    user_name: z.string().min(3).max(100),
    ref_number: z.union([z.string(), z.null()]).optional(),
    user_group_id: z.union([z.number(), z.null()]).optional(),
    customer_id: z.union([z.number(), z.null()]).optional(),
    active_branch_id: z.union([z.number(), z.null()]).optional(),
    user_role: z.union([UserRole, z.null()]).optional().default('CLERK'),
    id: z.union([z.number(), z.null()]).optional(),
    password: z.union([z.string(), z.null()]),
    password_old1: z.union([z.string(), z.null()]).optional(),
    password_od2: z.union([z.string(), z.null()]).optional(),
    password_status: z.union([z.number(), z.null()]).optional(),
    report_status: z.union([z.number(), z.null()]).optional(),
    status: z.union([z.number(), z.null()]).optional(),
    c_at: z.union([z.string(), z.null()]).optional(),
    c_by: z.union([z.number(), z.null()]).optional(),
    m_at: z.union([z.string(), z.null()]).optional(),
    m_by: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const PlAccountCommentResponse = z
  .object({
    id: z.number().int(),
    pl_account_id: z.number().int(),
    is_ai: z.boolean(),
    comment: z.string(),
    c_by: z.union([z.number(), z.null()]),
    c_at: z.string().datetime({ offset: true }),
    user: z.union([ItUserMaster, z.null()]).optional(),
  })
  .passthrough();
const APIResponse_list_PlAccountCommentResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PlAccountCommentResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PlAccountCommentCreateRequest = z
  .object({
    comment: z.string(),
    is_ai: z.union([z.boolean(), z.null()]).optional().default(false),
    ai_request: z.union([z.string(), z.null()]).optional(),
    ai_action: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_PlAccountCommentResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PlAccountCommentResponse, z.null()]),
  })
  .partial()
  .passthrough();
const LoanAppraisalAICommentRequest = z
  .object({
    past_due: z.boolean().default(false),
    saving_balances: z.boolean().default(false),
    loan_balances: z.boolean().default(false),
    transaction_history: z.boolean().default(false),
    transaction_history_month: z.number().int().gte(1).lte(6).default(1),
    guarantor_past_due: z.boolean().default(false),
    language: z.union([z.string(), z.null()]).default('English'),
  })
  .partial()
  .passthrough();
const LoanAppraisalAICommentResponse = z
  .object({
    generated_comment: z.string(),
    selected_data: z.object({}).partial().passthrough(),
  })
  .passthrough();
const APIResponse_LoanAppraisalAICommentResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([LoanAppraisalAICommentResponse, z.null()]),
  })
  .partial()
  .passthrough();
const PlPolicy = z
  .object({
    id: z.union([z.number(), z.null()]),
    pl_policy_type_id: z.union([z.number(), z.null()]),
    policy_name_ln1: z.union([z.string(), z.null()]),
    policy_name_ln2: z.union([z.string(), z.null()]),
    policy_name_ln3: z.union([z.string(), z.null()]),
    note: z.union([z.string(), z.null()]),
    fomular: z.union([z.string(), z.null()]),
    status: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const PlAccountTypeDocuments = z
  .object({
    id: z.union([z.number(), z.null()]),
    pl_account_type_id: z.union([z.number(), z.null()]),
    document_name: z.union([z.string(), z.null()]),
    status: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const PLAccountTypeInfo = z
  .object({
    id: z.union([z.number(), z.null()]),
    pl_account_type_id: z.union([z.number(), z.null()]),
    field_name: z.union([z.string(), z.null()]),
    status: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const LoanProductResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    name_ln1: z.union([z.string(), z.null()]),
    name_ln2: z.union([z.string(), z.null()]),
    name_ln3: z.union([z.string(), z.null()]),
    ref_number: z.union([z.string(), z.null()]),
    auto_number_creation: z.union([z.boolean(), z.null()]).default(true),
    period_max: z.union([z.number(), z.null()]),
    period_min: z.union([z.number(), z.null()]),
    interest_policy: z.union([z.number(), z.null()]),
    interest_update_policy: z.union([z.number(), z.null()]),
    interest_rate_max: z.union([z.number(), z.null()]),
    interest_rate_min: z.union([z.number(), z.null()]),
    penalty_policy: z.union([z.number(), z.null()]),
    penalty_rate: z.union([z.number(), z.null()]),
    insurance_policy: z.union([z.number(), z.null()]),
    insurance_rate: z.union([z.number(), z.null()]),
    fee_policy: z.union([z.number(), z.null()]),
    fee_rate: z.union([z.number(), z.null()]),
    gl_coa_id: z.union([z.number(), z.null()]),
    gl_interest_id: z.union([z.number(), z.null()]),
    gl_penalty_id: z.union([z.number(), z.null()]),
    gl_insurance_id: z.union([z.number(), z.null()]),
    gl_fee_id: z.union([z.number(), z.null()]),
    gl_interest_res_id: z.union([z.number(), z.null()]),
    gl_tax_account: z.union([z.number(), z.null()]),
    pl_account_category_id: z.union([z.number(), z.null()]),
    pl_operating_type_id: z.union([z.number(), z.null()]),
    minimum_balance: z.union([z.number(), z.null()]),
    tax_minimum_balance: z.union([z.number(), z.null()]),
    tax_rate: z.union([z.number(), z.null()]),
    decimal_number: z.union([z.number(), z.null()]),
    no_of_loan_guarantor: z.union([z.number(), z.null()]),
    passdue_policy: z.union([z.number(), z.null()]),
    fd_loan_margin: z.union([z.number(), z.null()]),
    fd_loan_inte_gap: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
    pb_type: z.union([z.number(), z.null()]),
    policy_interest: z.union([PlPolicy, z.null()]),
    documents: z.union([z.array(PlAccountTypeDocuments), z.null()]),
    info_fields: z.union([z.array(PLAccountTypeInfo), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_LoanProductResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(LoanProductResponse), z.null()]),
  })
  .partial()
  .passthrough();
const LoanApplicationRequest = z
  .object({
    customer_number: z.string().min(1).max(50),
    loan_product_name: z.union([z.string(), z.null()]).optional(),
    loan_product_id: z.union([z.number(), z.null()]).optional(),
    requested_amount: z.number().gt(0),
    remarks: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const CiCustomer = z
  .object({
    id: z.union([z.number(), z.null()]),
    token: z.union([z.string(), z.null()]),
    customer_number: z.union([z.string(), z.null()]),
    customer_type_id: z.union([z.number(), z.null()]),
    branch_id: z.union([z.number(), z.null()]),
    title_ln1: z.union([z.string(), z.null()]),
    title_ln2: z.union([z.string(), z.null()]),
    title_ln3: z.union([z.string(), z.null()]),
    full_name_ln1: z.union([z.string(), z.null()]),
    full_name_ln2: z.union([z.string(), z.null()]),
    full_name_ln3: z.union([z.string(), z.null()]),
    ini_name_ln1: z.union([z.string(), z.null()]),
    ini_name_ln2: z.union([z.string(), z.null()]),
    ini_name_ln3: z.union([z.string(), z.null()]),
    address_ln1: z.union([z.string(), z.null()]),
    address_ln2: z.union([z.string(), z.null()]),
    address_ln3: z.union([z.string(), z.null()]),
    latitude: z.union([z.number(), z.null()]),
    longitude: z.union([z.number(), z.null()]),
    postal_code: z.union([z.string(), z.null()]),
    ci_city_id: z.union([z.number(), z.null()]),
    group_id: z.union([z.number(), z.null()]),
    nic: z.union([z.string(), z.null()]),
    gender: z.union([z.string(), z.null()]),
    date_of_birth: z.union([z.string(), z.null()]),
    member_date: z.union([z.string(), z.null()]),
    married_status: z.union([z.string(), z.null()]),
    mobile_1: z.union([z.string(), z.null()]),
    mobile_2: z.union([z.string(), z.null()]),
    home_phone: z.union([z.string(), z.null()]),
    e_mail: z.union([z.string(), z.null()]),
    credit_limit: z.union([z.number(), z.null()]),
    credit_limit_date: z.union([z.string(), z.null()]),
    status: z.union([z.number(), z.null()]),
    inactive_reson: z.union([z.string(), z.null()]),
    deactivate_id: z.union([z.number(), z.null()]),
    photo: z.union([z.instanceof(File), z.null()]),
    signature: z.union([z.instanceof(File), z.null()]),
    sms_status: z.union([z.number(), z.null()]),
    dp_app_status: z.union([z.number(), z.null()]),
    pos_app_status: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
    field_officer_id: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const LoanApplicationResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    application_number: z.union([z.string(), z.null()]),
    ci_customer_id: z.union([z.number(), z.null()]),
    loan_product_id: z.union([z.number(), z.null()]),
    customer_number: z.union([z.string(), z.null()]),
    amount: z.union([z.number(), z.null()]),
    status: z.union([z.string(), z.null()]),
    remarks: z.union([z.string(), z.null()]),
    created_at: z.union([z.string(), z.null()]),
    loan_product: z.union([LoanProductResponse, z.null()]),
    pl_account: z.union([PlAccountResponse, z.null()]),
    customer: z.union([CiCustomer, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_LoanApplicationResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([LoanApplicationResponse, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_LoanApplicationResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(LoanApplicationResponse), z.null()]),
  })
  .partial()
  .passthrough();
const LoanAppraisalUpdateRequest = z
  .object({
    loan_product_id: z.number().int().gt(0),
    amount: z.number().gt(0),
    interest_rate: z.number().gte(0),
    loan_period: z.number().int().gte(0),
    installment_amount: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const PlAccountInfoResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    pl_account_type_info_id: z.union([z.number(), z.null()]),
    information: z.union([z.string(), z.null()]),
    pl_account_type_info: z.union([PLAccountTypeInfo, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_PlAccountInfoResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PlAccountInfoResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PlDocumentsResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    pl_account_id: z.union([z.number(), z.null()]),
    pl_account_type_document_id: z.union([z.number(), z.null()]),
    document: z.union([z.string(), z.null()]),
    pl_account_type_document: z.union([PlAccountTypeDocuments, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_PlDocumentsResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PlDocumentsResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PlGuarantersResponse = z
  .object({
    max_guarantors: z.number().int().default(0),
    existing_guarantors: z
      .array(z.object({}).partial().passthrough())
      .default([]),
  })
  .partial()
  .passthrough();
const APIResponse_PlGuarantersResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PlGuarantersResponse, z.null()]),
  })
  .partial()
  .passthrough();
const SaveGuarantorItem = z
  .object({ customer_number: z.union([z.string(), z.null()]) })
  .partial()
  .passthrough();
const SaveGuarantorsRequest = z
  .object({ guarantors: z.array(SaveGuarantorItem) })
  .passthrough();
const APIResponse_str_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const GuarantersListResponse = z
  .object({
    customer_number: z.union([z.string(), z.null()]),
    nic: z.union([z.string(), z.null()]),
    name: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_GuarantersListResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(GuarantersListResponse), z.null()]),
  })
  .partial()
  .passthrough();
const Body_upload_loan_document_api_loan_application_documents_upload__pl_document_id__post =
  z.object({ file: z.instanceof(File) }).passthrough();
const APIResponse_PlDocumentsResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PlDocumentsResponse, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_PlAccountInfoResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PlAccountInfoResponse, z.null()]),
  })
  .partial()
  .passthrough();
const approve_loan_application_api_loan_application_approve__application_id__post_Body =
  z.union([z.object({}).partial().passthrough(), z.null()]);
const Body_fd_advance_upload_certificate_api_loan_application_fd_advance_upload_certificate_post =
  z
    .object({ file: z.instanceof(File), account_ref_number: z.string() })
    .passthrough();
const FdAdvanceLoanItem = z
  .object({
    fd_account_id: z.number().int(),
    request_amount: z.number().gt(0),
    certificate_path: z.string(),
    period: z.number().int().gt(0),
  })
  .passthrough();
const FdAdvanceRequest = z
  .object({
    total_amount: z.number().gt(0),
    loan_product_id: z.number().int(),
    member_id: z.number().int(),
    loans: z.array(FdAdvanceLoanItem),
  })
  .passthrough();
const AmountAndPeriodResponse = z
  .object({
    issue_amount: z.union([z.string(), z.null()]),
    period: z.union([z.number(), z.null()]),
  })
  .passthrough();
const APIResponse_list_AmountAndPeriodResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(AmountAndPeriodResponse), z.null()]),
  })
  .partial()
  .passthrough();
const LoanAmotRequest = z
  .object({
    loan_product_id: z.number().int().gt(0),
    amount: z.number().gt(0),
    interest_rate: z.number().gte(0),
    loan_period: z.number().int().gte(0),
    disburse_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    installment_amount: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_PlAccountResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PlAccountResponse, z.null()]),
  })
  .partial()
  .passthrough();
const CustomerLoanSummaryStats = z
  .object({
    my_loans_count: z.number().int(),
    guarantor_loans_count: z.number().int(),
    max_guarantors: z.number().int(),
  })
  .passthrough();
const LoanGuarantorResponse = z
  .object({
    guarantor_type_id: z.number().int(),
    guarantor_type: z.union([z.string(), z.null()]).optional(),
    customer_number: z.union([z.string(), z.null()]).optional(),
    customer_name: z.union([z.string(), z.null()]).optional(),
    exists: z.boolean().optional().default(false),
  })
  .passthrough();
const MyLoanItemResponse = z
  .object({
    loan_id: z.number().int(),
    loan_account_number: z.union([z.string(), z.null()]).optional(),
    product_name: z.union([z.string(), z.null()]).optional(),
    balance: z.union([z.number(), z.null()]).optional(),
    issue_date: z.union([z.string(), z.null()]).optional(),
    issued_amount: z.union([z.number(), z.null()]).optional(),
    period: z.union([z.number(), z.null()]).optional(),
    interest_rate: z.union([z.number(), z.null()]).optional(),
    installment: z.union([z.number(), z.null()]).optional(),
    transaction_date: z.union([z.string(), z.null()]).optional(),
    last_transaction_date: z.union([z.string(), z.null()]).optional(),
    past_due: z.union([z.number(), z.null()]).optional(),
    past_due_amount: z.union([z.number(), z.null()]).optional(),
    interest: z.union([z.number(), z.null()]).optional(),
    due_interest: z.union([z.number(), z.null()]).optional(),
    guarantor_count: z.number().int(),
    max_guarantors: z.number().int(),
    guarantors: z.array(LoanGuarantorResponse),
  })
  .passthrough();
const GuarantorRoleResponse = z
  .object({
    guarantor_type_id: z.union([z.number(), z.null()]),
    guarantor_type: z.union([z.string(), z.null()]),
    customer_number: z.union([z.string(), z.null()]),
    customer_name: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const GuarantorLoanItemResponse = z
  .object({
    loan_id: z.number().int(),
    loan_account_number: z.union([z.string(), z.null()]).optional(),
    product_name: z.union([z.string(), z.null()]).optional(),
    balance: z.union([z.number(), z.null()]).optional(),
    issue_date: z.union([z.string(), z.null()]).optional(),
    issued_amount: z.union([z.number(), z.null()]).optional(),
    period: z.union([z.number(), z.null()]).optional(),
    interest_rate: z.union([z.number(), z.null()]).optional(),
    installment: z.union([z.number(), z.null()]).optional(),
    transaction_date: z.union([z.string(), z.null()]).optional(),
    last_transaction_date: z.union([z.string(), z.null()]).optional(),
    past_due: z.union([z.number(), z.null()]).optional(),
    past_due_amount: z.union([z.number(), z.null()]).optional(),
    interest: z.union([z.number(), z.null()]).optional(),
    due_interest: z.union([z.number(), z.null()]).optional(),
    guarantor: GuarantorRoleResponse,
  })
  .passthrough();
const CustomerLoanSummaryResponse = z
  .object({
    customer_number: z.string(),
    summary: CustomerLoanSummaryStats,
    my_loans: z.array(MyLoanItemResponse),
    i_am_guarantor_for: z.array(GuarantorLoanItemResponse),
  })
  .passthrough();
const APIResponse_CustomerLoanSummaryResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([CustomerLoanSummaryResponse, z.null()]),
  })
  .partial()
  .passthrough();
const LoanProductRead = z
  .object({
    ref_number: z.union([z.string(), z.null()]).optional(),
    name_ln1: z.union([z.string(), z.null()]).optional(),
    name_ln2: z.union([z.string(), z.null()]).optional(),
    name_ln3: z.union([z.string(), z.null()]).optional(),
    it_institute_id: z.union([z.number(), z.null()]).optional(),
    gl_coa_id: z.union([z.number(), z.null()]).optional(),
    gl_interest_id: z.union([z.number(), z.null()]).optional(),
    gl_penalty_id: z.union([z.number(), z.null()]).optional(),
    gl_insurance_id: z.union([z.number(), z.null()]).optional(),
    gl_fee_id: z.union([z.number(), z.null()]).optional(),
    gl_interest_res_id: z.union([z.number(), z.null()]).optional(),
    gl_tax_account: z.union([z.number(), z.null()]).optional(),
    last_number: z.union([z.number(), z.null()]).optional(),
    pl_account_category_id: z.union([z.number(), z.null()]).optional(),
    pl_operating_type_id: z.union([z.number(), z.null()]).optional(),
    interest_policy: z.union([z.number(), z.null()]).optional(),
    interest_update_policy: z.union([z.number(), z.null()]).optional(),
    period_policy: z.union([z.number(), z.null()]).optional(),
    penalty_policy: z.union([z.number(), z.null()]).optional(),
    insurance_policy: z.union([z.number(), z.null()]).optional(),
    fee_policy: z.union([z.number(), z.null()]).optional(),
    interest_rate_max: z.union([z.number(), z.null()]).optional(),
    interest_rate_min: z.union([z.number(), z.null()]).optional(),
    period_max: z.union([z.number(), z.null()]).optional(),
    period_min: z.union([z.number(), z.null()]).optional(),
    penalty_rate: z.union([z.number(), z.null()]).optional(),
    insurance_rate: z.union([z.number(), z.null()]).optional(),
    fee_rate: z.union([z.number(), z.null()]).optional(),
    depreciation_rate: z.union([z.number(), z.null()]).optional(),
    minimum_balance: z.union([z.number(), z.null()]).optional(),
    allow_interest_edit: z.union([z.number(), z.null()]).optional(),
    loan_schedule_type: z.union([z.number(), z.null()]).optional(),
    tax_minimum_balance: z.union([z.number(), z.null()]).optional(),
    tax_rate: z.union([z.number(), z.null()]).optional(),
    interest_benefit_max_int: z.union([z.number(), z.null()]).optional(),
    auto_number_creation: z
      .union([z.boolean(), z.null()])
      .optional()
      .default(true),
    no_of_loan_guarantor: z.union([z.number(), z.null()]).optional(),
    passdue_policy: z.union([z.number(), z.null()]).optional(),
    fd_loan_margin: z.union([z.number(), z.null()]).optional(),
    fd_loan_inte_gap: z.union([z.number(), z.null()]).optional(),
    field_collection: z.union([z.number(), z.null()]).optional(),
    pb_type: z.union([z.number(), z.null()]).optional(),
    decimal_number: z.union([z.number(), z.null()]).optional(),
    status: z.union([z.number(), z.null()]).optional(),
    id: z.number().int(),
    category_name: z.union([z.string(), z.null()]).optional(),
    operating_type_name: z.union([z.string(), z.null()]).optional(),
    interest_policy_name: z.union([z.string(), z.null()]).optional(),
    period_policy_name: z.union([z.string(), z.null()]).optional(),
    c_at: z.union([z.string(), z.null()]).optional(),
    m_at: z.union([z.string(), z.null()]).optional(),
    documents: z
      .union([z.array(PlAccountTypeDocuments), z.null()])
      .optional()
      .default([]),
    info_fields: z
      .union([z.array(PLAccountTypeInfo), z.null()])
      .optional()
      .default([]),
  })
  .passthrough();
const LoanProductPaginated = z
  .object({
    items: z.array(LoanProductRead),
    total: z.number().int(),
    page: z.number().int(),
    page_size: z.number().int(),
    has_more: z.boolean(),
  })
  .passthrough();
const APIResponse_LoanProductPaginated_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([LoanProductPaginated, z.null()]),
  })
  .partial()
  .passthrough();
const PlProductAIPolicyRequest = z
  .object({
    product_data: z.object({}).partial().passthrough(),
    language: z.union([z.string(), z.null()]).optional().default('English'),
    source_text: z.union([z.string(), z.null()]).optional(),
    ai_action: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const PlProductAIPolicyResponse = z
  .object({ generated_policy: z.string() })
  .passthrough();
const APIResponse_PlProductAIPolicyResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PlProductAIPolicyResponse, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_LoanProductRead_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([LoanProductRead, z.null()]),
  })
  .partial()
  .passthrough();
const Body_update_loan_product_api_pl_product__id__post = z
  .object({
    data: z.string(),
    file: z.union([z.instanceof(File), z.null()]).optional(),
  })
  .passthrough();
const Body_create_loan_product_api_pl_product_post = z
  .object({
    data: z.string(),
    file: z.union([z.instanceof(File), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_PlAccountType_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PlAccountType, z.null()]),
  })
  .partial()
  .passthrough();
const Body_add_product_policy_doc_api_pl_product__id__policy_doc_post = z
  .object({ doc_name: z.string(), file: z.instanceof(File) })
  .passthrough();
const Body_upload_amortization_file_api_pl_product__id__amortization_file_post =
  z.object({ file: z.array(z.instanceof(File)) }).passthrough();
const APIResponse_list_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(z.unknown()), z.null()]),
  })
  .partial()
  .passthrough();
const ItProductResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    it_institute_id: z.union([z.number(), z.null()]),
    it_product_type_id: z.union([z.number(), z.null()]),
    name: z.union([z.string(), z.null()]),
    description: z.union([z.string(), z.null()]),
    purchase_price: z.union([z.number(), z.null()]),
    selling_price: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
  })
  .passthrough();
const APIResponse_List_ItProductResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(ItProductResponse), z.null()]),
  })
  .partial()
  .passthrough();
const Product = z
  .object({
    product_id: z.number().int(),
    name: z.union([z.string(), z.null()]),
    description: z.union([z.string(), z.null()]),
    unit_price: z.union([z.number(), z.null()]),
    qty: z.union([z.number(), z.null()]),
    amount: z.union([z.number(), z.null()]),
  })
  .passthrough();
const PlQuotationRequest = z
  .object({
    customer_id: z.number().int(),
    total_amount: z.union([z.number(), z.null()]),
    products: z.union([z.array(Product), z.null()]),
  })
  .passthrough();
const PlQuotationResponse = z
  .object({
    id: z.number().int(),
    quotation_number: z.string(),
    customer_id: z.number().int(),
    total_amount: z.union([z.number(), z.null()]),
    products: z.union([z.array(Product), z.null()]),
  })
  .passthrough();
const APIResponse_PlQuotationResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PlQuotationResponse, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_List_PlQuotationResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PlQuotationResponse), z.null()]),
  })
  .partial()
  .passthrough();
const AccountFilterType = z.enum([
  'RECEIPT',
  'JOURNAL',
  'PAYMENT',
  'EQUIPMENT',
]);
const type = z.union([AccountFilterType, z.null()]).optional();
const APIResponse_list_GLAccountResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(GLAccountResponse), z.null()]),
  })
  .partial()
  .passthrough();
const ChequeBookResponse = z
  .object({ id: z.number().int(), name: z.string() })
  .passthrough();
const APIResponse_list_ChequeBookResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(ChequeBookResponse), z.null()]),
  })
  .partial()
  .passthrough();
const GlCoaResponse = z
  .object({
    id: z.number().int(),
    name: z.string(),
    operation_filter: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_list_GlCoaResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(GlCoaResponse), z.null()]),
  })
  .partial()
  .passthrough();
const GLAccountRead = z
  .object({
    gl_coa_id: z.union([z.number(), z.null()]).optional(),
    branch_id: z.union([z.number(), z.null()]).optional(),
    section_id: z.union([z.number(), z.null()]).optional(),
    ref_number: z.union([z.string(), z.null()]).optional(),
    budget: z.union([z.number(), z.null()]).optional(),
    status: z.union([z.number(), z.null()]).optional().default(1),
    id: z.number().int(),
    c_at: z.union([z.string(), z.null()]).optional(),
    m_at: z.union([z.string(), z.null()]).optional(),
    gl_coa_name: z.union([z.string(), z.null()]).optional(),
    branch_name: z.union([z.string(), z.null()]).optional(),
    section_name: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const GLAccountPaginated = z
  .object({
    items: z.array(GLAccountRead),
    total: z.number().int(),
    page: z.number().int(),
    page_size: z.number().int(),
    has_more: z.boolean(),
  })
  .passthrough();
const APIResponse_GLAccountPaginated_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([GLAccountPaginated, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_GLAccountRead_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([GLAccountRead, z.null()]),
  })
  .partial()
  .passthrough();
const GLAccountUpdate = z
  .object({
    gl_coa_id: z.union([z.number(), z.null()]),
    branch_id: z.union([z.number(), z.null()]),
    section_id: z.union([z.number(), z.null()]),
    ref_number: z.union([z.string(), z.null()]),
    budget: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]).default(1),
  })
  .partial()
  .passthrough();
const GLAccount = z
  .object({
    id: z.union([z.number(), z.null()]),
    gl_coa_id: z.union([z.number(), z.null()]),
    branch_id: z.union([z.number(), z.null()]),
    section_id: z.union([z.number(), z.null()]),
    ref_number: z.union([z.string(), z.null()]),
    budget: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_GLAccount_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([GLAccount, z.null()]),
  })
  .partial()
  .passthrough();
const Body_create_gl_account_api_gl_account_create_post = z
  .object({ data: z.string() })
  .passthrough();
const QueueResponse = z
  .object({
    user_id: z.number().int(),
    data: z.union([z.unknown(), z.null()]).optional(),
    msg_id: z.string(),
  })
  .passthrough();
const APIResponse_list_QueueResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(QueueResponse), z.null()]),
  })
  .partial()
  .passthrough();
const CashBookOptionResponse = z
  .object({ id: z.number().int(), name_ln1: z.string() })
  .passthrough();
const APIResponse_list_CashBookOptionResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(CashBookOptionResponse), z.null()]),
  })
  .partial()
  .passthrough();
const SectionResponse = z
  .object({ id: z.number().int(), section_name: z.string() })
  .passthrough();
const APIResponse_list_SectionResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(SectionResponse), z.null()]),
  })
  .partial()
  .passthrough();
const OtherBranchResponse = z
  .object({
    id: z.number().int(),
    name_ln1: z.string(),
    ref_number: z.string(),
  })
  .passthrough();
const APIResponse_list_OtherBranchResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(OtherBranchResponse), z.null()]),
  })
  .partial()
  .passthrough();
const GLAccountOptionResponse = z
  .object({
    id: z.number().int(),
    name_ln1: z.string(),
    ref_number: z.union([z.string(), z.null()]).optional(),
    coa_number: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_list_GLAccountOptionResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(GLAccountOptionResponse), z.null()]),
  })
  .partial()
  .passthrough();
const GlAccountBasicInfoResponse = z
  .object({
    id: z.number().int(),
    section_id: z.union([z.number(), z.null()]).optional(),
    branch_id: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const GlAccountTransactionResponse = z
  .object({
    transaction_id: z.number().int(),
    transaction_number: z.string(),
    entered_date: z.string(),
    updated_date: z.string(),
    debit: z.number(),
    credit: z.number(),
    ini_name_ln1: z.union([z.string(), z.null()]).optional(),
    address_ln1: z.union([z.string(), z.null()]).optional(),
    cheque_num: z.union([z.string(), z.null()]).optional(),
    description: z.union([z.string(), z.null()]).optional(),
    entered_by: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const GlInfoResponse = z
  .object({
    gl_account: GlAccountBasicInfoResponse,
    last_transaction: z
      .union([GlAccountTransactionResponse, z.null()])
      .optional(),
  })
  .passthrough();
const APIResponse_GlInfoResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([GlInfoResponse, z.null()]),
  })
  .partial()
  .passthrough();
const PendingApprovedTransactionsResponse = z
  .object({
    transaction_number: z.string(),
    amount: z.number(),
    status: z.string(),
  })
  .passthrough();
const APIResponse_List_PendingApprovedTransactionsResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PendingApprovedTransactionsResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PendingApprovedTransactionResponse = z
  .object({
    transaction_number: z.string(),
    gl_account: z.number().int(),
    account_number: z.string(),
    gl_name: z.string(),
    debit: z.number(),
    name: z.string(),
    description: z.string(),
    status: z.number().int(),
  })
  .passthrough();
const APIResponse_List_PendingApprovedTransactionResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PendingApprovedTransactionResponse), z.null()]),
  })
  .partial()
  .passthrough();
const SaveTempTransactionRequest = z
  .object({
    gl_account_id: z.number().int(),
    section_id: z.number().int(),
    name: z.string(),
    description: z.string(),
    amount: z.number(),
    cheque_num: z.union([z.number(), z.null()]).optional(),
    cash_bank_gl_account: z.union([z.number(), z.null()]).optional(),
    other_branch_id: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const TransactionNumberResponse = z
  .object({ transaction_number: z.string() })
  .passthrough();
const APIResponse_TransactionNumberResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([TransactionNumberResponse, z.null()]),
  })
  .partial()
  .passthrough();
const PayTransactionRequest = z
  .object({ transaction_number: z.string() })
  .passthrough();
const FdAccountResponse = z
  .object({
    id: z.number().int(),
    ref_number: z.string(),
    customer_name: z.union([z.string(), z.null()]).optional(),
    customer_phone: z.union([z.string(), z.null()]).optional(),
    customer_number: z.union([z.string(), z.null()]).optional(),
    amount: z.number(),
    interest_rate: z.number(),
    interest: z.number(),
    period: z.number().int(),
    renew_date: z.string(),
    advance_loan_message: z.union([z.string(), z.null()]).optional(),
    selected_option_id: z.union([z.number(), z.null()]).optional(),
    selected_option_label: z.union([z.string(), z.null()]).optional(),
    is_disabled: z.boolean().optional().default(false),
  })
  .passthrough();
const APIResponse_List_FdAccountResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(FdAccountResponse), z.null()]),
  })
  .partial()
  .passthrough();
const Option = z
  .object({ id: z.number().int(), label: z.string() })
  .passthrough();
const app__schemas__fd_update__OptionsResponse = z
  .object({ options: z.array(Option) })
  .passthrough();
const APIResponse_OptionsResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([app__schemas__fd_update__OptionsResponse, z.null()]),
  })
  .partial()
  .passthrough();
const OptionRequest = z
  .object({ option: z.number().int(), ref_account_number: z.string() })
  .passthrough();
const InterestRange = z
  .object({ min: z.number(), max: z.number() })
  .passthrough();
const OptionResponse = z
  .object({
    valid: z.boolean(),
    message: z.union([z.string(), z.null()]).optional(),
    interest_range: z.union([InterestRange, z.null()]).optional(),
    savings_accounts: z.array(z.string()).optional(),
    zero_balance_accounts: z.union([z.array(z.string()), z.null()]).optional(),
    disable_actions: z.boolean().optional().default(false),
  })
  .passthrough();
const APIResponse_OptionResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([OptionResponse, z.null()]),
  })
  .partial()
  .passthrough();
const SaveOptionRequest = z
  .object({ option_id: z.number().int(), ref_account_number: z.string() })
  .passthrough();
const FDUpdateItem = z
  .object({
    fd_account_number: z.string(),
    option: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
    savings_account_number: z.union([z.string(), z.null()]).optional(),
    interest_rate: z.union([z.number(), z.string(), z.null()]).optional(),
  })
  .passthrough();
const FDUpdateRequest = z
  .object({ items: z.array(FDUpdateItem) })
  .passthrough();
const FDUpdateSummary = z
  .object({ summary: z.union([z.array(z.string()), z.null()]) })
  .passthrough();
const APIResponse_FDUpdateSummary_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([FDUpdateSummary, z.null()]),
  })
  .partial()
  .passthrough();
const FilterType = z.enum([
  'string',
  'int',
  'integer',
  'boolean',
  'date',
  'select',
  'today_date',
  'current_user_id',
  'current_branch_id',
  "<class 'app.schemas.report.FilterType.Config'>",
]);
const ReportFilter = z
  .object({
    name: z.string(),
    type: FilterType,
    query: z.union([z.string(), z.null()]).optional(),
    label: z.union([z.string(), z.null()]).optional(),
    value: z.union([z.string(), z.null()]).optional(),
    is_required: z.union([z.boolean(), z.null()]).optional().default(true),
  })
  .passthrough();
const CreateReportRequest = z
  .object({
    name: z.string(),
    category_id: z.union([z.number(), z.null()]).optional(),
    sql_query: z.string(),
    parameters: z
      .union([z.array(ReportFilter), z.null()])
      .optional()
      .default([]),
  })
  .passthrough();
const CreateReportResponse = z
  .object({
    report_uuid: z.string(),
    report_id: z.number().int(),
    name: z.string(),
    message: z.string(),
  })
  .passthrough();
const APIResponse_CreateReportResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([CreateReportResponse, z.null()]),
  })
  .partial()
  .passthrough();
const Body_upload_jrxml_api_report_upload_jrxml__report_uuid__post = z
  .object({ file: z.instanceof(File) })
  .passthrough();
const UploadJrxmlResponse = z
  .object({
    report_uuid: z.string(),
    success: z.boolean(),
    message: z.string(),
  })
  .passthrough();
const APIResponse_UploadJrxmlResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([UploadJrxmlResponse, z.null()]),
  })
  .partial()
  .passthrough();
const UploadReportRequest = z
  .object({
    id: z.union([z.number(), z.null()]).optional(),
    name: z.string(),
    jrxml_content_hash: z.union([z.string(), z.null()]),
    sql_query: z.union([z.string(), z.null()]),
    parameters: z
      .union([z.array(ReportFilter), z.null()])
      .optional()
      .default([]),
    category_id: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const app__responses__report_response__OptionsResponse = z
  .object({
    value: z.union([z.string(), z.number()]),
    label: z.string(),
    label_si: z.union([z.string(), z.null()]).optional(),
    label_ta: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const ReportFilterResponse = z
  .object({
    name: z.string(),
    type: FilterType,
    query: z.union([z.string(), z.null()]).optional(),
    label: z.union([z.string(), z.null()]).optional(),
    value: z.union([z.string(), z.null()]).optional(),
    is_required: z.union([z.boolean(), z.null()]).optional().default(true),
    options: z
      .union([
        z.array(app__responses__report_response__OptionsResponse),
        z.null(),
      ])
      .optional(),
  })
  .passthrough();
const ReportLanguageResponse = z
  .object({
    id: z.number().int(),
    language: z.string(),
    display_name: z.string(),
    code: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const ItReportTemplateResponse = z
  .object({
    id: z.number().int(),
    report_uuid: z.union([z.string(), z.null()]).optional(),
    name: z.string(),
    category_id: z.union([z.number(), z.null()]).optional(),
    sql_query: z.string(),
    parameters: z.array(ReportFilterResponse).optional().default([]),
    category_name: z.union([z.string(), z.null()]).optional(),
    uploaded_languages: z.array(z.number().int()).optional().default([]),
    languages: z.array(ReportLanguageResponse).optional().default([]),
  })
  .passthrough();
const APIResponse_ItReportTemplateResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([ItReportTemplateResponse, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_ItReportTemplateResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(ItReportTemplateResponse), z.null()]),
  })
  .partial()
  .passthrough();
const ReportFormat = z.enum(['pdf', 'html']);
const ReporDownloadRequest = z
  .object({
    report_id: z.number().int(),
    file_format: ReportFormat,
    parameters: z
      .union([z.object({}).partial().passthrough(), z.null()])
      .optional()
      .default({}),
    parameter_labels: z
      .union([z.object({}).partial().passthrough(), z.null()])
      .optional()
      .default({}),
    language_code: z.union([z.string(), z.null()]).optional().default('en'),
  })
  .passthrough();
const ItReportParamTypeResponse = z
  .object({
    id: z.number().int(),
    name: z.string(),
    label: z.string(),
    type: z.string(),
    default_value: z.union([z.string(), z.null()]).optional(),
    sql_query: z.union([z.string(), z.null()]).optional(),
    is_required: z.union([z.number(), z.null()]),
  })
  .passthrough();
const APIResponse_list_ItReportParamTypeResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(ItReportParamTypeResponse), z.null()]),
  })
  .partial()
  .passthrough();
const ItReportCategoryResponse = z
  .object({ id: z.number().int(), name: z.string() })
  .passthrough();
const APIResponse_list_ItReportCategoryResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(ItReportCategoryResponse), z.null()]),
  })
  .partial()
  .passthrough();
const LanguageFileStatusResponse = z
  .object({
    report_id: z.number().int(),
    english_content_hash: z.string(),
    uploaded_languages: z.array(z.number().int()),
  })
  .passthrough();
const APIResponse_LanguageFileStatusResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([LanguageFileStatusResponse, z.null()]),
  })
  .partial()
  .passthrough();
const ChequeItemResponse = z
  .object({
    id: z.number().int(),
    description: z.union([z.string(), z.null()]).optional(),
    receipt_no: z.union([z.string(), z.null()]).optional(),
    transaction_date: z.union([z.string(), z.null()]).optional(),
    amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    cheque_num: z.union([z.string(), z.null()]).optional(),
    pl_account_id: z.union([z.number(), z.null()]).optional(),
    account_name: z.union([z.string(), z.null()]).optional(),
    cash_bank_gl_account: z.union([z.number(), z.null()]).optional(),
    cash_book_name: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const CurrentAccountOption = z
  .object({
    id: z.number().int(),
    name_ln1: z.string(),
    ref_number: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const SendChequesToBankResponse = z
  .object({
    cheques: z.array(ChequeItemResponse),
    current_accounts: z.array(CurrentAccountOption),
  })
  .passthrough();
const APIResponse_SendChequesToBankResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([SendChequesToBankResponse, z.null()]),
  })
  .partial()
  .passthrough();
const SendChequesToBankRequest = z
  .object({
    cheque_ids: z.array(z.number().int()),
    current_account_id: z.number().int(),
    send_date: z.string(),
  })
  .passthrough();
const UserWithActiveStatusResponse = z
  .object({
    id: z.number().int(),
    user_id: z.number().int(),
    user_name: z.string(),
    name: z.string(),
    user_role: z.union([z.string(), z.null()]).optional(),
    active_branch_id: z.union([z.number(), z.null()]),
    user_group_id: z.union([z.number(), z.null()]),
    active_branch: z.union([GlBranch, z.null()]),
    user_group: z.union([ItUserGroup, z.null()]),
    institute: z.union([ItInstitute, z.null()]),
    ref_number: z.union([z.string(), z.null()]).optional(),
    password_status: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
    online_status: z.boolean(),
    reset_password_url: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_list_UserWithActiveStatusResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(UserWithActiveStatusResponse), z.null()]),
  })
  .partial()
  .passthrough();
const UserPermissionResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    permission: z.union([z.string(), z.null()]),
    description: z.union([z.string(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
  })
  .passthrough();
const APIResponse_list_UserPermissionResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(UserPermissionResponse), z.null()]),
  })
  .partial()
  .passthrough();
const UserMenuResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    label: z.union([z.string(), z.null()]),
    to_url: z.union([z.string(), z.null()]),
    parent_id: z.union([z.number(), z.null()]),
    order_num: z.union([z.number(), z.null()]),
    component: z.union([z.string(), z.null()]),
    icon: z.union([z.string(), z.null()]),
    has_menu: z.union([z.boolean(), z.null()]),
    cashier_required: z.union([z.boolean(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
  })
  .passthrough();
const APIResponse_list_UserMenuResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(UserMenuResponse), z.null()]),
  })
  .partial()
  .passthrough();
const UserGroupsResponse = z
  .object({ id: z.number().int(), name: z.string(), access_level: z.union([z.number().int(), z.null()]).optional() })
  .passthrough();
const APIResponse_list_UserGroupsResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(UserGroupsResponse), z.null()]),
  })
  .partial()
  .passthrough();
const UpdateFavoriteMenuRequest = z
  .object({ menu_id: z.number().int(), favourite: z.boolean() })
  .passthrough();
const UpdateFavouriteMenuResponse = z
  .object({ menu_id: z.number().int(), favourite: z.boolean() })
  .passthrough();
const APIResponse_UpdateFavouriteMenuResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([UpdateFavouriteMenuResponse, z.null()]),
  })
  .partial()
  .passthrough();
const TransactionAllowRequest = z
  .object({
    it_transaction_type_id: z.number().int(),
    amount: z.union([z.number(), z.null()]).optional(),
    approve_level: z.union([z.array(z.number().int()), z.null()]).optional(),
  })
  .passthrough();
const UserCreateRequest = z
  .object({
    name: z.string().min(1).max(300),
    user_name: z.string().min(3).max(100),
    ref_number: z.union([z.string(), z.null()]).optional(),
    user_group_id: z.union([z.number(), z.null()]).optional(),
    customer_id: z.union([z.number(), z.null()]).optional(),
    active_branch_id: z.union([z.number(), z.null()]).optional(),
    user_role: z.union([UserRole, z.null()]).optional().default('CLERK'),
    system_permissions: z
      .union([z.array(z.number().int()), z.null()])
      .optional(),
    system_menus: z.union([z.array(z.number().int()), z.null()]).optional(),
    user_reports: z.union([z.array(z.number().int()), z.null()]).optional(),
    transaction_allowances: z
      .union([z.array(TransactionAllowRequest), z.null()])
      .optional(),
  })
  .passthrough();
const UserCreateResponse = z
  .object({
    id: z.number().int(),
    user_id: z.number().int(),
    user_name: z.string(),
    name: z.string(),
    user_role: z.union([z.string(), z.null()]).optional(),
    active_branch_id: z.union([z.number(), z.null()]),
    user_group_id: z.union([z.number(), z.null()]),
    active_branch: z.union([GlBranch, z.null()]),
    user_group: z.union([ItUserGroup, z.null()]),
    institute: z.union([ItInstitute, z.null()]),
    ref_number: z.union([z.string(), z.null()]).optional(),
    reset_password_url: z.union([z.string(), z.null()]),
  })
  .passthrough();
const APIResponse_UserCreateResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([UserCreateResponse, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_int__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(z.number().int()), z.null()]),
  })
  .partial()
  .passthrough();
const BranchResponse = z
  .object({ branch_id: z.number().int(), branch_name: z.string() })
  .passthrough();
const APIResponse_list_BranchResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(BranchResponse), z.null()]),
  })
  .partial()
  .passthrough();
const SystemPermissionResponse = z
  .object({ id: z.number().int(), name: z.string(), description: z.string() })
  .passthrough();
const APIResponse_list_SystemPermissionResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(SystemPermissionResponse), z.null()]),
  })
  .partial()
  .passthrough();
const SystemMenuResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    label: z.union([z.string(), z.null()]),
    to_url: z.union([z.string(), z.null()]),
    parent_id: z.union([z.number(), z.null()]),
    order_num: z.union([z.number(), z.null()]),
    component: z.union([z.string(), z.null()]),
    icon: z.union([z.string(), z.null()]),
    has_menu: z.union([z.boolean(), z.null()]).default(true),
  })
  .partial()
  .passthrough();
const APIResponse_list_SystemMenuResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(SystemMenuResponse), z.null()]),
  })
  .partial()
  .passthrough();
const SystemMenuWithChildrenResponse: z.ZodType<SystemMenuWithChildrenResponse> =
  z.lazy(() =>
    z
      .object({
        id: z.union([z.number(), z.null()]),
        label: z.union([z.string(), z.null()]),
        to_url: z.union([z.string(), z.null()]),
        parent_id: z.union([z.number(), z.null()]),
        order_num: z.union([z.number(), z.null()]),
        component: z.union([z.string(), z.null()]),
        icon: z.union([z.string(), z.null()]),
        has_menu: z.union([z.boolean(), z.null()]).default(true),
        c_at: z.union([z.string(), z.null()]),
        c_by: z.union([z.number(), z.null()]),
        m_at: z.union([z.string(), z.null()]),
        m_by: z.union([z.number(), z.null()]),
        children: z.array(SystemMenuWithChildrenResponse).default([]),
      })
      .partial()
      .passthrough()
  );
const APIResponse_list_SystemMenuWithChildrenResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(SystemMenuWithChildrenResponse), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_SystemMenuResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([SystemMenuResponse, z.null()]),
  })
  .partial()
  .passthrough();
const ProcessRequest = z.object({ branch_id: z.number().int() }).passthrough();
const ProcessBackfillRequest = z
  .object({
    branch_id: z.number().int(),
    start_date: z.string(),
    end_date: z.string(),
  })
  .passthrough();
const ProductValidationRecord = z
  .object({
    id: z.number().int(),
    values: z.object({}).partial().passthrough(),
    valid: z.boolean(),
    missing_fields: z.array(z.string()),
  })
  .passthrough();
const ProductValidationType = z
  .object({
    required_fields: z.array(z.string()),
    records: z.array(ProductValidationRecord),
  })
  .passthrough();
const ProductValidationResponse = z
  .object({
    savings: ProductValidationType,
    loans: ProductValidationType,
    investments: ProductValidationType,
  })
  .passthrough();
const APIResponse_ProductValidationResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([ProductValidationResponse, z.null()]),
  })
  .partial()
  .passthrough();
const ProductValidationCheckResponse = z
  .object({ status: z.string(), message: z.string() })
  .passthrough();
const APIResponse_ProductValidationCheckResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([ProductValidationCheckResponse, z.null()]),
  })
  .partial()
  .passthrough();
const ChatLanguagesResponse = z
  .object({
    available_languages: z.array(z.string()),
    english_prompt_texts: z.array(z.string()),
  })
  .passthrough();
const APIResponse_ChatLanguagesResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([ChatLanguagesResponse, z.null()]),
  })
  .partial()
  .passthrough();
const ChatPromptResponse = z
  .object({
    id: z.number().int(),
    prompt_text: z.union([z.string(), z.null()]),
    prompt: z.string(),
    has_placeholders: z.boolean(),
  })
  .passthrough();
const ChatPromptsListResponse = z
  .object({ language: z.string(), prompts: z.array(ChatPromptResponse) })
  .passthrough();
const APIResponse_ChatPromptsListResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([ChatPromptsListResponse, z.null()]),
  })
  .partial()
  .passthrough();
const ChatPlaceholderInfoResponse = z
  .object({
    prompt_id: z.number().int(),
    prompt_placeholders: z.array(z.string()),
    query_placeholders: z.array(z.string()),
    all_placeholders: z.array(z.string()),
    has_placeholders_flag: z.boolean(),
    prompt_text: z.union([z.string(), z.null()]),
  })
  .passthrough();
const APIResponse_ChatPlaceholderInfoResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([ChatPlaceholderInfoResponse, z.null()]),
  })
  .partial()
  .passthrough();
const ChatSendRequest = z
  .object({
    prompt_id: z.union([z.number(), z.null()]).optional(),
    user_message: z.union([z.string(), z.null()]).optional(),
    selected_language: z.string(),
    form_data: z
      .union([z.object({}).partial().passthrough(), z.null()])
      .optional(),
  })
  .passthrough();
const ChatAIResponse = z
  .object({
    response: z.string(),
    success: z.boolean(),
    provider_used: z.string(),
    timestamp: z.string(),
    prompt: z.string(),
    prompt_id: z.union([z.number(), z.null()]).optional(),
    selected_language: z.string(),
    form_data: z
      .union([z.object({}).partial().passthrough(), z.null()])
      .optional(),
    user_message: z.union([z.string(), z.null()]).optional(),
    error: z.union([z.string(), z.null()]).optional(),
    usage: z.union([z.object({}).partial().passthrough(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_ChatAIResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([ChatAIResponse, z.null()]),
  })
  .partial()
  .passthrough();
const TranslateRequest = z
  .object({ text: z.string(), targets: z.array(z.string()) })
  .passthrough();
const ChatTranslationResult = z
  .object({
    translation: z.string(),
    success: z.boolean(),
    provider: z.string(),
  })
  .passthrough();
const ChatTranslationResponse = z
  .object({
    success: z.boolean(),
    translations: z.record(z.string(), ChatTranslationResult),
    error: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_ChatTranslationResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([ChatTranslationResponse, z.null()]),
  })
  .partial()
  .passthrough();
const LanguageResponse = z
  .object({
    id: z.number().int(),
    language: z.string(),
    display_name: z.string(),
    code: z.union([z.string(), z.null()]).optional(),
    is_active: z.boolean().optional().default(true),
  })
  .passthrough();
const LanguageListResponse = z
  .object({ languages: z.array(LanguageResponse), total: z.number().int() })
  .passthrough();
const APIResponse_LanguageListResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([LanguageListResponse, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_LanguageResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([LanguageResponse, z.null()]),
  })
  .partial()
  .passthrough();
const SystemHealthResponse = z
  .object({
    cache_service: z.boolean(),
    report_service: z.boolean(),
    timestamp: z.string(),
    status: z.enum(['healthy', 'degraded', 'unhealthy']),
  })
  .passthrough();
const APIResponse_SystemHealthResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([SystemHealthResponse, z.null()]),
  })
  .partial()
  .passthrough();
const MobileLoginRequest = z
  .object({
    user_name: z.string().min(3).max(100),
    password: z.string().min(8).max(128),
  })
  .passthrough();
const MobileLoginResponse = z
  .object({
    user_id: z.number().int(),
    user_name: z.string(),
    name: z.string(),
    user_role: z.union([z.string(), z.null()]).optional(),
    active_branch_id: z.union([z.number(), z.null()]).optional(),
    ref_number: z.union([z.string(), z.null()]).optional(),
    access_token: z.string(),
    refresh_token: z.string(),
  })
  .passthrough();
const APIResponse_MobileLoginResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([MobileLoginResponse, z.null()]),
  })
  .partial()
  .passthrough();
const MemberCreateRequest = z
  .object({
    customer_type_id: z.number().int(),
    title_ln1: z.union([z.string(), z.null()]).optional(),
    title_ln2: z.union([z.string(), z.null()]).optional(),
    title_ln3: z.union([z.string(), z.null()]).optional(),
    full_name_ln1: z.string(),
    full_name_ln2: z.union([z.string(), z.null()]).optional(),
    full_name_ln3: z.union([z.string(), z.null()]).optional(),
    ini_name_ln1: z.string().optional(),
    ini_name_ln2: z.union([z.string(), z.null()]).optional(),
    ini_name_ln3: z.union([z.string(), z.null()]).optional(),
    address_ln1: z.string().optional(),
    address_ln2: z.union([z.string(), z.null()]).optional(),
    address_ln3: z.union([z.string(), z.null()]).optional(),
    nic: z.string(),
    dob: z.string().datetime({ offset: true }),
    city_id: z.number().int(),
    sex: z.string(),
    marital_status: z.string(),
    mobile_1: z.string(),
    mobile_2: z.union([z.string(), z.null()]).optional(),
    email: z.union([z.string(), z.null()]).optional(),
    telephone: z.union([z.string(), z.null()]).optional(),
    nominee_name_ln1: z.union([z.string(), z.null()]).optional(),
    nominee_relation: z.union([z.string(), z.null()]).optional(),
    nominee_reg_id_num: z.union([z.string(), z.null()]).optional(),
    nominee_dob: z.union([z.string(), z.null()]).optional(),
    joined_date: z.string().datetime({ offset: true }),
    credit_limit: z.number(),
    credit_limit_app_date: z.string().datetime({ offset: true }),
    branch_id: z.number().int(),
    group_id: z.union([z.number(), z.null()]).optional(),
    field_officer_id: z.union([z.number(), z.null()]).optional(),
    custom_id: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const MemberCreateResponse = z
  .object({
    id: z.union([z.number(), z.null()]).optional(),
    created_at: z.string().datetime({ offset: true }).optional(),
    updated_at: z.string().datetime({ offset: true }).optional(),
    is_active: z.boolean().optional().default(true),
    member_id: z.number().int(),
  })
  .passthrough();
const APIResponse_MemberCreateResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([MemberCreateResponse, z.null()]),
  })
  .partial()
  .passthrough();
const MemberLocationCreateRequest = z
  .object({
    customer_id: z.number().int(),
    latitude: z.number(),
    longitude: z.number(),
  })
  .passthrough();
const MemberLocationCreateResponse = z
  .object({
    id: z.union([z.number(), z.null()]).optional(),
    created_at: z.string().datetime({ offset: true }).optional(),
    updated_at: z.string().datetime({ offset: true }).optional(),
    is_active: z.boolean().optional().default(true),
    customer_id: z.number().int(),
    customer_number: z.string(),
    latitude: z.number(),
    longitude: z.number(),
  })
  .passthrough();
const APIResponse_MemberLocationCreateResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([MemberLocationCreateResponse, z.null()]),
  })
  .partial()
  .passthrough();
const MobileCustomerLoanInfoRequest = z
  .object({
    customer_id: z.number().int().gt(0),
    branch_id: z.number().int().gt(0),
  })
  .passthrough();
const MobileExistingLoanDetails = z
  .object({
    ref_account_number: z.string(),
    product_name: z.string(),
    capital: z.number(),
    interest: z.number(),
    period: z.number(),
    balance: z.number(),
    past_due_amount: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const MobileCustomerLoanInfoResponse = z
  .object({
    customer_id: z.number().int(),
    customer_name: z.string(),
    customer_number: z.string(),
    nic: z.union([z.string(), z.null()]).optional(),
    mobile_1: z.union([z.string(), z.null()]).optional(),
    address: z.union([z.string(), z.null()]).optional(),
    existing_loans: z.array(MobileExistingLoanDetails).optional().default([]),
  })
  .passthrough();
const APIResponse_MobileCustomerLoanInfoResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([MobileCustomerLoanInfoResponse, z.null()]),
  })
  .partial()
  .passthrough();
const MobileLoanApplicationRequest = z
  .object({
    customer_id: z.number().int().gt(0),
    requested_amount: z.number().gt(0),
    loan_purpose: z.string().min(1).max(250),
    branch_id: z.number().int().gt(0),
  })
  .passthrough();
const MobileLoanApplicationResponse = z
  .object({
    id: z.number().int(),
    application_number: z.string(),
    customer_name: z.string(),
    approved_amount: z.number(),
    interest_rate: z.number(),
    period: z.number().int(),
    open_date: z.string().datetime({ offset: true }),
    reneve_date: z.string().datetime({ offset: true }),
    loan_purpose: z.string(),
  })
  .passthrough();
const APIResponse_MobileLoanApplicationResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([MobileLoanApplicationResponse, z.null()]),
  })
  .partial()
  .passthrough();
const MobileLoanApplicationStatusResponse = z
  .object({
    id: z.number().int(),
    application_number: z.union([z.string(), z.null()]).optional(),
    ci_customer_id: z.union([z.number(), z.null()]).optional(),
    customer_number: z.union([z.string(), z.null()]).optional(),
    amount: z.union([z.number(), z.null()]).optional(),
    status: z.union([z.string(), z.null()]).optional(),
    remarks: z.union([z.string(), z.null()]).optional(),
    created_at: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_list_MobileLoanApplicationStatusResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(MobileLoanApplicationStatusResponse), z.null()]),
  })
  .partial()
  .passthrough();
const ItExternalResource = z
  .object({
    id: z.union([z.number(), z.null()]),
    section: z.union([z.string(), z.null()]),
    title: z.union([z.string(), z.null()]),
    description: z.union([z.string(), z.null()]),
    link: z.union([z.string(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_List_ItExternalResource__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(ItExternalResource), z.null()]),
  })
  .partial()
  .passthrough();
const TsoCustomerItem = z
  .object({
    customer_id: z.number().int(),
    customer_number: z.union([z.string(), z.null()]).optional(),
    full_name: z.union([z.string(), z.null()]).optional(),
    email: z.union([z.string(), z.null()]).optional(),
    nic: z.union([z.string(), z.null()]).optional(),
    mobile_1: z.union([z.string(), z.null()]).optional(),
    address: z.union([z.string(), z.null()]).optional(),
    latitude: z.union([z.number(), z.null()]).optional(),
    longitude: z.union([z.number(), z.null()]).optional(),
    branch_id: z.union([z.number(), z.null()]).optional(),
    status: z.union([z.number(), z.null()]).optional(),
    member_date: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const TsoCustomersResponse = z
  .object({
    tso_id: z.number().int(),
    tso_name: z.union([z.string(), z.null()]).optional(),
    total_customers: z.number().int(),
    customers: z.array(TsoCustomerItem),
  })
  .passthrough();
const APIResponse_TsoCustomersResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([TsoCustomersResponse, z.null()]),
  })
  .partial()
  .passthrough();
const TsoTaskItem = z
  .object({
    task_id: z.number().int(),
    tso_id: z.union([z.number(), z.null()]).optional(),
    customer_id: z.union([z.number(), z.null()]).optional(),
    assigned_by: z.union([z.number(), z.null()]).optional(),
    description: z.union([z.string(), z.null()]).optional(),
    task_type: z.union([z.string(), z.null()]).optional(),
    task_status: z.union([z.string(), z.null()]).optional(),
    assigned_date: z.union([z.string(), z.null()]).optional(),
    started_date: z.union([z.string(), z.null()]).optional(),
    end_date: z.union([z.string(), z.null()]).optional(),
    status: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const TsoCustomerTasksResponse = z
  .object({
    tso_id: z.number().int(),
    customer_id: z.number().int(),
    total_tasks: z.number().int(),
    tasks: z.array(TsoTaskItem),
  })
  .passthrough();
const APIResponse_TsoCustomerTasksResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([TsoCustomerTasksResponse, z.null()]),
  })
  .partial()
  .passthrough();
const DueSummaryDetails = z
  .object({
    due_for_the_month: z.union([z.string(), z.null()]),
    balance_for_the_month: z.union([z.string(), z.null()]),
    total_balance: z.union([z.string(), z.null()]),
    collection_for_the_month: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const DueDetails = z
  .object({
    client_name: z.string(),
    product_name: z.string(),
    invoice_number: z.string(),
    due_on: z.union([z.string(), z.null()]).optional(),
    due_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    capital: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
  })
  .passthrough();
const DueResponse = z
  .object({ summary: DueSummaryDetails, dues: z.array(DueDetails) })
  .passthrough();
const APIResponse_DueResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([DueResponse, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_DueSummaryDetails_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([DueSummaryDetails, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_int_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const TaskItem = z
  .object({
    id: z.number().int(),
    tso_id: z.union([z.number(), z.null()]).optional(),
    tso_name: z.union([z.string(), z.null()]).optional(),
    customer_id: z.union([z.number(), z.null()]).optional(),
    customer_name: z.union([z.string(), z.null()]).optional(),
    customer_number: z.union([z.string(), z.null()]).optional(),
    assigned_by: z.union([z.number(), z.null()]).optional(),
    assigned_by_name: z.union([z.string(), z.null()]).optional(),
    description: z.union([z.string(), z.null()]).optional(),
    task_type: z.union([z.string(), z.null()]).optional(),
    task_status: z.union([z.string(), z.null()]).optional(),
    assigned_date: z.union([z.string(), z.null()]).optional(),
    started_date: z.union([z.string(), z.null()]).optional(),
    end_date: z.union([z.string(), z.null()]).optional(),
    status: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const TsoTasksResponse = z
  .object({
    tso_id: z.number().int(),
    tso_name: z.union([z.string(), z.null()]).optional(),
    total_tasks: z.number().int(),
    tasks: z.array(TaskItem),
  })
  .passthrough();
const APIResponse_TsoTasksResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([TsoTasksResponse, z.null()]),
  })
  .partial()
  .passthrough();
const CustomerTasksResponse = z
  .object({
    customer_id: z.number().int(),
    customer_name: z.union([z.string(), z.null()]).optional(),
    customer_number: z.union([z.string(), z.null()]).optional(),
    total_tasks: z.number().int(),
    tasks: z.array(TaskItem),
  })
  .passthrough();
const APIResponse_CustomerTasksResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([CustomerTasksResponse, z.null()]),
  })
  .partial()
  .passthrough();
const PlAccountTypeDocumentsCreate = z
  .object({
    pl_account_type_id: z.union([z.number(), z.null()]),
    document_name: z.union([z.string(), z.null()]),
    status: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const PlAccountTypeDocumentsRead = z
  .object({
    pl_account_type_id: z.union([z.number(), z.null()]).optional(),
    document_name: z.union([z.string(), z.null()]).optional(),
    status: z.union([z.number(), z.null()]).optional(),
    c_at: z.union([z.string(), z.null()]).optional(),
    c_by: z.union([z.number(), z.null()]).optional(),
    id: z.number().int(),
  })
  .passthrough();
const APIResponse_PlAccountTypeDocumentsRead_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PlAccountTypeDocumentsRead, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_PlAccountTypeDocumentsRead__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PlAccountTypeDocumentsRead), z.null()]),
  })
  .partial()
  .passthrough();
const PlAccountTypeDocumentsUpdate = z
  .object({
    pl_account_type_id: z.union([z.number(), z.null()]),
    document_name: z.union([z.string(), z.null()]),
    status: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const Body_create_savings_product_api_savings_create_products_post = z
  .object({ data: z.string() })
  .passthrough();
const SavingsProduct = z
  .object({
    id: z.number().int(),
    name_ln1: z.string(),
    name_ln2: z.union([z.string(), z.null()]),
    pl_operating_type_id: z.union([z.number(), z.null()]),
    interest_policy: z.union([z.number(), z.null()]),
    interest_rate_min: z.union([z.number(), z.null()]),
    interest_rate_max: z.union([z.number(), z.null()]),
    period_min: z.union([z.number(), z.null()]),
    period_max: z.union([z.number(), z.null()]),
    auto_number_creation: z
      .union([z.boolean(), z.null()])
      .optional()
      .default(true),
  })
  .passthrough();
const APIResponse_list_SavingsProduct__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(SavingsProduct), z.null()]),
  })
  .partial()
  .passthrough();
const SavingsAccount = z
  .object({
    account_no: z.string(),
    product_name: z.string(),
    balance: z.number(),
    opening_date: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_list_SavingsAccount__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(SavingsAccount), z.null()]),
  })
  .partial()
  .passthrough();
const CreateSavingsAccountRequest = z
  .object({
    customer_id: z.number().int(),
    savings_product_id: z.number().int(),
    period: z.number().int(),
    interest_rate: z.number(),
    ref_account_number: z.string(),
    interest_update_account: z.union([z.string(), z.null()]),
    maturity_type: z.union([z.string(), z.null()]),
    maturity_account_no: z.union([z.string(), z.null()]),
    join_account: z.union([z.string(), z.null()]),
    joint_withdrawal_option: z.union([z.string(), z.null()]).optional(),
    deposit_amount: z.number(),
    withdraw_instruction: z.string(),
  })
  .passthrough();
const CreateSavingsAccountResponse = z
  .object({ account_no: z.string(), message: z.string() })
  .passthrough();
const APIResponse_CreateSavingsAccountResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([CreateSavingsAccountResponse, z.null()]),
  })
  .partial()
  .passthrough();
const LastAccountNumberResponse = z
  .object({ account_number: z.string() })
  .passthrough();
const APIResponse_LastAccountNumberResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([LastAccountNumberResponse, z.null()]),
  })
  .partial()
  .passthrough();
const InterestUpdateAccountsResponse = z
  .object({ accounts: z.array(z.string()) })
  .passthrough();
const APIResponse_InterestUpdateAccountsResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([InterestUpdateAccountsResponse, z.null()]),
  })
  .partial()
  .passthrough();
const JointAccountsResponse = z
  .object({ customer_numbers: z.array(z.string()) })
  .passthrough();
const APIResponse_JointAccountsResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([JointAccountsResponse, z.null()]),
  })
  .partial()
  .passthrough();
const InvestmentProductPaginated = z
  .object({
    items: z.array(LoanProductRead),
    total: z.number().int(),
    page: z.number().int(),
    page_size: z.number().int(),
    has_more: z.boolean(),
  })
  .passthrough();
const APIResponse_InvestmentProductPaginated_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([InvestmentProductPaginated, z.null()]),
  })
  .partial()
  .passthrough();
const InvestmentProductRead = z
  .object({
    ref_number: z.union([z.string(), z.null()]).optional(),
    name_ln1: z.union([z.string(), z.null()]).optional(),
    name_ln2: z.union([z.string(), z.null()]).optional(),
    name_ln3: z.union([z.string(), z.null()]).optional(),
    it_institute_id: z.union([z.number(), z.null()]).optional(),
    gl_coa_id: z.union([z.number(), z.null()]).optional(),
    gl_interest_id: z.union([z.number(), z.null()]).optional(),
    gl_penalty_id: z.union([z.number(), z.null()]).optional(),
    gl_insurance_id: z.union([z.number(), z.null()]).optional(),
    gl_fee_id: z.union([z.number(), z.null()]).optional(),
    gl_interest_res_id: z.union([z.number(), z.null()]).optional(),
    gl_tax_account: z.union([z.number(), z.null()]).optional(),
    last_number: z.union([z.number(), z.null()]).optional(),
    pl_account_category_id: z.union([z.number(), z.null()]).optional(),
    pl_operating_type_id: z.union([z.number(), z.null()]).optional(),
    interest_policy: z.union([z.number(), z.null()]).optional(),
    interest_update_policy: z.union([z.number(), z.null()]).optional(),
    period_policy: z.union([z.number(), z.null()]).optional(),
    penalty_policy: z.union([z.number(), z.null()]).optional(),
    insurance_policy: z.union([z.number(), z.null()]).optional(),
    fee_policy: z.union([z.number(), z.null()]).optional(),
    interest_rate_max: z.union([z.number(), z.null()]).optional(),
    interest_rate_min: z.union([z.number(), z.null()]).optional(),
    period_max: z.union([z.number(), z.null()]).optional(),
    period_min: z.union([z.number(), z.null()]).optional(),
    penalty_rate: z.union([z.number(), z.null()]).optional(),
    insurance_rate: z.union([z.number(), z.null()]).optional(),
    fee_rate: z.union([z.number(), z.null()]).optional(),
    depreciation_rate: z.union([z.number(), z.null()]).optional(),
    minimum_balance: z.union([z.number(), z.null()]).optional(),
    allow_interest_edit: z.union([z.number(), z.null()]).optional(),
    loan_schedule_type: z.union([z.number(), z.null()]).optional(),
    tax_minimum_balance: z.union([z.number(), z.null()]).optional(),
    tax_rate: z.union([z.number(), z.null()]).optional(),
    interest_benefit_max_int: z.union([z.number(), z.null()]).optional(),
    auto_number_creation: z
      .union([z.boolean(), z.null()])
      .optional()
      .default(true),
    no_of_loan_guarantor: z.union([z.number(), z.null()]).optional(),
    passdue_policy: z.union([z.number(), z.null()]).optional(),
    fd_loan_margin: z.union([z.number(), z.null()]).optional(),
    fd_loan_inte_gap: z.union([z.number(), z.null()]).optional(),
    field_collection: z.union([z.number(), z.null()]).optional(),
    pb_type: z.union([z.number(), z.null()]).optional(),
    decimal_number: z.union([z.number(), z.null()]).optional(),
    status: z.union([z.number(), z.null()]).optional(),
    id: z.number().int(),
    category_name: z.union([z.string(), z.null()]).optional(),
    operating_type_name: z.union([z.string(), z.null()]).optional(),
    interest_policy_name: z.union([z.string(), z.null()]).optional(),
    period_policy_name: z.union([z.string(), z.null()]).optional(),
    c_at: z.union([z.string(), z.null()]).optional(),
    m_at: z.union([z.string(), z.null()]).optional(),
    documents: z
      .union([z.array(PlAccountTypeDocuments), z.null()])
      .optional()
      .default([]),
    info_fields: z
      .union([z.array(PLAccountTypeInfo), z.null()])
      .optional()
      .default([]),
    interest_update_policy_name: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_InvestmentProductRead_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([InvestmentProductRead, z.null()]),
  })
  .partial()
  .passthrough();
const InvestmentProductUpdate = z
  .object({
    ref_number: z.union([z.string(), z.null()]),
    name_ln1: z.union([z.string(), z.null()]),
    name_ln2: z.union([z.string(), z.null()]),
    name_ln3: z.union([z.string(), z.null()]),
    it_institute_id: z.union([z.number(), z.null()]),
    gl_coa_id: z.union([z.number(), z.null()]),
    gl_interest_id: z.union([z.number(), z.null()]),
    gl_penalty_id: z.union([z.number(), z.null()]),
    gl_insurance_id: z.union([z.number(), z.null()]),
    gl_fee_id: z.union([z.number(), z.null()]),
    gl_interest_res_id: z.union([z.number(), z.null()]),
    gl_tax_account: z.union([z.number(), z.null()]),
    last_number: z.union([z.number(), z.null()]),
    pl_account_category_id: z.union([z.number(), z.null()]),
    pl_operating_type_id: z.union([z.number(), z.null()]),
    interest_policy: z.union([z.number(), z.null()]),
    interest_update_policy: z.union([z.number(), z.null()]),
    period_policy: z.union([z.number(), z.null()]),
    penalty_policy: z.union([z.number(), z.null()]),
    insurance_policy: z.union([z.number(), z.null()]),
    fee_policy: z.union([z.number(), z.null()]),
    interest_rate_max: z.union([z.number(), z.null()]),
    interest_rate_min: z.union([z.number(), z.null()]),
    period_max: z.union([z.number(), z.null()]),
    period_min: z.union([z.number(), z.null()]),
    penalty_rate: z.union([z.number(), z.null()]),
    insurance_rate: z.union([z.number(), z.null()]),
    fee_rate: z.union([z.number(), z.null()]),
    depreciation_rate: z.union([z.number(), z.null()]),
    minimum_balance: z.union([z.number(), z.null()]),
    allow_interest_edit: z.union([z.number(), z.null()]),
    loan_schedule_type: z.union([z.number(), z.null()]),
    tax_minimum_balance: z.union([z.number(), z.null()]),
    tax_rate: z.union([z.number(), z.null()]),
    interest_benefit_max_int: z.union([z.number(), z.null()]),
    auto_number_creation: z.union([z.boolean(), z.null()]).default(true),
    no_of_loan_guarantor: z.union([z.number(), z.null()]),
    passdue_policy: z.union([z.number(), z.null()]),
    fd_loan_margin: z.union([z.number(), z.null()]),
    fd_loan_inte_gap: z.union([z.number(), z.null()]),
    field_collection: z.union([z.number(), z.null()]),
    pb_type: z.union([z.number(), z.null()]),
    decimal_number: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const Body_create_investment_product_api_investment_create_products_post = z
  .object({ data: z.string() })
  .passthrough();
const Body_add_product_policy_doc_api_investment_products__id__policy_doc_post =
  z.object({ doc_name: z.string(), file: z.instanceof(File) }).passthrough();
const ItFiscalYear = z
  .object({
    id: z.union([z.number(), z.null()]),
    it_institute_id: z.union([z.number(), z.null()]),
    year_name: z.union([z.string(), z.null()]),
    date_from: z.union([z.string(), z.null()]),
    date_upto: z.union([z.string(), z.null()]),
    status: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const FiscalMonthResponse = z
  .object({
    id: z.number().int(),
    fiscal_year_id: z.number().int(),
    month_name: z.union([z.string(), z.null()]).optional(),
    date_from: z.union([z.string(), z.null()]).optional(),
    date_upto: z.union([z.string(), z.null()]).optional(),
    status: z.number().int(),
    fiscal_year: z.union([ItFiscalYear, z.null()]).optional(),
  })
  .passthrough();
const APIResponse_list_FiscalMonthResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(FiscalMonthResponse), z.null()]),
  })
  .partial()
  .passthrough();
const ItExternalResourceCreate = z
  .object({
    section: z.union([z.string(), z.null()]),
    title: z.union([z.string(), z.null()]),
    description: z.union([z.string(), z.null()]),
    link: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const ItExternalResourceUpdate = z
  .object({
    section: z.union([z.string(), z.null()]),
    title: z.union([z.string(), z.null()]),
    description: z.union([z.string(), z.null()]),
    link: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const GLCOARead = z
  .object({
    gl_coa_type_id: z.union([z.number(), z.null()]).optional(),
    ref_number: z.union([z.string(), z.null()]).optional(),
    name_ln1: z.union([z.string(), z.null()]).optional(),
    name_ln2: z.union([z.string(), z.null()]).optional(),
    name_ln3: z.union([z.string(), z.null()]).optional(),
    gl_operation_type_id: z.union([z.number(), z.null()]).optional(),
    status: z.union([z.number(), z.null()]).optional().default(1),
    id: z.number().int(),
    c_at: z.union([z.string(), z.null()]).optional(),
    m_at: z.union([z.string(), z.null()]).optional(),
    gl_coa_type_name: z.union([z.string(), z.null()]).optional(),
    gl_operation_type_name: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const GLCOAPaginated = z
  .object({
    items: z.array(GLCOARead),
    total: z.number().int(),
    page: z.number().int(),
    page_size: z.number().int(),
    has_more: z.boolean(),
  })
  .passthrough();
const APIResponse_GLCOAPaginated_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([GLCOAPaginated, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_GLCOARead_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([GLCOARead, z.null()]),
  })
  .partial()
  .passthrough();
const GLCOAUpdate = z
  .object({
    gl_coa_type_id: z.union([z.number(), z.null()]),
    ref_number: z.union([z.string(), z.null()]),
    name_ln1: z.union([z.string(), z.null()]),
    name_ln2: z.union([z.string(), z.null()]),
    name_ln3: z.union([z.string(), z.null()]),
    gl_operation_type_id: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]).default(1),
  })
  .partial()
  .passthrough();
const APIResponse_GlCoa_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([GlCoa, z.null()]),
  })
  .partial()
  .passthrough();
const Body_create_gl_coa_api_gl_coa_create_post = z
  .object({ data: z.string() })
  .passthrough();
const PlEquipment = z
  .object({
    id: z.union([z.number(), z.null()]),
    pl_account_type_id: z.union([z.number(), z.null()]),
    gl_account_id: z.union([z.number(), z.null()]),
    gl_depre_id: z.union([z.number(), z.null()]),
    gl_depre_res_id: z.union([z.number(), z.null()]),
    purchase_value: z.union([z.string(), z.null()]),
    balance: z.union([z.string(), z.null()]),
    quantity: z.union([z.string(), z.null()]),
    total_depreciation: z.union([z.string(), z.null()]),
    monthly_depreciation: z.union([z.string(), z.null()]),
    depre_rate: z.union([z.string(), z.null()]),
    name_ln1: z.union([z.string(), z.null()]),
    name_ln2: z.union([z.string(), z.null()]),
    name_ln3: z.union([z.string(), z.null()]),
    supplier_name: z.union([z.string(), z.null()]),
    supplier_address: z.union([z.string(), z.null()]),
    note: z.union([z.string(), z.null()]),
    warranty: z.union([z.string(), z.null()]),
    serial_number: z.union([z.string(), z.null()]),
    branch_id: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
    depreciation_temp: z.union([z.string(), z.null()]),
    purchase_date: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_PlEquipment__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PlEquipment), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_PlAccountType__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PlAccountType), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_PlAccountTypeCategory__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PlAccountTypeCategory), z.null()]),
  })
  .partial()
  .passthrough();
const InvoiceItem = z
  .object({
    name: z.string(),
    productId: z.union([z.number(), z.null()]).optional(),
    description: z.union([z.string(), z.null()]).optional(),
    unitPrice: z.number().gt(0),
    qty: z.number().int().gt(0),
    amount: z.number().gt(0),
  })
  .passthrough();
const InvoiceTransactionRequest = z
  .object({
    customerId: z.number().int(),
    branchId: z.number().int(),
    plAccountTypeId: z.number().int(),
    quotationId: z.union([z.number(), z.null()]).optional(),
    invoiceDate: z.union([z.string(), z.null()]).optional(),
    creditPeriod: z.union([z.string(), z.null()]).optional(),
    items: z.array(InvoiceItem),
    totalAmount: z.number().gt(0),
    note: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const Month = z
  .object({
    id: z.number().int(),
    month_name: z.string(),
    year_name: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const MonthEstimate = z
  .object({ estimate: z.union([z.number(), z.null()]) })
  .partial()
  .passthrough();
const GlBranchEstimateGridRow = z
  .object({
    gl_account_id: z.number().int(),
    gl_account_number: z.union([z.string(), z.null()]),
    account_number: z.union([z.string(), z.null()]),
    gl_coa_id: z.union([z.number(), z.null()]),
    gl_account_name: z.union([z.string(), z.null()]),
    gl_section_name: z.union([z.string(), z.null()]),
    branch_id: z.number().int(),
    branch_name: z.union([z.string(), z.null()]),
    year_id: z.union([z.number(), z.null()]),
    year_name: z.union([z.string(), z.null()]),
    gl_account_budget: z.union([z.number(), z.null()]),
    months: z.record(z.string(), MonthEstimate),
  })
  .passthrough();
const GlBranchEstimateGridResponse = z
  .object({ months: z.array(Month), rows: z.array(GlBranchEstimateGridRow) })
  .passthrough();
const APIResponse_GlBranchEstimateGridResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([GlBranchEstimateGridResponse, z.null()]),
  })
  .partial()
  .passthrough();
const GlBranchEstimateItem = z
  .object({
    branch_id: z.number().int(),
    gl_account_id: z.number().int(),
    it_fiscal_month_id: z.number().int(),
    estimate_month: z.union([z.number(), z.null()]).optional(),
    estimate_cumulative: z.union([z.number(), z.null()]).optional(),
    status: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const GlBranchEstimateBulkRequest = z
  .object({ estimates: z.array(GlBranchEstimateItem) })
  .passthrough();
const APIResponse_List_GlBranchEstimateItem__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(GlBranchEstimateItem), z.null()]),
  })
  .partial()
  .passthrough();
const Body_upload_mdb_api_import_upload_post = z
  .object({ file: z.instanceof(File) })
  .passthrough();
const ImportUploadResponse = z
  .object({ file_id: z.unknown(), tables: z.array(z.record(z.string(), z.string())) })
  .passthrough();
const APIResponse_ImportUploadResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([ImportUploadResponse, z.null()]),
  })
  .partial()
  .passthrough();
const TableList = z
  .object({
    source_table: z.string(),
    destination_table: z.string(),
    status: z.string(),
    enabled: z.boolean(),
    rows_source: z.number().int(),
    rows_imported: z.number().int(),
    error_message: z.union([z.string(), z.null()]).optional(),
    can_advance: z.boolean(),
    can_clear: z.boolean(),
  })
  .passthrough();
const APIResponse_List_TableList__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(TableList), z.null()]),
  })
  .partial()
  .passthrough();
const TableImportStatusResponse = z
  .object({
    rows_imported: z.number().int(),
    rows_source: z.number().int(),
    status: z.string(),
    error_message: z.union([z.string(), z.null()]).optional(),
    unique_id: z.string(),
  })
  .passthrough();
const APIResponse_TableImportStatusResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([TableImportStatusResponse, z.null()]),
  })
  .partial()
  .passthrough();
const InstituteStatusResponse = z
  .object({ status: z.number().int() })
  .passthrough();
const APIResponse_InstituteStatusResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([InstituteStatusResponse, z.null()]),
  })
  .partial()
  .passthrough();
const ImportActiveResponse = z
  .object({ file_id: z.unknown(), tables: z.array(TableList) })
  .passthrough();
const APIResponse_ImportActiveResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([ImportActiveResponse, z.null()]),
  })
  .partial()
  .passthrough();
const TransactionNumberRow = z
  .object({
    branch_id: z.number().int(),
    branch_name: z.union([z.string(), z.null()]).optional(),
    it_transaction_type_id: z.number().int(),
    it_transaction_type_name: z.union([z.string(), z.null()]).optional(),
    last_number: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_List_TransactionNumberRow__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(TransactionNumberRow), z.null()]),
  })
  .partial()
  .passthrough();
const TransactionNumberUpdateRow = z
  .object({
    branch_id: z.number().int(),
    it_transaction_type_id: z.number().int(),
    last_number: z.number().int(),
  })
  .passthrough();
const TransactionNumbersUpdateRequest = z
  .object({ items: z.array(TransactionNumberUpdateRow) })
  .passthrough();
const PrintCategory = z.enum(['FRONT_PAGE', 'HEADING', 'TRANSACTIONS']);
const PlPrintPolicyCreate = z
  .object({
    passbook_policy_id: z.union([z.number(), z.null()]),
    passbook_type: z.union([z.number(), z.null()]),
    category: z.union([PrintCategory, z.null()]),
    config: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const PlPrintPolicyRead = z
  .object({
    passbook_policy_id: z.union([z.number(), z.null()]).optional(),
    passbook_type: z.union([z.number(), z.null()]).optional(),
    category: z.union([PrintCategory, z.null()]).optional(),
    config: z.union([z.string(), z.null()]).optional(),
    id: z.number().int(),
  })
  .passthrough();
const category__2 = z.union([PrintCategory, z.null()]).optional();
const PlPawningItemType = z
  .object({
    id: z.union([z.number(), z.null()]),
    it_institute_id: z.union([z.number(), z.null()]),
    name_ln1: z.union([z.string(), z.null()]),
    name_ln2: z.union([z.string(), z.null()]),
    name_ln3: z.union([z.string(), z.null()]),
    status: z.union([z.number(), z.null()]).default(1),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_PlPawningItemType__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PlPawningItemType), z.null()]),
  })
  .partial()
  .passthrough();
const PlPawningItemTypeCreate = z
  .object({
    it_institute_id: z.union([z.number(), z.null()]),
    name_ln1: z.union([z.string(), z.null()]),
    name_ln2: z.union([z.string(), z.null()]),
    name_ln3: z.union([z.string(), z.null()]),
    status: z.union([z.number(), z.null()]).optional().default(1),
  })
  .passthrough();
const APIResponse_PlPawningItemType_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PlPawningItemType, z.null()]),
  })
  .partial()
  .passthrough();
const PlPawningItemTypeUpdate = z
  .object({
    it_institute_id: z.union([z.number(), z.null()]),
    name_ln1: z.union([z.string(), z.null()]),
    name_ln2: z.union([z.string(), z.null()]),
    name_ln3: z.union([z.string(), z.null()]),
    status: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const PlPawningAssessedValue = z
  .object({
    id: z.union([z.number(), z.null()]),
    it_institute_id: z.union([z.number(), z.null()]),
    carat: z.union([z.number(), z.null()]),
    gram_value: z.union([z.number(), z.null()]),
    market_value: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]).default(1),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_PlPawningAssessedValue__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PlPawningAssessedValue), z.null()]),
  })
  .partial()
  .passthrough();
const PlPawningAssessedValueCreate = z
  .object({
    it_institute_id: z.union([z.number(), z.null()]),
    carat: z.union([z.number(), z.null()]),
    gram_value: z.union([z.number(), z.null()]),
    market_value: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]).default(1),
  })
  .partial()
  .passthrough();
const APIResponse_PlPawningAssessedValue_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PlPawningAssessedValue, z.null()]),
  })
  .partial()
  .passthrough();
const PlPawningAssessedValueUpdate = z
  .object({
    it_institute_id: z.union([z.number(), z.null()]),
    carat: z.union([z.number(), z.null()]),
    gram_value: z.union([z.number(), z.null()]),
    market_value: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const PawningItemRequest = z
  .object({
    item_type_id: z.number().int(),
    qty: z.number().int(),
    total_weight: z.number(),
    gold_weight: z.number(),
    carat: z.string(),
    assessed_value: z.number(),
    market_value: z.number(),
    pawning_amount: z.number(),
  })
  .passthrough();
const PawningApplicationRequest = z
  .object({
    customer_number: z.string(),
    product_id: z.number().int(),
    period: z.number().int(),
    interest_rate: z.number(),
    reason: z.union([z.string(), z.null()]).optional(),
    items: z.array(PawningItemRequest),
  })
  .passthrough();
const PawningApplicationResponse = z
  .object({
    application_id: z.number().int(),
    application_number: z.string(),
    account_id: z.number().int(),
    total_amount: z.number(),
    status: z.string(),
    created_at: z.string().datetime({ offset: true }),
  })
  .passthrough();
const APIResponse_PawningApplicationResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PawningApplicationResponse, z.null()]),
  })
  .partial()
  .passthrough();
const LoanPastDueType = z.enum(['coop', 'mfi']);
const LoanPastDueFilterRequest = z
  .object({
    date: z.union([z.string(), z.null()]),
    branch_id: z.union([z.number(), z.null()]),
    type: z.union([LoanPastDueType, z.null()]),
  })
  .partial()
  .passthrough();
const PastDueCategory = z
  .object({ label: z.string(), amount: z.number(), ratio: z.number() })
  .passthrough();
const LoanPastDuePieData = z
  .object({
    total_loan: z.number(),
    loan_without_pastdue: z.number(),
    total_loan_pastdue: z.number(),
    categories: z.array(PastDueCategory),
  })
  .passthrough();
const DetailedTableRow = z
  .object({
    category: z.string(),
    amount: z.number(),
    ratio: z.number(),
    no_of_loans: z.number().int(),
  })
  .passthrough();
const LoanPastDueDetailedTable = z
  .object({
    type: LoanPastDueType,
    total_loan: z.number(),
    loan_without_pastdue: z.number(),
    total_loan_pastdue: z.number(),
    rows: z.array(DetailedTableRow),
  })
  .passthrough();
const LoanPastDueResponse = z
  .object({
    pie_data: LoanPastDuePieData,
    detailed_table: LoanPastDueDetailedTable,
  })
  .passthrough();
const APIResponse_LoanPastDueResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([LoanPastDueResponse, z.null()]),
  })
  .partial()
  .passthrough();
const DashboardType = z.enum(['saving', 'loan']);
const AccountTypeSummary = z
  .object({
    account_type_id: z.number().int(),
    account_type_name: z.string(),
    total_balance: z.number(),
    account_count: z.number().int(),
    ratio: z.number(),
  })
  .passthrough();
const DashboardBarData = z
  .object({
    type: DashboardType,
    total_balance: z.number(),
    total_accounts: z.number().int(),
    categories: z.array(AccountTypeSummary),
  })
  .passthrough();
const DashboardDetailedTable = z
  .object({
    type: DashboardType,
    total_balance: z.number(),
    total_accounts: z.number().int(),
    rows: z.array(AccountTypeSummary),
  })
  .passthrough();
const AccountTypeDetail = z
  .object({
    account_type_id: z.number().int(),
    account_type_name: z.string(),
    description: z.union([z.string(), z.null()]).optional(),
    total_balance: z.number(),
    account_count: z.number(),
    logo_url: z.union([z.string(), z.null()]).optional(),
    flyer_url: z.union([z.string(), z.null()]).optional(),
    interest_rate_min: z.union([z.number(), z.null()]).optional(),
    interest_rate_max: z.union([z.number(), z.null()]).optional(),
    penalty_rate: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const DashboardResponse = z
  .object({
    bar_data: DashboardBarData,
    detailed_table: DashboardDetailedTable,
    account_type_details: z.array(AccountTypeDetail),
  })
  .passthrough();
const APIResponse_DashboardResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([DashboardResponse, z.null()]),
  })
  .partial()
  .passthrough();
const TransactionType = z.enum(['RECEIPT', 'PAYMENT', 'WITHDRAWAL', 'JOURNAL']);
const CommonTransactionRequest = z
  .object({
    uuid: z.union([z.string(), z.null()]).optional(),
    transaction_type: TransactionType,
    payload: z.object({}).partial().passthrough(),
    totalAmount: z.number().gt(0),
  })
  .passthrough();
const UnifiedTransactionResponse = z
  .object({
    message: z.string(),
    data: z.object({}).partial().passthrough(),
    requires_approval: z.union([z.boolean(), z.null()]).optional(),
    approval_level: z.union([z.number(), z.null()]).optional(),
    transaction_id: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_UnifiedTransactionResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([UnifiedTransactionResponse, z.null()]),
  })
  .partial()
  .passthrough();
const PendingApprovalResponse = z
  .object({
    id: z.string(),
    uuid: z.string(),
    transaction_type: z.string(),
    enum: z.union([z.string(), z.null()]).optional(),
    total_amount: z.number(),
    submitted_by: z.number().int(),
    submitted_by_name: z.string(),
    submitted_at: z.string(),
    payload: z.object({}).partial().passthrough(),
  })
  .passthrough();
const PendingApprovalsListResponse = z
  .object({
    transactions: z.array(PendingApprovalResponse),
    count: z.number().int(),
  })
  .passthrough();
const APIResponse_PendingApprovalsListResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PendingApprovalsListResponse, z.null()]),
  })
  .partial()
  .passthrough();
const ApprovalAction = z.enum(['APPROVE', 'REJECT']);
const ApprovalRequest = z
  .object({
    transaction_id: z.string(),
    msg_id: z.string(),
    action: ApprovalAction,
    approval_level: z.number().int().gte(1).lte(3),
    notes: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const ApprovalProcessResponse = z
  .object({
    message: z.string(),
    approved: z.boolean(),
    transaction_id: z.union([z.string(), z.null()]).optional(),
    next_level: z.union([z.number(), z.null()]).optional(),
    next_msg_id: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_ApprovalProcessResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([ApprovalProcessResponse, z.null()]),
  })
  .partial()
  .passthrough();
const UnifiedTransactionAction = z.enum([
  'SUBMIT',
  'APPROVE',
  'REJECT',
  'EXECUTE',
]);
const UnifiedActionRequest = z
  .object({
    action: UnifiedTransactionAction,
    uuid: z.union([z.string(), z.null()]).optional(),
    transaction_type: z.union([TransactionType, z.null()]).optional(),
    payload: z
      .union([z.object({}).partial().passthrough(), z.null()])
      .optional(),
    totalAmount: z.union([z.number(), z.null()]).optional(),
    transaction_id: z.union([z.string(), z.null()]).optional(),
    msg_id: z.union([z.string(), z.null()]).optional(),
    approval_level: z.union([z.number(), z.null()]).optional(),
    notes: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const ExecuteApprovedRequest = z
  .object({ transaction_id: z.string(), transaction_type: TransactionType })
  .passthrough();
const RootResponse = z
  .object({
    version: z.string(),
    environment: z.string(),
    app_name: z.string(),
  })
  .passthrough();
const APIResponse_RootResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([RootResponse, z.null()]),
  })
  .partial()
  .passthrough();

export const schemas = {
  LoginRequest,
  GlBranch,
  ItUserGroup,
  ItInstitute,
  LoginResponse,
  APIResponse_LoginResponse_,
  ValidationError,
  HTTPValidationError,
  APIResponse,
  RefreshTokenResponse,
  APIResponse_RefreshTokenResponse_,
  FirstTimeLoginRequest,
  AuthResponse,
  APIResponse_AuthResponse_,
  CashBookResponse,
  APIResponse_list_CashBookResponse__,
  CashierSignInRequest,
  CashierSignInResponse,
  APIResponse_CashierSignInResponse_,
  CashierSignOffDataResponse,
  APIResponse_CashierSignOffDataResponse_,
  CashierSignOffRequest,
  CashierSignOffResponse,
  APIResponse_CashierSignOffResponse_,
  CashierStatusResponse,
  APIResponse_CashierStatusResponse_,
  paymentOnly,
  GlTransactionHeader,
  APIResponse_GlTransactionHeader_,
  PlAccountType,
  PlAccountTypeCategory,
  PlPawningItemTypeResponse,
  PlPawningItemResponse,
  PlAccountResponse,
  ItTransactionType,
  PlTransactionResponse,
  APIResponse_PlTransactionResponse_,
  limit,
  APIResponse_list_PlTransactionResponse__,
  AccountTypeEnum,
  Accounts,
  ReceiptRequest,
  TransactionResponse,
  APIResponse_TransactionResponse_,
  WithdrawRequest,
  GlCoa,
  GlCoaType,
  GlSection,
  GLAccountResponse,
  GlTransactionResponse,
  GlTransactionHeaderResponse,
  APIResponse_GlTransactionHeaderResponse_,
  APIResponse_bool_,
  category,
  passbook_type,
  APIResponse_dict_,
  APIResponse_list_GlTransactionHeaderResponse__,
  ApprovalDashboardResponse,
  APIResponse_list_ApprovalDashboardResponse__,
  ApprovalDashboardUpdateRequest,
  JournalTransactionTypeEnum,
  JournalAccountStandard,
  EquipmentItem,
  JournalAccountEquipment,
  JournalRequest,
  EquipmentTransactionRequest,
  InvestmentItem,
  InvestmentTransactionRequest,
  APIResponse_list_Annotated_Union_JournalAccountStandard__JournalAccountEquipment___FieldInfo_annotation_NoneType__required_True__discriminator__account_type_____,
  save_journal_draft_api_transaction_journal_draft_post_Body,
  PrevMonthRequest,
  JournalPrevMonthResponse,
  APIResponse_JournalPrevMonthResponse_,
  MobilePosAccountResponse,
  MobilePosBranchResponse,
  MobilePosUiDataResponse,
  APIResponse_list_MobilePosUiDataResponse__,
  ReverseJournalResponse,
  APIResponse_list_ReverseJournalResponse__,
  ReverseJournalUpdateRequest,
  LoanDisbursementRequest,
  GlTransactionListResponse,
  APIResponse_GlTransactionListResponse_,
  ItTransactionTypeResponse,
  APIResponse_list_ItTransactionTypeResponse__,
  GLMonthBalanceTResponse,
  GLAccountTResponse,
  GLCoaTResponse,
  GLCoaTypeTResponse,
  GLNotePolicyTResponse,
  APIResponse_list_GLNotePolicyTResponse__,
  CancellableTransactionResponse,
  APIResponse_list_CancellableTransactionResponse__,
  Body_upload_cancellation_attachment_api_transaction_upload_cancellation_attachment_post,
  CancelTransactionRequest,
  PlLoanAmotResponse,
  APIResponse_list_PlLoanAmotResponse__,
  AutoRecoveryLoanDetail,
  AutoRecoveryRow,
  APIResponse_list_AutoRecoveryRow__,
  AutoRecoveryBatchRequest,
  APIResponse_Union_dict__NoneType__,
  FinancialDateResponse,
  APIResponse_FinancialDateResponse_,
  BranchLookupResponse,
  APIResponse_list_BranchLookupResponse__,
  CustomerTypeResponse,
  APIResponse_list_CustomerTypeResponse__,
  DeactivateOptionResponse,
  APIResponse_list_DeactivateOptionResponse__,
  CiCityResponse,
  APIResponse_list_CiCityResponse__,
  CiCustomerType,
  CiNomineeResponse,
  CustomerResponse,
  APIResponse_CustomerResponse_,
  CustomerUpdate,
  MobileCustomerResponse,
  APIResponse_MobileCustomerResponse_,
  APIResponse_list_PlAccountResponse__,
  CustomerListDetail,
  PaginatedCustomerListResponse,
  APIResponse_PaginatedCustomerListResponse_,
  CustomerCreate,
  CustomerReject,
  CustomerTypeFieldResponse,
  APIResponse_list_CustomerTypeFieldResponse__,
  CustomerTypeDocResponse,
  APIResponse_list_CustomerTypeDocResponse__,
  Body_create_customer_info_api_customer__customer_id__info_post,
  APIResponse_list_dict__,
  Body_upload_customer_document_api_customer__customer_id__docs_upload_post,
  Body_upload_customer_photo_api_customer__customer_number__photo_post,
  Body_upload_customer_signature_api_customer__customer_number__signature_post,
  CommonCustomerResponse,
  APIResponse_CommonCustomerResponse_,
  AccountBase,
  CommonAccountsResponse,
  APIResponse_CommonAccountsResponse_,
  PlAccountTypeSimpleResponse,
  APIResponse_list_PlAccountTypeSimpleResponse__,
  TransactionTypeResponse,
  APIResponse_list_TransactionTypeResponse__,
  UserRole,
  ItUserMaster,
  PlAccountCommentResponse,
  APIResponse_list_PlAccountCommentResponse__,
  PlAccountCommentCreateRequest,
  APIResponse_PlAccountCommentResponse_,
  LoanAppraisalAICommentRequest,
  LoanAppraisalAICommentResponse,
  APIResponse_LoanAppraisalAICommentResponse_,
  PlPolicy,
  PlAccountTypeDocuments,
  PLAccountTypeInfo,
  LoanProductResponse,
  APIResponse_list_LoanProductResponse__,
  LoanApplicationRequest,
  CiCustomer,
  LoanApplicationResponse,
  APIResponse_LoanApplicationResponse_,
  APIResponse_list_LoanApplicationResponse__,
  LoanAppraisalUpdateRequest,
  PlAccountInfoResponse,
  APIResponse_list_PlAccountInfoResponse__,
  PlDocumentsResponse,
  APIResponse_list_PlDocumentsResponse__,
  PlGuarantersResponse,
  APIResponse_PlGuarantersResponse_,
  SaveGuarantorItem,
  SaveGuarantorsRequest,
  APIResponse_str_,
  GuarantersListResponse,
  APIResponse_list_GuarantersListResponse__,
  Body_upload_loan_document_api_loan_application_documents_upload__pl_document_id__post,
  APIResponse_PlDocumentsResponse_,
  APIResponse_PlAccountInfoResponse_,
  approve_loan_application_api_loan_application_approve__application_id__post_Body,
  Body_fd_advance_upload_certificate_api_loan_application_fd_advance_upload_certificate_post,
  FdAdvanceLoanItem,
  FdAdvanceRequest,
  AmountAndPeriodResponse,
  APIResponse_list_AmountAndPeriodResponse__,
  LoanAmotRequest,
  APIResponse_PlAccountResponse_,
  CustomerLoanSummaryStats,
  LoanGuarantorResponse,
  MyLoanItemResponse,
  GuarantorRoleResponse,
  GuarantorLoanItemResponse,
  CustomerLoanSummaryResponse,
  APIResponse_CustomerLoanSummaryResponse_,
  LoanProductRead,
  LoanProductPaginated,
  APIResponse_LoanProductPaginated_,
  PlProductAIPolicyRequest,
  PlProductAIPolicyResponse,
  APIResponse_PlProductAIPolicyResponse_,
  APIResponse_LoanProductRead_,
  Body_update_loan_product_api_pl_product__id__post,
  Body_create_loan_product_api_pl_product_post,
  APIResponse_PlAccountType_,
  Body_add_product_policy_doc_api_pl_product__id__policy_doc_post,
  Body_upload_amortization_file_api_pl_product__id__amortization_file_post,
  APIResponse_list_,
  ItProductResponse,
  APIResponse_List_ItProductResponse__,
  Product,
  PlQuotationRequest,
  PlQuotationResponse,
  APIResponse_PlQuotationResponse_,
  APIResponse_List_PlQuotationResponse__,
  AccountFilterType,
  type,
  APIResponse_list_GLAccountResponse__,
  ChequeBookResponse,
  APIResponse_list_ChequeBookResponse__,
  GlCoaResponse,
  APIResponse_list_GlCoaResponse__,
  GLAccountRead,
  GLAccountPaginated,
  APIResponse_GLAccountPaginated_,
  APIResponse_GLAccountRead_,
  GLAccountUpdate,
  GLAccount,
  APIResponse_GLAccount_,
  Body_create_gl_account_api_gl_account_create_post,
  QueueResponse,
  APIResponse_list_QueueResponse__,
  CashBookOptionResponse,
  APIResponse_list_CashBookOptionResponse__,
  SectionResponse,
  APIResponse_list_SectionResponse__,
  OtherBranchResponse,
  APIResponse_list_OtherBranchResponse__,
  GLAccountOptionResponse,
  APIResponse_list_GLAccountOptionResponse__,
  GlAccountBasicInfoResponse,
  GlAccountTransactionResponse,
  GlInfoResponse,
  APIResponse_GlInfoResponse_,
  PendingApprovedTransactionsResponse,
  APIResponse_List_PendingApprovedTransactionsResponse__,
  PendingApprovedTransactionResponse,
  APIResponse_List_PendingApprovedTransactionResponse__,
  SaveTempTransactionRequest,
  TransactionNumberResponse,
  APIResponse_TransactionNumberResponse_,
  PayTransactionRequest,
  FdAccountResponse,
  APIResponse_List_FdAccountResponse__,
  Option,
  app__schemas__fd_update__OptionsResponse,
  APIResponse_OptionsResponse_,
  OptionRequest,
  InterestRange,
  OptionResponse,
  APIResponse_OptionResponse_,
  SaveOptionRequest,
  FDUpdateItem,
  FDUpdateRequest,
  FDUpdateSummary,
  APIResponse_FDUpdateSummary_,
  FilterType,
  ReportFilter,
  CreateReportRequest,
  CreateReportResponse,
  APIResponse_CreateReportResponse_,
  Body_upload_jrxml_api_report_upload_jrxml__report_uuid__post,
  UploadJrxmlResponse,
  APIResponse_UploadJrxmlResponse_,
  UploadReportRequest,
  app__responses__report_response__OptionsResponse,
  ReportFilterResponse,
  ReportLanguageResponse,
  ItReportTemplateResponse,
  APIResponse_ItReportTemplateResponse_,
  APIResponse_list_ItReportTemplateResponse__,
  ReportFormat,
  ReporDownloadRequest,
  ItReportParamTypeResponse,
  APIResponse_list_ItReportParamTypeResponse__,
  ItReportCategoryResponse,
  APIResponse_list_ItReportCategoryResponse__,
  LanguageFileStatusResponse,
  APIResponse_LanguageFileStatusResponse_,
  ChequeItemResponse,
  CurrentAccountOption,
  SendChequesToBankResponse,
  APIResponse_SendChequesToBankResponse_,
  SendChequesToBankRequest,
  UserWithActiveStatusResponse,
  APIResponse_list_UserWithActiveStatusResponse__,
  UserPermissionResponse,
  APIResponse_list_UserPermissionResponse__,
  UserMenuResponse,
  APIResponse_list_UserMenuResponse__,
  UserGroupsResponse,
  APIResponse_list_UserGroupsResponse__,
  UpdateFavoriteMenuRequest,
  UpdateFavouriteMenuResponse,
  APIResponse_UpdateFavouriteMenuResponse_,
  TransactionAllowRequest,
  UserCreateRequest,
  UserCreateResponse,
  APIResponse_UserCreateResponse_,
  APIResponse_list_int__,
  BranchResponse,
  APIResponse_list_BranchResponse__,
  SystemPermissionResponse,
  APIResponse_list_SystemPermissionResponse__,
  SystemMenuResponse,
  APIResponse_list_SystemMenuResponse__,
  SystemMenuWithChildrenResponse,
  APIResponse_list_SystemMenuWithChildrenResponse__,
  APIResponse_SystemMenuResponse_,
  ProcessRequest,
  ProcessBackfillRequest,
  ProductValidationRecord,
  ProductValidationType,
  ProductValidationResponse,
  APIResponse_ProductValidationResponse_,
  ProductValidationCheckResponse,
  APIResponse_ProductValidationCheckResponse_,
  ChatLanguagesResponse,
  APIResponse_ChatLanguagesResponse_,
  ChatPromptResponse,
  ChatPromptsListResponse,
  APIResponse_ChatPromptsListResponse_,
  ChatPlaceholderInfoResponse,
  APIResponse_ChatPlaceholderInfoResponse_,
  ChatSendRequest,
  ChatAIResponse,
  APIResponse_ChatAIResponse_,
  TranslateRequest,
  ChatTranslationResult,
  ChatTranslationResponse,
  APIResponse_ChatTranslationResponse_,
  LanguageResponse,
  LanguageListResponse,
  APIResponse_LanguageListResponse_,
  APIResponse_LanguageResponse_,
  SystemHealthResponse,
  APIResponse_SystemHealthResponse_,
  MobileLoginRequest,
  MobileLoginResponse,
  APIResponse_MobileLoginResponse_,
  MemberCreateRequest,
  MemberCreateResponse,
  APIResponse_MemberCreateResponse_,
  MemberLocationCreateRequest,
  MemberLocationCreateResponse,
  APIResponse_MemberLocationCreateResponse_,
  MobileCustomerLoanInfoRequest,
  MobileExistingLoanDetails,
  MobileCustomerLoanInfoResponse,
  APIResponse_MobileCustomerLoanInfoResponse_,
  MobileLoanApplicationRequest,
  MobileLoanApplicationResponse,
  APIResponse_MobileLoanApplicationResponse_,
  MobileLoanApplicationStatusResponse,
  APIResponse_list_MobileLoanApplicationStatusResponse__,
  ItExternalResource,
  APIResponse_List_ItExternalResource__,
  TsoCustomerItem,
  TsoCustomersResponse,
  APIResponse_TsoCustomersResponse_,
  TsoTaskItem,
  TsoCustomerTasksResponse,
  APIResponse_TsoCustomerTasksResponse_,
  DueSummaryDetails,
  DueDetails,
  DueResponse,
  APIResponse_DueResponse_,
  APIResponse_DueSummaryDetails_,
  APIResponse_int_,
  TaskItem,
  TsoTasksResponse,
  APIResponse_TsoTasksResponse_,
  CustomerTasksResponse,
  APIResponse_CustomerTasksResponse_,
  PlAccountTypeDocumentsCreate,
  PlAccountTypeDocumentsRead,
  APIResponse_PlAccountTypeDocumentsRead_,
  APIResponse_list_PlAccountTypeDocumentsRead__,
  PlAccountTypeDocumentsUpdate,
  Body_create_savings_product_api_savings_create_products_post,
  SavingsProduct,
  APIResponse_list_SavingsProduct__,
  SavingsAccount,
  APIResponse_list_SavingsAccount__,
  CreateSavingsAccountRequest,
  CreateSavingsAccountResponse,
  APIResponse_CreateSavingsAccountResponse_,
  LastAccountNumberResponse,
  APIResponse_LastAccountNumberResponse_,
  InterestUpdateAccountsResponse,
  APIResponse_InterestUpdateAccountsResponse_,
  JointAccountsResponse,
  APIResponse_JointAccountsResponse_,
  InvestmentProductPaginated,
  APIResponse_InvestmentProductPaginated_,
  InvestmentProductRead,
  APIResponse_InvestmentProductRead_,
  InvestmentProductUpdate,
  Body_create_investment_product_api_investment_create_products_post,
  Body_add_product_policy_doc_api_investment_products__id__policy_doc_post,
  ItFiscalYear,
  FiscalMonthResponse,
  APIResponse_list_FiscalMonthResponse__,
  ItExternalResourceCreate,
  ItExternalResourceUpdate,
  GLCOARead,
  GLCOAPaginated,
  APIResponse_GLCOAPaginated_,
  APIResponse_GLCOARead_,
  GLCOAUpdate,
  APIResponse_GlCoa_,
  Body_create_gl_coa_api_gl_coa_create_post,
  PlEquipment,
  APIResponse_list_PlEquipment__,
  APIResponse_list_PlAccountType__,
  APIResponse_list_PlAccountTypeCategory__,
  InvoiceItem,
  InvoiceTransactionRequest,
  Month,
  MonthEstimate,
  GlBranchEstimateGridRow,
  GlBranchEstimateGridResponse,
  APIResponse_GlBranchEstimateGridResponse_,
  GlBranchEstimateItem,
  GlBranchEstimateBulkRequest,
  APIResponse_List_GlBranchEstimateItem__,
  Body_upload_mdb_api_import_upload_post,
  ImportUploadResponse,
  APIResponse_ImportUploadResponse_,
  TableList,
  APIResponse_List_TableList__,
  TableImportStatusResponse,
  APIResponse_TableImportStatusResponse_,
  InstituteStatusResponse,
  APIResponse_InstituteStatusResponse_,
  ImportActiveResponse,
  APIResponse_ImportActiveResponse_,
  TransactionNumberRow,
  APIResponse_List_TransactionNumberRow__,
  TransactionNumberUpdateRow,
  TransactionNumbersUpdateRequest,
  PrintCategory,
  PlPrintPolicyCreate,
  PlPrintPolicyRead,
  category__2,
  PlPawningItemType,
  APIResponse_list_PlPawningItemType__,
  PlPawningItemTypeCreate,
  APIResponse_PlPawningItemType_,
  PlPawningItemTypeUpdate,
  PlPawningAssessedValue,
  APIResponse_list_PlPawningAssessedValue__,
  PlPawningAssessedValueCreate,
  APIResponse_PlPawningAssessedValue_,
  PlPawningAssessedValueUpdate,
  PawningItemRequest,
  PawningApplicationRequest,
  PawningApplicationResponse,
  APIResponse_PawningApplicationResponse_,
  LoanPastDueType,
  LoanPastDueFilterRequest,
  PastDueCategory,
  LoanPastDuePieData,
  DetailedTableRow,
  LoanPastDueDetailedTable,
  LoanPastDueResponse,
  APIResponse_LoanPastDueResponse_,
  DashboardType,
  AccountTypeSummary,
  DashboardBarData,
  DashboardDetailedTable,
  AccountTypeDetail,
  DashboardResponse,
  APIResponse_DashboardResponse_,
  TransactionType,
  CommonTransactionRequest,
  UnifiedTransactionResponse,
  APIResponse_UnifiedTransactionResponse_,
  PendingApprovalResponse,
  PendingApprovalsListResponse,
  APIResponse_PendingApprovalsListResponse_,
  ApprovalAction,
  ApprovalRequest,
  ApprovalProcessResponse,
  APIResponse_ApprovalProcessResponse_,
  UnifiedTransactionAction,
  UnifiedActionRequest,
  ExecuteApprovedRequest,
  RootResponse,
  APIResponse_RootResponse_,
};

const endpoints = makeApi([
  {
    method: 'get',
    path: '/',
    alias: 'root__get',
    requestFormat: 'json',
    response: APIResponse_RootResponse_,
  },
  {
    method: 'post',
    path: '/api/account-type-documents/',
    alias: 'create_document_type_api_account_type_documents__post',
    description: `Create a new document type for an account type`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PlAccountTypeDocumentsCreate,
      },
    ],
    response: APIResponse_PlAccountTypeDocumentsRead_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/account-type-documents/:document_type_id',
    alias:
      'get_document_type_api_account_type_documents__document_type_id__get',
    description: `Get a specific document type`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'document_type_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PlAccountTypeDocumentsRead_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/account-type-documents/:document_type_id',
    alias:
      'update_document_type_api_account_type_documents__document_type_id__put',
    description: `Update a document type`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PlAccountTypeDocumentsUpdate,
      },
      {
        name: 'document_type_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PlAccountTypeDocumentsRead_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/account-type-documents/:document_type_id',
    alias:
      'delete_document_type_api_account_type_documents__document_type_id__delete',
    description: `Delete a document type (soft delete)`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'document_type_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/account-type-documents/account-type/:account_type_id',
    alias:
      'get_document_types_api_account_type_documents_account_type__account_type_id__get',
    description: `Get all document types for a specific account type`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'account_type_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_PlAccountTypeDocumentsRead__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/auth/first_time_login',
    alias: 'first_time_login_api_auth_first_time_login_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: FirstTimeLoginRequest,
      },
    ],
    response: APIResponse_LoginResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/auth/login',
    alias: 'login_api_auth_login_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: LoginRequest,
      },
    ],
    response: APIResponse_LoginResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/auth/logout',
    alias: 'logout_api_auth_logout_post',
    requestFormat: 'json',
    response: APIResponse,
  },
  {
    method: 'post',
    path: '/api/auth/refresh',
    alias: 'refresh_token_api_auth_refresh_post',
    requestFormat: 'json',
    response: APIResponse_RefreshTokenResponse_,
  },
  {
    method: 'get',
    path: '/api/branches/all',
    alias: 'get_all_branches_api_branches_all_get',
    requestFormat: 'json',
    response: APIResponse_list_BranchResponse__,
  },
  {
    method: 'get',
    path: '/api/cashier/cash_books',
    alias: 'get_cash_books_api_cashier_cash_books_get',
    requestFormat: 'json',
    response: APIResponse_list_CashBookResponse__,
  },
  {
    method: 'post',
    path: '/api/cashier/sign_in',
    alias: 'cashier_sign_in_api_cashier_sign_in_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CashierSignInRequest,
      },
    ],
    response: APIResponse_CashierSignInResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/cashier/sign_off',
    alias: 'get_cashier_sign_off_data_api_cashier_sign_off_get',
    requestFormat: 'json',
    response: APIResponse_CashierSignOffDataResponse_,
  },
  {
    method: 'post',
    path: '/api/cashier/sign_off',
    alias: 'cashier_sign_off_api_cashier_sign_off_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CashierSignOffRequest,
      },
    ],
    response: APIResponse_CashierSignOffResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/cashier/status',
    alias: 'get_cashier_status_api_cashier_status_get',
    requestFormat: 'json',
    response: APIResponse_CashierStatusResponse_,
  },
  {
    method: 'get',
    path: '/api/chat/languages',
    alias: 'available_languages_api_chat_languages_get',
    description: `Get available languages and English prompt texts for language selection`,
    requestFormat: 'json',
    response: APIResponse_ChatLanguagesResponse_,
  },
  {
    method: 'post',
    path: '/api/chat/pie-chart-ai-message',
    alias: 'pie_chart_ai_message_api_chat_pie_chart_ai_message_post',
    description: `Generate AI summary for pie chart data.

- **message**: The prompt containing data to summarize (required)
- **language**: Language for the response (optional, defaults to English)`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({}).partial().passthrough(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/chat/prompt-placeholders/:prompt_id',
    alias: 'prompt_placeholders_api_chat_prompt_placeholders__prompt_id__get',
    description: `Get placeholder information for a specific prompt`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'prompt_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_ChatPlaceholderInfoResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/chat/quick-prompts/:language',
    alias: 'quick_prompts_by_language_api_chat_quick_prompts__language__get',
    description: `Get quick prompts for a specific language`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'language',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_ChatPromptsListResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/chat/send',
    alias: 'send_chat_api_chat_send_post',
    description: `Send chat message and get AI response.

- **selected_language**: Language for the response (required)
- **prompt_id**: ID of predefined prompt (optional)
- **user_message**: Free-form user message (optional)
- **form_data**: Additional data for placeholder replacement (optional)

At least one of prompt_id or user_message must be provided.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ChatSendRequest,
      },
    ],
    response: APIResponse_ChatAIResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/chat/send-stream',
    alias: 'send_chat_stream_api_chat_send_stream_post',
    description: `Send chat message with streaming response.

Returns Server-Sent Events (SSE) for real-time streaming.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ChatSendRequest,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/chat/translate',
    alias: 'translate_text_api_chat_translate_post',
    description: `Translate text into multiple target languages using AI provider.

- **text**: Source text to translate (required)
- **targets**: List of target language names (required)

No authentication required for translation endpoint.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TranslateRequest,
      },
    ],
    response: APIResponse_ChatTranslationResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/cheques/send-to-bank',
    alias: 'get_pending_cheques_for_bank_api_cheques_send_to_bank_get',
    description: `Get all cheques for the cashier session for today&#x27;s date.

Shows all cheques with:
- Cheque number populated
- Cash book &#x3D; cashier&#x27;s cash accounts
- Entered date &#x3D; today

Returns list of cheques with description, receipt number, date, amount,
and available current accounts for selection.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'cashier_session_id',
        type: 'Query',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_SendChequesToBankResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/cheques/send-to-bank',
    alias: 'send_cheques_to_bank_api_cheques_send_to_bank_post',
    description: `Process sending selected cheques to bank.

Updates the status of selected cheques and associates them with
the specified current account.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SendChequesToBankRequest,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/common/accounts',
    alias: 'get_all_accounts_api_common_accounts_get',
    requestFormat: 'json',
    response: APIResponse_CommonAccountsResponse_,
  },
  {
    method: 'get',
    path: '/api/common/customer/:customer_no',
    alias: 'get_customer_api_common_customer__customer_no__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_no',
        type: 'Path',
        schema: z.string().regex(/^[A-Za-z0-9]{3,10}$/),
      },
    ],
    response: APIResponse_CommonCustomerResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/common/fd-loan-products',
    alias: 'get_fd_loan_products_api_common_fd_loan_products_get',
    description: `Get all FD Advance loan products (passdue_policy&#x3D;75).`,
    requestFormat: 'json',
    response: APIResponse_list_PlAccountTypeSimpleResponse__,
  },
  {
    method: 'get',
    path: '/api/common/transaction-types',
    alias: 'get_transaction_types_api_common_transaction_types_get',
    description: `Get all transaction types with their IDs and names.`,
    requestFormat: 'json',
    response: APIResponse_list_TransactionTypeResponse__,
  },
  {
    method: 'get',
    path: '/api/customer/',
    alias: 'get_customers_api_customer__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_number',
        type: 'Query',
        schema: category,
      },
      {
        name: 'branch_id',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'status',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.number().int().gte(1).optional().default(1),
      },
      {
        name: 'page_size',
        type: 'Query',
        schema: z.number().int().gte(1).lte(100).optional().default(10),
      },
    ],
    response: APIResponse_PaginatedCustomerListResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/customer/',
    alias: 'create_new_customer_api_customer__post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CustomerCreate,
      },
    ],
    response: APIResponse_MobileCustomerResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/customer/:customer_id/accounts',
    alias: 'customer_account_details_api_customer__customer_id__accounts_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_id',
        type: 'Path',
        schema: z.number().int().gte(1),
      },
    ],
    response: APIResponse_list_PlAccountResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/customer/:customer_id/all_accounts',
    alias:
      'customer_all_account_details_api_customer__customer_id__all_accounts_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_id',
        type: 'Path',
        schema: z.number().int().gte(1),
      },
    ],
    response: APIResponse_list_PlAccountResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/customer/:customer_id/creditor_accounts',
    alias:
      'customer_savings_account_details_api_customer__customer_id__creditor_accounts_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_id',
        type: 'Path',
        schema: z.number().int().gte(1),
      },
    ],
    response: APIResponse_list_PlAccountResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/customer/:customer_id/docs',
    alias: 'get_customer_docs_api_customer__customer_id__docs_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_id',
        type: 'Path',
        schema: z.number().int().gte(1),
      },
    ],
    response: APIResponse_list_dict__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/customer/:customer_id/docs/upload',
    alias:
      'upload_customer_document_api_customer__customer_id__docs_upload_post',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          Body_upload_customer_document_api_customer__customer_id__docs_upload_post,
      },
      {
        name: 'customer_id',
        type: 'Path',
        schema: z.number().int().gte(1),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/customer/:customer_id/fd_accounts',
    alias:
      'customer_fd_account_details_api_customer__customer_id__fd_accounts_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_id',
        type: 'Path',
        schema: z.number().int().gte(1),
      },
    ],
    response: APIResponse_list_PlAccountResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/customer/:customer_id/info',
    alias: 'create_customer_info_api_customer__customer_id__info_post',
    requestFormat: 'form-url',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: Body_create_customer_info_api_customer__customer_id__info_post,
      },
      {
        name: 'customer_id',
        type: 'Path',
        schema: z.number().int().gte(1),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/customer/:customer_id/info',
    alias: 'get_customer_infos_api_customer__customer_id__info_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_id',
        type: 'Path',
        schema: z.number().int().gte(1),
      },
    ],
    response: APIResponse_list_dict__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/customer/:customer_id/saving_accounts',
    alias:
      'customer_savings_account_details_api_customer__customer_id__saving_accounts_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_id',
        type: 'Path',
        schema: z.number().int().gte(1),
      },
    ],
    response: APIResponse_list_PlAccountResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/customer/:customer_number',
    alias: 'get_customer_details_api_customer__customer_number__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_number',
        type: 'Path',
        schema: z.string().regex(/^[A-Za-z0-9]{3,10}$/),
      },
    ],
    response: APIResponse_CustomerResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/customer/:customer_number',
    alias: 'update_customer_api_customer__customer_number__put',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CustomerUpdate,
      },
      {
        name: 'customer_number',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_MobileCustomerResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/customer/:customer_number/approve',
    alias: 'approve_customer_api_customer__customer_number__approve_put',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_number',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_MobileCustomerResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/customer/:customer_number/photo',
    alias: 'upload_customer_photo_api_customer__customer_number__photo_post',
    description: `Upload customer profile photo`,
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ photo: z.instanceof(File) }).passthrough(),
      },
      {
        name: 'customer_number',
        type: 'Path',
        schema: z.string().regex(/^[A-Za-z0-9]{3,10}$/),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/customer/:customer_number/reject',
    alias: 'reject_customer_api_customer__customer_number__reject_put',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CustomerReject,
      },
      {
        name: 'customer_number',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_MobileCustomerResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/customer/:customer_number/signature',
    alias:
      'upload_customer_signature_api_customer__customer_number__signature_post',
    description: `Upload customer signature`,
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ signature: z.instanceof(File) }).passthrough(),
      },
      {
        name: 'customer_number',
        type: 'Path',
        schema: z.string().regex(/^[A-Za-z0-9]{3,10}$/),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/customer/branches',
    alias: 'get_branches_lookup_api_customer_branches_get',
    requestFormat: 'json',
    response: APIResponse_list_BranchLookupResponse__,
  },
  {
    method: 'get',
    path: '/api/customer/ci-cities',
    alias: 'get_ci_cities_api_customer_ci_cities_get',
    requestFormat: 'json',
    response: APIResponse_list_CiCityResponse__,
  },
  {
    method: 'get',
    path: '/api/customer/customer-type/:customer_type_id/docs',
    alias:
      'get_customer_type_docs_api_customer_customer_type__customer_type_id__docs_get',
    description: `Get all required documents for a customer type`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_type_id',
        type: 'Path',
        schema: z.number().int().gte(1),
      },
    ],
    response: APIResponse_list_CustomerTypeDocResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/customer/customer-type/:customer_type_id/info',
    alias:
      'get_customer_type_info_fields_api_customer_customer_type__customer_type_id__info_get',
    description: `Get all required info fields for a customer type`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_type_id',
        type: 'Path',
        schema: z.number().int().gte(1),
      },
    ],
    response: APIResponse_list_CustomerTypeFieldResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/customer/customer-types',
    alias: 'get_customer_types_lookup_api_customer_customer_types_get',
    requestFormat: 'json',
    response: APIResponse_list_CustomerTypeResponse__,
  },
  {
    method: 'get',
    path: '/api/customer/deactivate-options',
    alias: 'get_deactivate_options_api_customer_deactivate_options_get',
    requestFormat: 'json',
    response: APIResponse_list_DeactivateOptionResponse__,
  },
  {
    method: 'get',
    path: '/api/customer/files/:file_path',
    alias: 'get_file_api_customer_files__file_path__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'file_path',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/customer/id/:customer_id',
    alias: 'get_customer_by_id_api_customer_id__customer_id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_id',
        type: 'Path',
        schema: z.number().int().gte(1),
      },
    ],
    response: APIResponse_CustomerResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/dashboard/data/:dashboard_type',
    alias: 'get_dashboard_data_api_dashboard_data__dashboard_type__get',
    description: `Get dashboard data for saving or loan dashboard`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'dashboard_type',
        type: 'Path',
        schema: z.enum(['saving', 'loan']),
      },
    ],
    response: APIResponse_DashboardResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/external_resource/',
    alias: 'load_resources_api_external_resource__get',
    requestFormat: 'json',
    response: z.array(ItExternalResource),
  },
  {
    method: 'post',
    path: '/api/external_resource/',
    alias: 'save_resource_api_external_resource__post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ItExternalResourceCreate,
      },
    ],
    response: ItExternalResource,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/external_resource/:resource_id',
    alias: 'delete_resource_api_external_resource__resource_id__delete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'resource_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/external_resource/:resource_id',
    alias: 'update_resource_api_external_resource__resource_id__put',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ItExternalResourceUpdate,
      },
      {
        name: 'resource_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: ItExternalResource,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/fd-update/fd-accounts',
    alias: 'get_fd_accounts_api_fd_update_fd_accounts_get',
    description: `Retrieves a list of fixed deposit accounts.`,
    requestFormat: 'json',
    response: APIResponse_List_FdAccountResponse__,
  },
  {
    method: 'post',
    path: '/api/fd-update/fd-update',
    alias: 'update_fd_accounts_api_fd_update_fd_update_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: FDUpdateRequest,
      },
    ],
    response: APIResponse_FDUpdateSummary_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/fd-update/fd-update-options',
    alias: 'get_fd_update_options_api_fd_update_fd_update_options_get',
    description: `Retrieves a list of fixed deposit accounts.`,
    requestFormat: 'json',
    response: APIResponse_OptionsResponse_,
  },
  {
    method: 'post',
    path: '/api/fd-update/save-fd-option',
    alias: 'save_fd_option_api_fd_update_save_fd_option_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SaveOptionRequest,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/fd-update/validate-fd-update',
    alias: 'validate_fd_update_api_fd_update_validate_fd_update_post',
    description: `Validates the FD update according to the selected option.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: OptionRequest,
      },
    ],
    response: APIResponse_OptionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/financial_date/current',
    alias: 'get_current_financial_date_api_financial_date_current_get',
    requestFormat: 'json',
    response: APIResponse_FinancialDateResponse_,
  },
  {
    method: 'get',
    path: '/api/fiscal_month/all',
    alias: 'get_all_fiscal_months_api_fiscal_month_all_get',
    description: `Retrieves a list of all fiscal months.`,
    requestFormat: 'json',
    response: APIResponse_list_FiscalMonthResponse__,
  },
  {
    method: 'get',
    path: '/api/gl_account',
    alias: 'get_gl_accounts_api_gl_account_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'branch_id',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'type',
        type: 'Query',
        schema: type,
      },
    ],
    response: APIResponse_list_GLAccountResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/gl_account/:id',
    alias: 'get_gl_account_by_id_api_gl_account__id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_GLAccountRead_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/gl_account/:id',
    alias: 'update_gl_account_api_gl_account__id__post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GLAccountUpdate,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_GLAccount_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/gl_account/available-coas',
    alias: 'get_available_coas_api_gl_account_available_coas_get',
    description: `Returns COAs not yet created as GL Accounts for the given branch+section combination.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'branch_id',
        type: 'Query',
        schema: z.number().int(),
      },
      {
        name: 'section_id',
        type: 'Query',
        schema: passbook_type,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/gl_account/cheque_books',
    alias: 'get_cheque_books_api_gl_account_cheque_books_get',
    description: `Populates the cheque cash book options if cheque is ticked.
If not cash book will be selected automatically in the login.`,
    requestFormat: 'json',
    response: APIResponse_list_ChequeBookResponse__,
  },
  {
    method: 'get',
    path: '/api/gl_account/coa',
    alias: 'get_gl_coas_api_gl_account_coa_get',
    description: `Retrieves the list of GL COAs for the current user&#x27;s active branch.`,
    requestFormat: 'json',
    response: APIResponse_list_GlCoaResponse__,
  },
  {
    method: 'post',
    path: '/api/gl_account/create',
    alias: 'create_gl_account_api_gl_account_create_post',
    requestFormat: 'form-url',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ data: z.string() }).passthrough(),
      },
    ],
    response: APIResponse_GLAccount_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/gl_account/gl-account-lookups',
    alias: 'get_gl_account_lookups_api_gl_account_gl_account_lookups_get',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/gl_account/paginated',
    alias: 'get_all_gl_account_paginated_api_gl_account_paginated_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.number().int().optional().default(1),
      },
      {
        name: 'page_size',
        type: 'Query',
        schema: z.number().int().optional().default(10),
      },
      {
        name: 'search_term',
        type: 'Query',
        schema: category,
      },
      {
        name: 'branch_id',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'gl_coa_id',
        type: 'Query',
        schema: passbook_type,
      },
    ],
    response: APIResponse_GLAccountPaginated_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/gl_branch_estimate/bulk_upsert',
    alias: 'bulk_upsert_estimates_api_gl_branch_estimate_bulk_upsert_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GlBranchEstimateBulkRequest,
      },
    ],
    response: APIResponse_List_GlBranchEstimateItem__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/gl_branch_estimate/grid',
    alias: 'get_estimate_grid_api_gl_branch_estimate_grid_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'branch_id',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'fiscal_year_id',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'gl_coa_id',
        type: 'Query',
        schema: passbook_type,
      },
    ],
    response: APIResponse_GlBranchEstimateGridResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/gl_branch_estimate/overview',
    alias: 'get_budget_overview_api_gl_branch_estimate_overview_get',
    description: `Return annual budget overview aggregated per GL account (sum of monthly estimates).`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'branch_id',
        type: 'Query',
        schema: z.number().int(),
      },
      {
        name: 'coa_type_group_id',
        type: 'Query',
        schema: z.number().int(),
      },
      {
        name: 'section_id',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'fiscal_year_id',
        type: 'Query',
        schema: passbook_type,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/gl-coa/:id',
    alias: 'get_gl_coa_by_id_api_gl_coa__id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_GLCOARead_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/gl-coa/:id',
    alias: 'update_gl_coa_api_gl_coa__id__post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GLCOAUpdate,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_GlCoa_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/gl-coa/create',
    alias: 'create_gl_coa_api_gl_coa_create_post',
    requestFormat: 'form-url',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ data: z.string() }).passthrough(),
      },
    ],
    response: APIResponse_GlCoa_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/gl-coa/gl-coa-lookups',
    alias: 'get_gl_coa_lookups_api_gl_coa_gl_coa_lookups_get',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/gl-coa/next-ref-number/:type_group_id',
    alias: 'get_next_ref_number_api_gl_coa_next_ref_number__type_group_id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'type_group_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/gl-coa/paginated',
    alias: 'get_all_gl_coa_paginated_api_gl_coa_paginated_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.number().int().optional().default(1),
      },
      {
        name: 'page_size',
        type: 'Query',
        schema: z.number().int().optional().default(10),
      },
      {
        name: 'search_term',
        type: 'Query',
        schema: category,
      },
      {
        name: 'gl_coa_type_id',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'gl_operation_type_id',
        type: 'Query',
        schema: passbook_type,
      },
    ],
    response: APIResponse_GLCOAPaginated_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/import/:file_id/tables',
    alias: 'list_tables_api_import__file_id__tables_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'file_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_List_TableList__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/import/:file_id/tables/:source_table/:destination_table/advance',
    alias:
      'advance_table_api_import__file_id__tables__source_table___destination_table__advance_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'file_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'source_table',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'destination_table',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/import/:file_id/tables/:source_table/:destination_table/clear',
    alias:
      'clear_table_api_import__file_id__tables__source_table___destination_table__clear_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'file_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'source_table',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'destination_table',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/import/:file_id/tables/:table',
    alias: 'import_table_api_import__file_id__tables__table__post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'file_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'table',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_TableImportStatusResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/import/active',
    alias: 'get_active_import_api_import_active_get',
    requestFormat: 'json',
    response: APIResponse_ImportActiveResponse_,
  },
  {
    method: 'post',
    path: '/api/import/complete-mdb-process',
    alias: 'complete_mdb_process_api_import_complete_mdb_process_post',
    requestFormat: 'json',
    response: APIResponse_InstituteStatusResponse_,
  },
  {
    method: 'get',
    path: '/api/import/institute-status',
    alias: 'get_institute_status_api_import_institute_status_get',
    requestFormat: 'json',
    response: APIResponse_InstituteStatusResponse_,
  },
  {
    method: 'get',
    path: '/api/import/transaction-numbers',
    alias: 'get_transaction_numbers_api_import_transaction_numbers_get',
    requestFormat: 'json',
    response: APIResponse_List_TransactionNumberRow__,
  },
  {
    method: 'post',
    path: '/api/import/transaction-numbers',
    alias: 'save_transaction_numbers_api_import_transaction_numbers_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TransactionNumbersUpdateRequest,
      },
    ],
    response: APIResponse_List_TransactionNumberRow__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/import/upload',
    alias: 'upload_mdb_api_import_upload_post',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ file: z.instanceof(File) }).passthrough(),
      },
    ],
    response: APIResponse_ImportUploadResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/investment/account-types',
    alias: 'get_investment_account_types_api_investment_account_types_get',
    description: `Return PL account types that belong to the investment category.`,
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/investment/accounts',
    alias: 'get_investment_accounts_api_investment_accounts_get',
    description: `Return investment PL accounts for a branch. Uses PLAccountService internally.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'branch_id',
        type: 'Query',
        schema: passbook_type,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/investment/create-products',
    alias: 'create_investment_product_api_investment_create_products_post',
    requestFormat: 'form-url',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ data: z.string() }).passthrough(),
      },
    ],
    response: APIResponse_PlAccountType_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/investment/investment-prod-lookups',
    alias: 'get_product_lookups_api_investment_investment_prod_lookups_get',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/investment/products/:id',
    alias: 'get_investment_product_by_id_api_investment_products__id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_InvestmentProductRead_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/investment/products/:id',
    alias: 'update_investment_product_api_investment_products__id__post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: InvestmentProductUpdate,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PlAccountType_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/investment/products/:id/policy-doc',
    alias:
      'add_product_policy_doc_api_investment_products__id__policy_doc_post',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          Body_add_product_policy_doc_api_investment_products__id__policy_doc_post,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/investment/products/paginated',
    alias:
      'get_all_investment_products_paginated_api_investment_products_paginated_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.number().int().optional().default(1),
      },
      {
        name: 'page_size',
        type: 'Query',
        schema: z.number().int().optional().default(10),
      },
      {
        name: 'search_term',
        type: 'Query',
        schema: category,
      },
      {
        name: 'category_id',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'operating_type_id',
        type: 'Query',
        schema: passbook_type,
      },
    ],
    response: APIResponse_InvestmentProductPaginated_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/investment/products/policy-doc/:doc_id',
    alias:
      'delete_policy_doc_api_investment_products_policy_doc__doc_id__delete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'doc_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/invoice/process',
    alias: 'process_invoice_api_invoice_process_post',
    description: `Enqueue invoice transaction for background processing.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z
          .object({
            customerId: z.number().int(),
            branchId: z.number().int(),
            plAccountTypeId: z.number().int(),
            quotationId: z.union([z.number(), z.null()]).optional(),
            invoiceDate: z.union([z.string(), z.null()]).optional(),
            creditPeriod: z.union([z.string(), z.null()]).optional(),
            items: z.array(InvoiceItem),
            totalAmount: z.number().gt(0),
            note: z.union([z.string(), z.null()]).optional(),
          })
          .passthrough(),
      },
    ],
    response: APIResponse_TransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/it_product/all',
    alias: 'get_all_it_products_api_it_product_all_get',
    description: `Return a simple list of IT products (from it_products table).

This endpoint requires authentication (current_user).`,
    requestFormat: 'json',
    response: APIResponse_List_ItProductResponse__,
  },
  {
    method: 'get',
    path: '/api/language',
    alias: 'get_all_languages_api_language_get',
    description: `Get all active languages available in the system.

Returns a list of all active languages with their codes and display names.`,
    requestFormat: 'json',
    response: APIResponse_LanguageListResponse_,
  },
  {
    method: 'get',
    path: '/api/language/:language_id',
    alias: 'get_language_by_id_api_language__language_id__get',
    description: `Get a specific language by its ID.

Args:
    language_id: The ID of the language to retrieve
    
Returns:
    Language details including ID, code, and display name`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'language_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_LanguageResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/language/code/:language_code',
    alias: 'get_language_by_code_api_language_code__language_code__get',
    description: `Get a specific language by its code.

Args:
    language_code: The language code (e.g., &#x27;english&#x27;, &#x27;sinhala&#x27;)
    
Returns:
    Language details including ID, code, and display name`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'language_code',
        type: 'Path',
        schema: z.string().min(1).max(10),
      },
    ],
    response: APIResponse_LanguageResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/language/name/:display_name',
    alias: 'get_language_by_name_api_language_name__display_name__get',
    description: `Get a specific language by its display name.

Args:
    display_name: The display name (e.g., &#x27;English&#x27;, &#x27;සිංහල&#x27;)
    
Returns:
    Language details including ID, code, and display name`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'display_name',
        type: 'Path',
        schema: z.string().min(1).max(50),
      },
    ],
    response: APIResponse_LanguageResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/loan-application/:application_id/ai-comment',
    alias:
      'generate_ai_appraisal_comment_api_loan_application__application_id__ai_comment_post',
    description: `Generate AI-supported appraisal comment using selected customer/account data points.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: LoanAppraisalAICommentRequest,
      },
      {
        name: 'application_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_LoanAppraisalAICommentResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/loan-application/:application_id/appraisal',
    alias:
      'update_loan_application_appraisal_api_loan_application__application_id__appraisal_post',
    description: `Update loan application during appraisal process`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: LoanAppraisalUpdateRequest,
      },
      {
        name: 'application_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_LoanApplicationResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/loan-application/account_info/:account_id',
    alias:
      'get_loan_account_info_api_loan_application_account_info__account_id__get',
    description: `Get detailed information about a loan account`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'account_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_list_PlAccountInfoResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/loan-application/all-products',
    alias: 'get_loan_products_api_loan_application_all_products_get',
    description: `Get all available loan products for the current branch`,
    requestFormat: 'json',
    response: APIResponse_list_LoanProductResponse__,
  },
  {
    method: 'post',
    path: '/api/loan-application/amot_schedule',
    alias: 'get_loan_amot_api_loan_application_amot_schedule_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: LoanAmotRequest,
      },
    ],
    response: APIResponse_list_PlLoanAmotResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/loan-application/amount_and_period/:pl_account_type_id',
    alias:
      'get_amount_and_period_api_loan_application_amount_and_period__pl_account_type_id__get',
    description: `Get amount and period options for a given loan product and amount`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'pl_account_type_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_AmountAndPeriodResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/loan-application/approve/:application_id',
    alias:
      'approve_loan_application_api_loan_application_approve__application_id__post',
    description: `Approve loan application (set status to in_disburse, pl_account.status to 1)`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          approve_loan_application_api_loan_application_approve__application_id__post_Body,
      },
      {
        name: 'application_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_LoanApplicationResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/loan-application/back_to_appraisal/:application_id',
    alias:
      'back_to_appraisal_api_loan_application_back_to_appraisal__application_id__post',
    description: `Move loan application back to appraisal (set status to in_appraisal, pl_account.status to 3)`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'application_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_LoanApplicationResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/loan-application/create',
    alias: 'create_loan_application_api_loan_application_create_post',
    description: `Create a new loan application`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: LoanApplicationRequest,
      },
    ],
    response: APIResponse_LoanApplicationResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/loan-application/customer-summary/:customer_number',
    alias:
      'get_customer_loan_summary_api_loan_application_customer_summary__customer_number__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_number',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_CustomerLoanSummaryResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/loan-application/documents/:account_id',
    alias: 'get_loan_documents_api_loan_application_documents__account_id__get',
    description: `Get detailed information about a loan account`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'account_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_list_PlDocumentsResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/loan-application/documents/download/:pl_document_id',
    alias:
      'download_document_api_loan_application_documents_download__pl_document_id__get',
    description: `Download a document file for a loan account document record`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'pl_document_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/loan-application/documents/upload/:pl_document_id',
    alias:
      'upload_loan_document_api_loan_application_documents_upload__pl_document_id__post',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ file: z.instanceof(File) }).passthrough(),
      },
      {
        name: 'pl_document_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_PlDocumentsResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/loan-application/fd_advance_save',
    alias: 'fd_advance_save_api_loan_application_fd_advance_save_post',
    description: `Process FD Advance loan application with multiple FD accounts.

- **total_amount**: Total loan amount across all FD accounts
- **loan_product_id**: ID of the loan product
- **member_id**: Customer/Member ID
- **loans**: List of individual FD loan requests with pre-uploaded certificate paths`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: FdAdvanceRequest,
      },
    ],
    response: APIResponse_list_LoanApplicationResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/loan-application/fd_advance_upload_certificate',
    alias:
      'fd_advance_upload_certificate_api_loan_application_fd_advance_upload_certificate_post',
    description: `Upload FD certificate for an account. File is immediately saved.

- **file**: Certificate PDF file
- **account_ref_number**: Account reference number (e.g., &quot;001-F-00123&quot;)

Returns the saved file path for inclusion in the FD Advance request`,
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          Body_fd_advance_upload_certificate_api_loan_application_fd_advance_upload_certificate_post,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/loan-application/fd-products',
    alias: 'get_fd_advance_products_api_loan_application_fd_products_get',
    description: `Get all available FD advance loan products for the current branch`,
    requestFormat: 'json',
    response: APIResponse_list_LoanProductResponse__,
  },
  {
    method: 'get',
    path: '/api/loan-application/guaranters-list/:account_id/:value',
    alias:
      'get_loan_guaranters_list_api_loan_application_guaranters_list__account_id___value__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'account_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
      {
        name: 'value',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_list_GuarantersListResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/loan-application/guaranters/:account_id',
    alias:
      'get_loan_guaranters_api_loan_application_guaranters__account_id__get',
    description: `Get detailed information about a loan account`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'account_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_PlGuarantersResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/loan-application/guaranters/:account_id',
    alias: 'save_guarantors_api_loan_application_guaranters__account_id__post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SaveGuarantorsRequest,
      },
      {
        name: 'account_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_str_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/loan-application/list',
    alias: 'list_loan_applications_api_loan_application_list_get',
    description: `List loan applications with optional status and product filters`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'status',
        type: 'Query',
        schema: category,
      },
      {
        name: 'loan_product_id',
        type: 'Query',
        schema: passbook_type,
      },
    ],
    response: APIResponse_list_LoanApplicationResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/loan-application/open_date/:loan_application_id',
    alias:
      'update_loan_application_open_date_api_loan_application_open_date__loan_application_id__put',
    description: `Update the open date of a loan application`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: 'loan_application_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_LoanApplicationResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/loan-application/pl_account_info/:pl_account_info_id',
    alias:
      'update_loan_account_info_api_loan_application_pl_account_info__pl_account_info_id__put',
    description: `Update loan account info field`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PlAccountInfoResponse,
      },
      {
        name: 'pl_account_info_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_PlAccountInfoResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/loan-application/pl_account/:pl_account_id',
    alias:
      'get_pl_account_data_by_id_api_loan_application_pl_account__pl_account_id__get',
    description: `Get detailed information about a PL account`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'pl_account_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_PlAccountResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/loan-application/pl_account/:pl_account_id/mark-printed',
    alias:
      'mark_pl_account_printed_api_loan_application_pl_account__pl_account_id__mark_printed_post',
    description: `Set printed&#x3D;1 on the pl_account record to track first-time mandate printing.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'pl_account_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/loan-application/pl_account/:pl_account_id/print-status',
    alias:
      'get_pl_account_print_status_api_loan_application_pl_account__pl_account_id__print_status_get',
    description: `Check whether the pawning mandate for this account has been printed (printed&#x3D;1).`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'pl_account_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/loan-application/pl-account/:pl_account_id/comments',
    alias:
      'get_pl_account_comments_api_loan_application_pl_account__pl_account_id__comments_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'pl_account_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_PlAccountCommentResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/loan-application/pl-account/:pl_account_id/comments',
    alias:
      'add_pl_account_comment_api_loan_application_pl_account__pl_account_id__comments_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PlAccountCommentCreateRequest,
      },
      {
        name: 'pl_account_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PlAccountCommentResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/loan-application/products',
    alias: 'get_loan_products_api_loan_application_products_get',
    description: `Get all available loan products for the current branch`,
    requestFormat: 'json',
    response: APIResponse_list_LoanProductResponse__,
  },
  {
    method: 'post',
    path: '/api/loan-application/reject/:application_id',
    alias:
      'reject_loan_application_api_loan_application_reject__application_id__post',
    description: `Reject loan application (update statuses)`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'application_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_LoanApplicationResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/loan-application/repayment-schedule/:pl_account_id',
    alias:
      'get_repayment_schedule_api_loan_application_repayment_schedule__pl_account_id__get',
    description: `Get repayment schedule for a loan account`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'pl_account_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_list_PlLoanAmotResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/loan-application/send_to_approve/:application_id',
    alias:
      'send_to_approve_api_loan_application_send_to_approve__application_id__post',
    description: `Send loan application to approve (update statuses)`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'application_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_LoanApplicationResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/loan-past-due/branches',
    alias: 'get_branches_api_loan_past_due_branches_get',
    description: `Get all branches for filter dropdown`,
    requestFormat: 'json',
    response: APIResponse_list_,
  },
  {
    method: 'post',
    path: '/api/loan-past-due/data',
    alias: 'get_loan_past_due_data_api_loan_past_due_data_post',
    description: `Get loan past due data for dashboard widget with pie chart and detailed table`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: LoanPastDueFilterRequest,
      },
    ],
    response: APIResponse_LoanPastDueResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/menus/:menu_id',
    alias: 'get_menu_by_id_api_menus__menu_id__get',
    description: `Get a specific menu by ID`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'menu_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_SystemMenuResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/menus/all',
    alias: 'get_all_menus_api_menus_all_get',
    description: `Get all system menus`,
    requestFormat: 'json',
    response: APIResponse_list_SystemMenuResponse__,
  },
  {
    method: 'get',
    path: '/api/menus/by-component/:component_name',
    alias: 'get_menu_by_component_api_menus_by_component__component_name__get',
    description: `Get a menu by its component name`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'component_name',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_SystemMenuResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/menus/by-url/:menu_url',
    alias: 'get_menu_by_url_api_menus_by_url__menu_url__get',
    description: `Get a menu by its URL`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'menu_url',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_SystemMenuResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/menus/children/:parent_id',
    alias: 'get_children_menus_api_menus_children__parent_id__get',
    description: `Get child menus of a specific parent`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'parent_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_SystemMenuResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/menus/hierarchy',
    alias: 'get_menu_hierarchy_api_menus_hierarchy_get',
    description: `Get menus with full hierarchical structure (parent-child relationships)`,
    requestFormat: 'json',
    response: APIResponse_list_SystemMenuWithChildrenResponse__,
  },
  {
    method: 'get',
    path: '/api/menus/parent',
    alias: 'get_parent_menus_api_menus_parent_get',
    description: `Get only parent menus (top-level navigation items)`,
    requestFormat: 'json',
    response: APIResponse_list_SystemMenuResponse__,
  },
  {
    method: 'post',
    path: '/api/mobile/v1/auth/auth/login',
    alias: 'mobile_login_api_mobile_v1_auth_auth_login_post',
    description: `Authenticate a field officer via the mobile app. Returns Bearer tokens in the response body.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: MobileLoginRequest,
      },
    ],
    response: APIResponse_MobileLoginResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/mobile/v1/external-resource/external-resource/',
    alias:
      'get_external_resources_api_mobile_v1_external_resource_external_resource__get',
    requestFormat: 'json',
    response: APIResponse_List_ItExternalResource__,
  },
  {
    method: 'get',
    path: '/api/mobile/v1/loan/loan/applications',
    alias:
      'list_customer_loan_applications_api_mobile_v1_loan_loan_applications_get',
    description: `Get all loan applications for a customer (OAuth authenticated)`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_id',
        type: 'Query',
        schema: z.number().int().gt(0),
      },
      {
        name: 'branch_id',
        type: 'Query',
        schema: z.number().int().gt(0),
      },
      {
        name: 'application_status',
        type: 'Query',
        schema: category,
      },
    ],
    response: APIResponse_list_MobileLoanApplicationStatusResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/mobile/v1/loan/loan/apply',
    alias: 'create_loan_application_api_mobile_v1_loan_loan_apply_post',
    description: `Create a new loan application (OAuth authenticated)`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: MobileLoanApplicationRequest,
      },
    ],
    response: APIResponse_MobileLoanApplicationResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/mobile/v1/loan/loan/customer-info',
    alias: 'get_customer_loan_info_api_mobile_v1_loan_loan_customer_info_post',
    description: `Get customer information with existing loans (OAuth authenticated)`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: MobileCustomerLoanInfoRequest,
      },
    ],
    response: APIResponse_MobileCustomerLoanInfoResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/mobile/v1/member/member/add',
    alias: 'membership_request_api_mobile_v1_member_member_add_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: MemberCreateRequest,
      },
    ],
    response: APIResponse_MemberCreateResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/mobile/v1/member/member/location/add',
    alias: 'add_member_location_api_mobile_v1_member_member_location_add_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: MemberLocationCreateRequest,
      },
    ],
    response: APIResponse_MemberLocationCreateResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/mobile/v1/print/',
    alias: 'print_receipt_api_mobile_v1_print__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'token',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/mobile/v1/print/sample_token',
    alias: 'print_receipt_api_mobile_v1_print_sample_token_get',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/mobile/v1/task/task/customer-tasks',
    alias: 'get_customer_tasks_api_mobile_v1_task_task_customer_tasks_get',
    description: `Get list of tasks for a specific customer - OAuth authenticated, FIELD_OFFICER only`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_id',
        type: 'Query',
        schema: z.number().int().gt(0),
      },
      {
        name: 'task_status',
        type: 'Query',
        schema: category,
      },
      {
        name: 'task_type',
        type: 'Query',
        schema: category,
      },
    ],
    response: APIResponse_CustomerTasksResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/mobile/v1/task/task/tso-tasks',
    alias: 'get_tso_tasks_api_mobile_v1_task_task_tso_tasks_get',
    description: `Get list of tasks assigned to a TSO (Field Officer) - OAuth authenticated, FIELD_OFFICER only`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'tso_id',
        type: 'Query',
        schema: z.number().int().gt(0),
      },
      {
        name: 'task_status',
        type: 'Query',
        schema: category,
      },
      {
        name: 'task_type',
        type: 'Query',
        schema: category,
      },
    ],
    response: APIResponse_TsoTasksResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/mobile/v1/tso/:tso_id/customer-count',
    alias:
      'get_coustomers_count_by_tso_api_mobile_v1_tso__tso_id__customer_count_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'tso_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_int_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/mobile/v1/tso/:tso_id/dues-and-collections',
    alias:
      'get_dues_and_collections_summary_by_tso_api_mobile_v1_tso__tso_id__dues_and_collections_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'tso_id',
        type: 'Path',
        schema: z.number().int().gt(0),
      },
      {
        name: 'month',
        type: 'Query',
        schema: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
      },
    ],
    response: APIResponse_DueSummaryDetails_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/mobile/v1/tso/customers',
    alias: 'get_tso_customers_api_mobile_v1_tso_customers_get',
    description: `Get list of customers assigned to a TSO (Field Officer) - OAuth authenticated`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'tso_id',
        type: 'Query',
        schema: z.number().int().gt(0),
      },
      {
        name: 'branch_id',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'status',
        type: 'Query',
        schema: passbook_type,
      },
    ],
    response: APIResponse_TsoCustomersResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/mobile/v1/tso/dues',
    alias: 'get_dues_by_tso_api_mobile_v1_tso_dues_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'tso_id',
        type: 'Query',
        schema: z.number().int().gt(0),
      },
      {
        name: 'month',
        type: 'Query',
        schema: category,
      },
    ],
    response: APIResponse_DueResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/mobile/v1/tso/tasks',
    alias: 'get_tasks_by_tso_and_customer_api_mobile_v1_tso_tasks_get',
    description: `Get tasks filtered by both TSO and customer from it_tso_task table`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'tso_id',
        type: 'Query',
        schema: z.number().int().gt(0),
      },
      {
        name: 'customer_id',
        type: 'Query',
        schema: z.number().int().gt(0),
      },
    ],
    response: APIResponse_TsoCustomerTasksResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pawning/application',
    alias: 'create_pawning_application_api_pawning_application_post',
    description: `Create a new pawning loan application with items and linked account.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PawningApplicationRequest,
      },
    ],
    response: APIResponse_PawningApplicationResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/payment/cheque-cash-book-options',
    alias: 'get_cash_book_options_api_payment_cheque_cash_book_options_get',
    description: `Populates the cheque cash book options if cheque is ticked.
If not cash book will be selected automatically in the login.`,
    requestFormat: 'json',
    response: APIResponse_list_CashBookOptionResponse__,
  },
  {
    method: 'get',
    path: '/api/payment/gl-account/:gl_account_id/information',
    alias:
      'get_gl_acc_info_api_payment_gl_account__gl_account_id__information_get',
    description: `Get GL Account information when clicked on info icon.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'gl_account_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_GlInfoResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/payment/gl-accounts/search',
    alias: 'search_gl_accounts_api_payment_gl_accounts_search_get',
    description: `Search for GL accounts based on Section and Branch.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'filtered_branch_id',
        type: 'Query',
        schema: z.number().int(),
      },
      {
        name: 'section_id',
        type: 'Query',
        schema: z.number().int(),
      },
      {
        name: 'is_current_branch',
        type: 'Query',
        schema: z.boolean(),
      },
      {
        name: 'search_term',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'type',
        type: 'Query',
        schema: type,
      },
    ],
    response: APIResponse_list_GLAccountOptionResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/payment/last-transaction',
    alias: 'get_last_transaction_api_payment_last_transaction_get',
    requestFormat: 'json',
    response: APIResponse_GlTransactionHeaderResponse_,
  },
  {
    method: 'get',
    path: '/api/payment/other-branches',
    alias: 'get_branches_api_payment_other_branches_get',
    requestFormat: 'json',
    response: APIResponse_list_OtherBranchResponse__,
  },
  {
    method: 'post',
    path: '/api/payment/pay',
    alias: 'finalize_transaction_api_payment_pay_post',
    description: `The Pay functionality - Enqueues the payment for background processing`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ transaction_number: z.string() }).passthrough(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/payment/pending-approved-transaction/:transaction_number',
    alias:
      'get_temp_transaction_details_api_payment_pending_approved_transaction__transaction_number__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'transaction_number',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_List_PendingApprovedTransactionResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/payment/pending-approved-transactions',
    alias:
      'get_pending_approved_transactions_api_payment_pending_approved_transactions_get',
    description: `Populates Pending/Approved Transactions Table.`,
    requestFormat: 'json',
    response: APIResponse_List_PendingApprovedTransactionsResponse__,
  },
  {
    method: 'post',
    path: '/api/payment/save',
    alias: 'save_temp_transaction_api_payment_save_post',
    description: `The Save functionality`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.array(SaveTempTransactionRequest),
      },
    ],
    response: APIResponse_TransactionNumberResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/payment/sections',
    alias: 'get_sections_api_payment_sections_get',
    requestFormat: 'json',
    response: APIResponse_list_SectionResponse__,
  },
  {
    method: 'get',
    path: '/api/pl_account_type/all',
    alias: 'get_all_pl_account_types_api_pl_account_type_all_get',
    description: `Get all active PL account types filtered by category_id.

Query Parameters:
    category_id (int): PL account category id to filter account types.

Returns:
    APIResponse: A response containing a list of PL account types.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'category_id',
        type: 'Query',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_PlAccountType__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pl_account_type/categories',
    alias: 'get_all_categories_api_pl_account_type_categories_get',
    description: `Get all active PL account type categories.

Returns:
    APIResponse: A response containing a list of PL account type categories.`,
    requestFormat: 'json',
    response: APIResponse_list_PlAccountTypeCategory__,
  },
  {
    method: 'get',
    path: '/api/pl_equipment/account-types',
    alias: 'get_pl_account_types_api_pl_equipment_account_types_get',
    description: `Get all active PL account types (used for equipment PL account type dropdown).

Returns:
    APIResponse: A response containing a list of PL account types.`,
    requestFormat: 'json',
    response: APIResponse_list_PlAccountType__,
  },
  {
    method: 'get',
    path: '/api/pl_equipment/all',
    alias: 'get_all_pl_equipment_api_pl_equipment_all_get',
    description: `Get all PL Equipment records.

Returns:
    APIResponse: A response containing a list of all PL Equipment records.`,
    requestFormat: 'json',
    response: APIResponse_list_PlEquipment__,
  },
  {
    method: 'post',
    path: '/api/pl_equipment/process',
    alias: 'process_equipment_request_api_pl_equipment_process_post',
    description: `Enqueue equipment transaction for background processing.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z
          .object({
            cash_book_id: z.number().int(),
            transaction_type: z.number().int(),
            branch_id: z.number().int(),
            cheque_num: z.union([z.string(), z.null()]).optional(),
            bank_name: z.union([z.number(), z.null()]).optional(),
            note: z.union([z.string(), z.null()]).optional(),
            equipments: z.array(EquipmentItem),
          })
          .passthrough(),
      },
    ],
    response: APIResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pl_pawning_assessed_values/',
    alias: 'create_pawning_assessed_value_api_pl_pawning_assessed_values__post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PlPawningAssessedValueCreate,
      },
    ],
    response: APIResponse_PlPawningAssessedValue_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pl_pawning_assessed_values/:item_id',
    alias:
      'get_pawning_assessed_value_api_pl_pawning_assessed_values__item_id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'item_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PlPawningAssessedValue_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/pl_pawning_assessed_values/:item_id',
    alias:
      'update_pawning_assessed_value_api_pl_pawning_assessed_values__item_id__put',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PlPawningAssessedValueUpdate,
      },
      {
        name: 'item_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PlPawningAssessedValue_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pl_pawning_assessed_values/all',
    alias:
      'list_pawning_assessed_values_api_pl_pawning_assessed_values_all_get',
    requestFormat: 'json',
    response: APIResponse_list_PlPawningAssessedValue__,
  },
  {
    method: 'post',
    path: '/api/pl_pawning_item_types/',
    alias: 'create_pawning_item_type_api_pl_pawning_item_types__post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PlPawningItemTypeCreate,
      },
    ],
    response: APIResponse_PlPawningItemType_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pl_pawning_item_types/:item_id',
    alias: 'get_pawning_item_type_api_pl_pawning_item_types__item_id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'item_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PlPawningItemType_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/pl_pawning_item_types/:item_id',
    alias: 'update_pawning_item_type_api_pl_pawning_item_types__item_id__put',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PlPawningItemTypeUpdate,
      },
      {
        name: 'item_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PlPawningItemType_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pl_pawning_item_types/all',
    alias: 'list_pawning_item_types_api_pl_pawning_item_types_all_get',
    requestFormat: 'json',
    response: APIResponse_list_PlPawningItemType__,
  },
  {
    method: 'post',
    path: '/api/pl_product',
    alias: 'create_loan_product_api_pl_product_post',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: Body_create_loan_product_api_pl_product_post,
      },
    ],
    response: APIResponse_PlAccountType_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pl_product/:id',
    alias: 'get_loan_product_by_id_api_pl_product__id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_LoanProductRead_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pl_product/:id',
    alias: 'update_loan_product_api_pl_product__id__post',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: Body_update_loan_product_api_pl_product__id__post,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_LoanProductRead_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pl_product/:id/additional-info',
    alias:
      'add_product_additional_info_api_pl_product__id__additional_info_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.array(z.string()),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pl_product/:id/amortization-file',
    alias:
      'upload_amortization_file_api_pl_product__id__amortization_file_post',
    description: `Upload a single amortization XLS/XLSX for a product and process it immediately.

This allows the frontend to upload amortization files separately without submitting the whole product form.`,
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          Body_upload_amortization_file_api_pl_product__id__amortization_file_post,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pl_product/:id/amortization-templates',
    alias:
      'get_amortization_templates_api_pl_product__id__amortization_templates_get',
    description: `Return available amortization templates grouped by issue amount and period for a product`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pl_product/:id/documents',
    alias: 'add_product_documents_api_pl_product__id__documents_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.array(z.string()),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pl_product/:id/policy-doc',
    alias: 'add_product_policy_doc_api_pl_product__id__policy_doc_post',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: Body_add_product_policy_doc_api_pl_product__id__policy_doc_post,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pl_product/ai-policy',
    alias: 'add_ai_policy_api_pl_product_ai_policy_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({}).partial().passthrough(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pl_product/ai-policy/:product_type_id',
    alias: 'get_ai_policy_api_pl_product_ai_policy__product_type_id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'product_type_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pl_product/amortization-template/download',
    alias:
      'download_amortization_template_api_pl_product_amortization_template_download_get',
    description: `Return a sample amortization XLSX template for users to download.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'period',
        type: 'Query',
        schema: z.number().int().optional().default(12),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pl_product/documents/:document_id',
    alias:
      'delete_product_document_api_pl_product_documents__document_id__post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'document_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pl_product/generate-ai-policy',
    alias: 'generate_ai_policy_api_pl_product_generate_ai_policy_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PlProductAIPolicyRequest,
      },
    ],
    response: APIResponse_PlProductAIPolicyResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pl_product/info-fields/:info_id',
    alias:
      'delete_product_info_field_api_pl_product_info_fields__info_id__post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'info_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pl_product/loan/next-ref-number',
    alias: 'get_next_ref_number_api_pl_product_loan_next_ref_number_get',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/pl_product/lookups',
    alias: 'get_product_lookups_api_pl_product_lookups_get',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/pl_product/paginated',
    alias: 'get_all_loan_products_paginated_api_pl_product_paginated_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.number().int().optional().default(1),
      },
      {
        name: 'page_size',
        type: 'Query',
        schema: z.number().int().optional().default(10),
      },
      {
        name: 'search_term',
        type: 'Query',
        schema: category,
      },
      {
        name: 'category_id',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'operating_type_id',
        type: 'Query',
        schema: passbook_type,
      },
    ],
    response: APIResponse_LoanProductPaginated_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/pl_product/policy-doc/:doc_id',
    alias: 'delete_policy_doc_api_pl_product_policy_doc__doc_id__delete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'doc_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pl_product/sav/next-ref-number',
    alias: 'get_next_saving_ref_number_api_pl_product_sav_next_ref_number_get',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'post',
    path: '/api/pl_quotation/create',
    alias: 'create_pl_quotation_api_pl_quotation_create_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PlQuotationRequest,
      },
    ],
    response: APIResponse_PlQuotationResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pl_quotation/create-purchase-order',
    alias: 'create_purchase_order_api_pl_quotation_create_purchase_order_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PlQuotationRequest,
      },
    ],
    response: APIResponse_PlQuotationResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pl_quotation/list',
    alias: 'list_quotations_api_pl_quotation_list_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_id',
        type: 'Query',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_List_PlQuotationResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pl_quotation/purchase-orders',
    alias: 'list_purchase_orders_api_pl_quotation_purchase_orders_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_id',
        type: 'Query',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_List_PlQuotationResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pl-print-policy/',
    alias: 'create_or_update_policy_api_pl_print_policy__post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PlPrintPolicyCreate,
      },
    ],
    response: PlPrintPolicyRead,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pl-print-policy/',
    alias: 'read_policies_api_pl_print_policy__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'skip',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().optional().default(100),
      },
    ],
    response: z.array(PlPrintPolicyRead),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pl-print-policy/config',
    alias: 'get_policy_config_api_pl_print_policy_config_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'passbook_type',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'category',
        type: 'Query',
        schema: category__2,
      },
      {
        name: 'passbook_policy_id',
        type: 'Query',
        schema: passbook_type,
      },
    ],
    response: z.union([PlPrintPolicyRead, z.null()]),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pl-print-policy/mark-as-printed',
    alias:
      'mark_transactions_as_printed_api_pl_print_policy_mark_as_printed_post',
    description: `Mark a list of transactions as printed.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.array(z.number().int()),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/process/backfill',
    alias: 'process_backfill_api_process_backfill_post',
    description: `Processes the backfill operation.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ProcessBackfillRequest,
      },
    ],
    response: APIResponse_TransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/process/backfill/check-tables',
    alias: 'check_tables_to_clear_api_process_backfill_check_tables_post',
    description: `Checks which tables will be cleared during backfill process.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ProcessBackfillRequest,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/process/backfill/date-range',
    alias: 'get_fiscal_date_range_api_process_backfill_date_range_get',
    description: `Gets the fiscal date range from it_fiscal_date table.`,
    requestFormat: 'json',
    response: APIResponse_dict_,
  },
  {
    method: 'get',
    path: '/api/process/backfill/validate',
    alias: 'validate_backfill_api_process_backfill_validate_get',
    description: `Validates if backfill process can be started.`,
    requestFormat: 'json',
    response: APIResponse_dict_,
  },
  {
    method: 'post',
    path: '/api/process/backfill/validate-products',
    alias: 'validate_products_api_process_backfill_validate_products_post',
    requestFormat: 'json',
    response: APIResponse_ProductValidationCheckResponse_,
  },
  {
    method: 'get',
    path: '/api/process/backfill/validation-data',
    alias: 'get_validation_data_api_process_backfill_validation_data_get',
    description: `Retrieves the validation data for the backfill operation.`,
    requestFormat: 'json',
    response: APIResponse_ProductValidationResponse_,
  },
  {
    method: 'post',
    path: '/api/process/day-end-open',
    alias: 'process_day_end_open_api_process_day_end_open_post',
    description: `Processes the day-end-open operation.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ branch_id: z.number().int() }).passthrough(),
      },
    ],
    response: APIResponse_TransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/process/month-close',
    alias: 'process_month_close_api_process_month_close_post',
    description: `Processes the month-close operation.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ branch_id: z.number().int() }).passthrough(),
      },
    ],
    response: APIResponse_TransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/process/month-end',
    alias: 'process_month_end_api_process_month_end_post',
    description: `Processes the month-end operation.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ branch_id: z.number().int() }).passthrough(),
      },
    ],
    response: APIResponse_TransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/process/year-end',
    alias: 'process_year_end_api_process_year_end_post',
    description: `Processes the year-end operation.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ branch_id: z.number().int() }).passthrough(),
      },
    ],
    response: APIResponse_TransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/profile/me',
    alias: 'get_profile_api_profile_me_get',
    requestFormat: 'json',
    response: APIResponse_AuthResponse_,
  },
  {
    method: 'post',
    path: '/api/purchasing/process',
    alias: 'process_purchase_api_purchasing_process_post',
    description: `Enqueue purchase transaction for background processing.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z
          .object({
            customerId: z.number().int(),
            branchId: z.number().int(),
            plAccountTypeId: z.number().int(),
            quotationId: z.union([z.number(), z.null()]).optional(),
            invoiceDate: z.union([z.string(), z.null()]).optional(),
            creditPeriod: z.union([z.string(), z.null()]).optional(),
            items: z.array(InvoiceItem),
            totalAmount: z.number().gt(0),
            note: z.union([z.string(), z.null()]).optional(),
          })
          .passthrough(),
      },
    ],
    response: APIResponse_TransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/report/:report_id',
    alias: 'delete_report_api_report__report_id__delete',
    description: `Delete a report template completely.

Steps:
1. Delete from it_user_reports (user access records)
2. Delete files from Jasper Report Service (all languages)
3. Delete from it_report_template (main record)

Requires: REPORT:MANAGE permission

Returns:
    Success message or error details`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'report_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/report/:report_id/languages',
    alias: 'get_report_languages_api_report__report_id__languages_get',
    description: `Get list of languages that have been uploaded for a report template.

Returns the uploaded_languages array from the report template.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'report_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_LanguageFileStatusResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/report/create',
    alias: 'create_report_api_report_create_post',
    description: `Create a new report template (step 1 of 2-step process).

This creates the database record and returns a UUID that will be used
for JRXML file naming. The UUID should then be used in the subsequent
JRXML file upload request.

Returns:
    - report_uuid: UUID to use for JRXML file naming
    - report_id: Database ID of created report`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateReportRequest,
      },
    ],
    response: APIResponse_CreateReportResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/report/download_report',
    alias: 'download_report_api_report_download_report_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ReporDownloadRequest,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/report/list_reports',
    alias: 'list_reports_api_report_list_reports_get',
    requestFormat: 'json',
    response: APIResponse_list_ItReportTemplateResponse__,
  },
  {
    method: 'get',
    path: '/api/report/list_reports/current_user',
    alias: 'get_report_list_for_user_api_report_list_reports_current_user_get',
    description: `Get all report IDs for a specific user where status&#x3D;1`,
    requestFormat: 'json',
    response: APIResponse_list_ItReportTemplateResponse__,
  },
  {
    method: 'get',
    path: '/api/report/parameters_types',
    alias: 'get_report_parameters_type_api_report_parameters_types_get',
    requestFormat: 'json',
    response: APIResponse_list_ItReportParamTypeResponse__,
  },
  {
    method: 'get',
    path: '/api/report/tempalate/:template_id',
    alias: 'get_report_template_by_id_api_report_tempalate__template_id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'template_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_ItReportTemplateResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/report/template_categories',
    alias: 'get_report_categories_api_report_template_categories_get',
    requestFormat: 'json',
    response: APIResponse_list_ItReportCategoryResponse__,
  },
  {
    method: 'post',
    path: '/api/report/upload_jrxml/:report_uuid',
    alias: 'upload_jrxml_api_report_upload_jrxml__report_uuid__post',
    description: `Upload JRXML file for a report (step 2 of 2-step workflow).

Must be called AFTER POST /upload_report which returns the report_uuid.
Uses the report_uuid to upload JRXML file with UUID-based naming.

Workflow:
1. First: POST /upload_report to create report and get report_uuid
2. Then: POST /upload_jrxml/{report_uuid} to upload JRXML file(s)
3. Can upload multiple language files (en, si, ta) by calling this endpoint multiple times

File will be named: {report_uuid}_{language_code}.jrxml
Example: &quot;550e8400-e29b-41d4-a716-446655440000_en.jrxml&quot;

Args:
    report_uuid: UUID from upload_report response (must exist in database)
    language_code: Language code (en, si, ta) - default &#x27;en&#x27;
    file: JRXML file to upload`,
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ file: z.instanceof(File) }).passthrough(),
      },
      {
        name: 'report_uuid',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'language_code',
        type: 'Query',
        schema: z.string().optional().default('en'),
      },
    ],
    response: APIResponse_UploadJrxmlResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/report/upload_report',
    alias: 'upload_report_api_report_upload_report_post',
    description: `Upload or create a report template (step 1 of 2-step workflow).

This endpoint creates a new report in the database and generates a UUID,
or updates an existing report. The response includes the report_uuid which
must be used in the next step to upload JRXML files.

Workflow:
1. Call this endpoint with report details (name, sql_query, parameters)
2. Receive report_uuid in response
3. Use report_uuid to call POST /report/upload_jrxml/{report_uuid} to upload JRXML files

Returns:
    - report_uuid: Use this UUID to upload JRXML files
    - report_id: Database ID of the report`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UploadReportRequest,
      },
    ],
    response: APIResponse_ItReportTemplateResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/savings/accounts',
    alias: 'get_savings_accounts_api_savings_accounts_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_id',
        type: 'Query',
        schema: z.number().int(),
      },
      {
        name: 'category_id',
        type: 'Query',
        schema: z.number().int().optional(),
      },
    ],
    response: APIResponse_list_SavingsAccount__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/savings/accounts',
    alias: 'create_savings_account_api_savings_accounts_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateSavingsAccountRequest,
      },
    ],
    response: APIResponse_CreateSavingsAccountResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/savings/create-products',
    alias: 'create_savings_product_api_savings_create_products_post',
    requestFormat: 'form-url',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ data: z.string() }).passthrough(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/savings/interest-update-accounts/:nic_or_number',
    alias:
      'get_interest_update_accounts_api_savings_interest_update_accounts__nic_or_number__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'nic_or_number',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'category_id',
        type: 'Query',
        schema: z.number().int().optional(),
      },
    ],
    response: APIResponse_InterestUpdateAccountsResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/savings/joint-accounts',
    alias: 'get_joint_accounts_api_savings_joint_accounts_get',
    requestFormat: 'json',
    response: APIResponse_JointAccountsResponse_,
  },
  {
    method: 'get',
    path: '/api/savings/last-account-number/:product_id',
    alias:
      'get_last_account_number_api_savings_last_account_number__product_id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'product_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_LastAccountNumberResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/savings/products',
    alias: 'get_savings_products_api_savings_products_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'category_id',
        type: 'Query',
        schema: z.number().int().optional(),
      },
    ],
    response: APIResponse_list_SavingsProduct__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/savings/products/:id',
    alias: 'get_savings_product_by_id_api_savings_products__id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/savings/products/:id',
    alias: 'update_savings_product_api_savings_products__id__post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/savings/products/paginated',
    alias: 'get_savings_products_paginated_api_savings_products_paginated_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.number().int().optional().default(1),
      },
      {
        name: 'page_size',
        type: 'Query',
        schema: z.number().int().optional().default(10),
      },
      {
        name: 'search_term',
        type: 'Query',
        schema: category,
      },
      {
        name: 'category_id',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'operating_type_id',
        type: 'Query',
        schema: passbook_type,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/savings/saving-prod-lookups',
    alias: 'get_saving_product_lookups_api_savings_saving_prod_lookups_get',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/system_permissions/all',
    alias: 'get_all_system_permissions_api_system_permissions_all_get',
    requestFormat: 'json',
    response: APIResponse_list_SystemPermissionResponse__,
  },
  {
    method: 'get',
    path: '/api/system/health',
    alias: 'get_system_health_api_system_health_get',
    description: `Get system health status including cache and report service status.

No authentication required - this endpoint is used for health checks.

Returns:
    - cache_service: bool - Redis/Cache service health
    - report_service: bool - Jasper Report Service health
    - timestamp: str - When the check was performed
    - status: str - Overall status (&#x27;healthy&#x27;, &#x27;degraded&#x27;, &#x27;unhealthy&#x27;)`,
    requestFormat: 'json',
    response: APIResponse_SystemHealthResponse_,
  },
  {
    method: 'get',
    path: '/api/test/read_stream/:stream_name/:group_name',
    alias: 'dequeue_api_test_read_stream__stream_name___group_name__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'stream_name',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'group_name',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_list_QueueResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/approval-dashboard',
    alias: 'approval_dashboard_api_transaction_approval_dashboard_get',
    description: `Retrieve the approval dashboard with pending transactions.`,
    requestFormat: 'json',
    response: APIResponse_list_ApprovalDashboardResponse__,
  },
  {
    method: 'post',
    path: '/api/transaction/approval-dashboard-status-update',
    alias:
      'approval_dashboard_status_update_api_transaction_approval_dashboard_status_update_post',
    description: `Update the approval dashboard with pending transactions.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ApprovalDashboardUpdateRequest,
      },
    ],
    response: APIResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/transaction/auto_recovery/confirm',
    alias:
      'confirm_auto_recovery_batch_api_transaction_auto_recovery_confirm_post',
    description: `Confirm and process the auto recovery batch.
This will create receipt transactions for all recoverable loans.`,
    requestFormat: 'json',
    response: APIResponse,
  },
  {
    method: 'get',
    path: '/api/transaction/auto_recovery/recoverable',
    alias:
      'get_auto_recovery_data_api_transaction_auto_recovery_recoverable_get',
    description: `Get all customers with auto-recovery enabled loans grouped by savings account.
Shows recoverable and less savings balance scenarios.
Supports pagination via limit and offset parameters.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).lte(500).optional().default(50),
      },
      {
        name: 'offset',
        type: 'Query',
        schema: z.number().int().gte(0).optional().default(0),
      },
    ],
    response: APIResponse_list_AutoRecoveryRow__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/transaction/auto_recovery/update_batch',
    alias:
      'update_auto_recovery_batch_api_transaction_auto_recovery_update_batch_post',
    description: `Update the auto recovery batch with modified payment amounts.
This allows adjusting recovery amounts before confirming.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AutoRecoveryBatchRequest,
      },
    ],
    response: APIResponse_TransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/transaction/cancel',
    alias: 'cancel_transaction_api_transaction_cancel_post',
    description: `Cancel a transaction by transaction number.
Adds cancellation to queue for async processing.
Creates a new cancellation transaction with &#x27;C&#x27; prefix.
Only for approval users. Cannot cancel journal transactions.

- **transaction_number**: The transaction number to cancel
- **ref1**: Optional file URL for supporting document`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CancelTransactionRequest,
      },
    ],
    response: APIResponse_TransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/cancellable',
    alias: 'get_cancellable_transactions_api_transaction_cancellable_get',
    description: `Retrieve cancellable transactions based on filters.

- **only_today**: Filter transactions by today&#x27;s working date
- **include_receipts**: Include receipt type transactions
- **include_withdrawals**: Include withdrawal type transactions
- **include_payments**: Include payment type transactions`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'only_today',
        type: 'Query',
        schema: z.boolean().optional().default(true),
      },
      {
        name: 'include_receipts',
        type: 'Query',
        schema: z.boolean().optional().default(true),
      },
      {
        name: 'include_withdrawals',
        type: 'Query',
        schema: z.boolean().optional().default(true),
      },
      {
        name: 'include_payments',
        type: 'Query',
        schema: z.boolean().optional().default(true),
      },
    ],
    response: APIResponse_list_CancellableTransactionResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/transaction/equipment',
    alias: 'equipment_transaction_api_transaction_equipment_post',
    description: `Queue an equipment transaction for asynchronous processing.

The request should include a list of equipments (items) describing
purchase/sale entries. This endpoint enqueues the request to the
equipment Redis stream for background processing.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z
          .object({
            cash_book_id: z.number().int(),
            transaction_type: z.number().int(),
            branch_id: z.number().int(),
            cheque_num: z.union([z.string(), z.null()]).optional(),
            bank_name: z.union([z.number(), z.null()]).optional(),
            note: z.union([z.string(), z.null()]).optional(),
            equipments: z.array(EquipmentItem),
          })
          .passthrough(),
      },
    ],
    response: APIResponse_TransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/gl_month_balances',
    alias: 'get_gl_month_balances_api_transaction_gl_month_balances_get',
    description: `Retrieve the GL month balances.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'branch_id',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'from_month_id',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'to_month_id',
        type: 'Query',
        schema: passbook_type,
      },
    ],
    response: APIResponse_list_GLNotePolicyTResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/gl_transaction_header/:transaction_header_id',
    alias:
      'get_gl_transaction_header_by_id_api_transaction_gl_transaction_header__transaction_header_id__get',
    description: `Retrieve GL transaction header by ID.

- **transaction_header_id**: ID of the transaction header`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'transaction_header_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_GlTransactionHeaderResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/gl_transactions',
    alias: 'list_gl_transactions_api_transaction_gl_transactions_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'gl_coa_id',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'transaction_type_id',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'branch_id',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'date_from',
        type: 'Query',
        schema: category,
      },
      {
        name: 'date_to',
        type: 'Query',
        schema: category,
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).lte(100).optional().default(20),
      },
      {
        name: 'page',
        type: 'Query',
        schema: z.number().int().gte(1).optional().default(1),
      },
    ],
    response: APIResponse_GlTransactionListResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/transaction/investment',
    alias: 'investment_transaction_api_transaction_investment_post',
    description: `Queue an investment transaction for asynchronous processing.

The request should include a list of investment items describing deposit/withdrawal
or account creation entries. This endpoint enqueues the request to the investment
Redis stream for background processing.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z
          .object({
            cash_book_id: z.number().int(),
            transaction_type: z.number().int(),
            branch_id: z.number().int(),
            cheque_num: z.union([z.string(), z.null()]).optional(),
            bank_name: z.union([z.number(), z.null()]).optional(),
            note: z.union([z.string(), z.null()]).optional(),
            investments: z.array(InvestmentItem),
          })
          .passthrough(),
      },
    ],
    response: APIResponse_TransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/transaction/journal',
    alias: 'journal_transaction_api_transaction_journal_post',
    description: `Process a journal transaction with the provided details.

Journal entries allow posting to both GL and PL accounts with matching debits and credits.
Supports multiple customers - each PL account can belong to a different customer.

- **accounts**: List of account entries (minimum 2) with debit/credit amounts
  - For PL accounts: customer_id is required
  - For GL accounts: customer_id is optional
- **note**: Optional description for the journal entry
- **cheque_num**: Optional cheque number if applicable

**Validation Rules:**
- Total debits must equal total credits
- Each account can have either debit OR credit, not both
- PL Accounts (Loan): Only credit allowed (receipt process)
- PL Accounts (Saving): Debit (withdraw) or Credit (receipt) allowed
- GL Accounts: Debit (payment) or Credit (receipt) allowed
- PL accounts must include customer_id`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: JournalRequest,
      },
    ],
    response: APIResponse_TransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/journal/draft',
    alias: 'get_journal_draft_api_transaction_journal_draft_get',
    description: `Retrieve cached journal draft accounts for the current user.

Returns empty list if no draft exists or cache has expired.`,
    requestFormat: 'json',
    response:
      APIResponse_list_Annotated_Union_JournalAccountStandard__JournalAccountEquipment___FieldInfo_annotation_NoneType__required_True__discriminator__account_type_____,
  },
  {
    method: 'post',
    path: '/api/transaction/journal/draft',
    alias: 'save_journal_draft_api_transaction_journal_draft_post',
    description: `Save journal draft accounts to cache for the current user.

This allows the user to persist their work-in-progress journal entries
across page refreshes without submitting the transaction.

- **accounts**: List of account entries to cache

Cache expires after 24 hours.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: save_journal_draft_api_transaction_journal_draft_post_Body,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/transaction/journal/draft',
    alias: 'clear_journal_draft_api_transaction_journal_draft_delete',
    description: `Clear cached journal draft for the current user.

This is typically called after successful journal submission to remove
the work-in-progress cache.`,
    requestFormat: 'json',
    response: APIResponse_dict_,
  },
  {
    method: 'post',
    path: '/api/transaction/journal/prev_month',
    alias: 'get_journal_prev_month_api_transaction_journal_prev_month_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ branch_id: z.number().int() }).passthrough(),
      },
    ],
    response: APIResponse_JournalPrevMonthResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/last_transaction_headers_list/:transaction_type',
    alias:
      'get_last_transaction_headers_list_of_user_api_transaction_last_transaction_headers_list__transaction_type__get',
    description: `Retrieve the last transaction headers list for a current user.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'transaction_type',
        type: 'Path',
        schema: z.number().int(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: passbook_type,
      },
      {
        name: 'pl_only',
        type: 'Query',
        schema: paymentOnly,
      },
    ],
    response: APIResponse_list_GlTransactionHeaderResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/last_transaction/:transaction_type',
    alias:
      'get_last_transaction_api_transaction_last_transaction__transaction_type__get',
    description: `Retrieve the last transaction for a current user.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'transaction_type',
        type: 'Path',
        schema: z.number().int(),
      },
      {
        name: 'paymentOnly',
        type: 'Query',
        schema: paymentOnly,
      },
      {
        name: 'isJournal',
        type: 'Query',
        schema: paymentOnly,
      },
    ],
    response: APIResponse_GlTransactionHeader_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/last_transaction/account/:pl_account_id',
    alias:
      'get_last_transaction_of_account_api_transaction_last_transaction_account__pl_account_id__get',
    description: `Retrieve the last transaction for a specific PL account.

- **pl_account_id**: ID of the PL account`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'pl_account_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PlTransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/last_transactions_list/account/:pl_account_id',
    alias:
      'get_last_transactions_list_of_account_api_transaction_last_transactions_list_account__pl_account_id__get',
    description: `Retrieve the last transaction for a specific PL account.

- **pl_account_id**: ID of the PL account`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'pl_account_id',
        type: 'Path',
        schema: z.number().int(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: limit,
      },
    ],
    response: APIResponse_list_PlTransactionResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/loan_amot/:pl_account_id',
    alias:
      'get_loan_amotization_schedule_api_transaction_loan_amot__pl_account_id__get',
    description: `Retrieve the loan amortization schedule for a specific PL account.

- **pl_account_id**: ID of the PL account`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'pl_account_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_PlLoanAmotResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/transaction/loan_disbursement',
    alias:
      'loan_disbursement_transaction_api_transaction_loan_disbursement_post',
    description: `Process a loan disbursement transaction with the provided details.

- **member_id**: Member number for the transaction
- **cash_book_id**: ID of the cash book
- **accounts**: List of accounts involved in the transaction`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: LoanDisbursementRequest,
      },
    ],
    response: APIResponse_TransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/transaction/mobile_pos',
    alias: 'mobile_pos_transaction_api_transaction_mobile_pos_post',
    description: `Process mobile POS transactions.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.array(ReceiptRequest),
      },
    ],
    response: APIResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/mobile_pos_ui_data',
    alias: 'get_mobile_pos_ui_data_api_transaction_mobile_pos_ui_data_get',
    description: `Retrieve Mobile POS UI data.`,
    requestFormat: 'json',
    response: APIResponse_list_MobilePosUiDataResponse__,
  },
  {
    method: 'delete',
    path: '/api/transaction/mobile_pos/:transaction_id',
    alias:
      'delete_mobile_pos_transaction_api_transaction_mobile_pos__transaction_id__delete',
    description: `Delete a Mobile POS transaction.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'transaction_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_bool_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/passbook_data/:pl_account_id',
    alias:
      'get_passbook_data_api_transaction_passbook_data__pl_account_id__get',
    description: `Retrieve unprinted PL transactions for passbook printing.

- **pl_account_id**: ID of the PL account`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'pl_account_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_PlTransactionResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/passbook_policy/:passbook_id',
    alias:
      'get_passbook_policy_api_transaction_passbook_policy__passbook_id__get',
    description: `Retrieve passbook policy configuration by ID.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'passbook_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/transaction/pay-to-creditor',
    alias: 'pay_to_creditor_transaction_api_transaction_pay_to_creditor_post',
    description: `Process a withdrawal transaction.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: WithdrawRequest,
      },
    ],
    response: APIResponse_TransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/transaction/pl_account/:pl_account_id/reset-passbook',
    alias:
      'reset_passbook_tracking_api_transaction_pl_account__pl_account_id__reset_passbook_post',
    description: `Reset passbook page and line tracking for an account.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'pl_account_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_bool_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/pl_transaction_by_header/:gl_transaction_header_id/and_pl_account/:pl_account_id',
    alias:
      'get_pl_transaction_by_header_and_account_api_transaction_pl_transaction_by_header__gl_transaction_header_id__and_pl_account__pl_account_id__get',
    description: `Retrieve PL transaction by GL transaction header ID and PL account ID.

- **gl_transaction_header_id**: ID of the GL transaction header
- **pl_account_id**: ID of the PL account`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'gl_transaction_header_id',
        type: 'Path',
        schema: z.number().int(),
      },
      {
        name: 'pl_account_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PlTransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/pl_transaction/:pl_transaction_id',
    alias:
      'get_pl_transaction_by_id_api_transaction_pl_transaction__pl_transaction_id__get',
    description: `Retrieve PL transaction by ID.

- **pl_transaction_id**: ID of the PL transaction`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'pl_transaction_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PlTransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/pl_transactions/:transaction_header_id',
    alias:
      'get_pl_transactions_by_header_id_api_transaction_pl_transactions__transaction_header_id__get',
    description: `Retrieve PL transactions by transaction header ID.

- **transaction_header_id**: ID of the transaction header`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'transaction_header_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_PlTransactionResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/print_config/:pl_account_id',
    alias: 'get_print_config_api_transaction_print_config__pl_account_id__get',
    description: `Retrieve print policy configuration for an account.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'pl_account_id',
        type: 'Path',
        schema: z.number().int(),
      },
      {
        name: 'category',
        type: 'Query',
        schema: category,
      },
      {
        name: 'passbook_type',
        type: 'Query',
        schema: passbook_type,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/transaction/receipt',
    alias: 'receipt_transaction_api_transaction_receipt_post',
    description: `Process a transaction with the provided details.

- **member_id**: Member number for the transaction
- **cash_book_id**: ID of the cash book
- **accounts**: List of accounts involved in the transaction`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ReceiptRequest,
      },
    ],
    response: APIResponse_TransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/reverse_journal/:customer_id',
    alias:
      'get_reverse_journal_data_api_transaction_reverse_journal__customer_id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customer_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_ReverseJournalResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/transaction/reverse_journal/update',
    alias:
      'update_reverse_journal_transactions_api_transaction_reverse_journal_update_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.array(ReverseJournalUpdateRequest),
      },
    ],
    response: APIResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/transaction-type-print/:type_id',
    alias: 'get_config_api_transaction_transaction_type_print__type_id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'type_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_Union_dict__NoneType__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/transaction/transaction-type-print/:type_id',
    alias: 'save_config_api_transaction_transaction_type_print__type_id__put',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: 'type_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction/transaction-types',
    alias: 'get_types_api_transaction_transaction_types_get',
    requestFormat: 'json',
    response: APIResponse_list_ItTransactionTypeResponse__,
  },
  {
    method: 'get',
    path: '/api/transaction/types',
    alias: 'list_transaction_types_api_transaction_types_get',
    description: `Retrieve the list of transaction types.`,
    requestFormat: 'json',
    response: APIResponse_list_ItTransactionTypeResponse__,
  },
  {
    method: 'post',
    path: '/api/transaction/upload_cancellation_attachment',
    alias:
      'upload_cancellation_attachment_api_transaction_upload_cancellation_attachment_post',
    description: `Upload attachment for transaction cancellation.

- **file**: Supporting document (PDF, JPG, PNG)
- **transaction_number**: Transaction number being cancelled

Returns the saved file path for inclusion in the cancellation request`,
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          Body_upload_cancellation_attachment_api_transaction_upload_cancellation_attachment_post,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/transaction/withdraw',
    alias: 'withdraw_transaction_api_transaction_withdraw_post',
    description: `Process a withdrawal transaction.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: WithdrawRequest,
      },
    ],
    response: APIResponse_TransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/updated-version',
    alias: 'get_updated_version_api_updated_version_get',
    requestFormat: 'json',
    response: APIResponse_dict_,
  },
  {
    method: 'post',
    path: '/api/users/:user_id/activate',
    alias: 'activate_user_api_users__user_id__activate_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_bool_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/:user_id/allowances',
    alias: 'get_user_allowances_api_users__user_id__allowances_get',
    description: `Get all transaction allowances for a specific user.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/:user_id/deactivate',
    alias: 'deactivate_user_api_users__user_id__deactivate_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_bool_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/:user_id/menu_list',
    alias: 'get_user_menu_list_api_users__user_id__menu_list_get',
    description: `Get all menus assigned to a user with full details.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_UserMenuResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/:user_id/menus',
    alias: 'get_user_menus_api_users__user_id__menus_get',
    description: `Get all menu IDs for a specific user where status&#x3D;True`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_int__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/:user_id/permission_list',
    alias: 'get_user_permission_list_api_users__user_id__permission_list_get',
    description: `Get all permissions assigned to a user with full details.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_UserPermissionResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/:user_id/permissions',
    alias: 'get_user_permissions_api_users__user_id__permissions_get',
    description: `Get all permission IDs for a specific user where status&#x3D;True`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_int__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/:user_id/reports',
    alias: 'get_user_reports_api_users__user_id__reports_get',
    description: `Get all report IDs for a specific user where status&#x3D;1`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_int__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/:user_id/reset_password',
    alias: 'reset_user_password_api_users__user_id__reset_password_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_str_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/:user_id/send_offline',
    alias: 'send_user_offline_api_users__user_id__send_offline_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_bool_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/all',
    alias: 'get_all_users_api_users_all_get',
    requestFormat: 'json',
    response: APIResponse_list_UserWithActiveStatusResponse__,
  },
  {
    method: 'post',
    path: '/api/users/create',
    alias: 'create_new_user_api_users_create_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UserCreateRequest,
      },
    ],
    response: APIResponse_UserCreateResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/users/delete_user/:user_id',
    alias: 'delete_user_api_users_delete_user__user_id__delete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/favourite_menu_list',
    alias: 'get_user_favourite_menu_list_api_users_favourite_menu_list_get',
    description: `Get all menus assigned to a user with full details.`,
    requestFormat: 'json',
    response: APIResponse_list_UserMenuResponse__,
  },
  {
    method: 'get',
    path: '/api/users/groups/all',
    alias: 'get_all_user_groups_api_users_groups_all_get',
    requestFormat: 'json',
    response: APIResponse_list_UserGroupsResponse__,
  },
  {
    method: 'get',
    path: '/api/users/menu_list',
    alias: 'get_current_user_menu_list_api_users_menu_list_get',
    description: `Get all menus for the current user with full details.`,
    requestFormat: 'json',
    response: APIResponse_list_UserMenuResponse__,
  },
  {
    method: 'get',
    path: '/api/users/permission_list',
    alias: 'get_current_user_permission_list_api_users_permission_list_get',
    description: `Get all permissions for the current user with full details.`,
    requestFormat: 'json',
    response: APIResponse_list_UserPermissionResponse__,
  },
  {
    method: 'put',
    path: '/api/users/update/:user_id',
    alias: 'update_user_api_users_update__user_id__put',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UserCreateRequest,
      },
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_UserCreateResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/upldate_favourite_menu',
    alias: 'update_user_favourite_menu_api_users_upldate_favourite_menu_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateFavoriteMenuRequest,
      },
    ],
    response: APIResponse_UpdateFavouriteMenuResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/v1/unified-transaction/',
    alias: 'unified_transaction_endpoint_api_v1_unified_transaction__post',
    description: `Unified endpoint for processing Receipt, Payment, Withdrawal, and Journal transactions.

This endpoint validates the &#x60;transaction_type&#x60; and the structure of the &#x60;payload&#x60;
before dispatching the request to the appropriate service logic.

**Transaction Types:**
- RECEIPT: Cash/cheque receipt transactions
- PAYMENT: Payment transactions  
- WITHDRAWAL: Cash withdrawal transactions
- JOURNAL: Journal entry transactions

**Workflow:**
1. Validate payload structure and amounts
2. Check user transaction allowances
3. Determine if approval is required based on amount limits
4. Either execute directly or submit for approval

**Request Format:**
&#x60;&#x60;&#x60;json
{
    &quot;transaction_type&quot;: &quot;RECEIPT&quot;,
    &quot;totalAmount&quot;: 1000.00,
    &quot;payload&quot;: {
        &quot;member_id&quot;: 123,
        &quot;cash_book_id&quot;: 1,
        &quot;accounts&quot;: [...],
        &quot;total_amount&quot;: 1000.00
    }
}
&#x60;&#x60;&#x60;`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CommonTransactionRequest,
      },
    ],
    response: APIResponse_UnifiedTransactionResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/v1/unified-transaction/action',
    alias: 'unified_action_endpoint_api_v1_unified_transaction_action_post',
    description: `Combined endpoint for all transaction operations.

Set &#x60;action&#x60; to choose the operation:

| &#x60;action&#x60;  | Description                                          |
|-----------|------------------------------------------------------|
| &#x60;SUBMIT&#x60;  | Submit a new transaction or resubmit an existing one |
| &#x60;APPROVE&#x60; | Approve a pending transaction from the queue         |
| &#x60;REJECT&#x60;  | Reject a pending transaction from the queue          |
| &#x60;EXECUTE&#x60; | Execute a fully-approved transaction from cache      |

---
**SUBMIT**
&#x60;&#x60;&#x60;json
{ &quot;action&quot;: &quot;SUBMIT&quot;, &quot;transaction_type&quot;: &quot;RECEIPT&quot;,
  &quot;totalAmount&quot;: 1000.0, &quot;payload&quot;: { ... }, &quot;uuid&quot;: null }
&#x60;&#x60;&#x60;
**APPROVE / REJECT**
&#x60;&#x60;&#x60;json
{ &quot;action&quot;: &quot;APPROVE&quot;, &quot;transaction_id&quot;: &quot;&lt;uuid&gt;&quot;,
  &quot;msg_id&quot;: &quot;&lt;stream-id&gt;&quot;, &quot;approval_level&quot;: 1, &quot;notes&quot;: &quot;ok&quot; }
&#x60;&#x60;&#x60;
**EXECUTE**
&#x60;&#x60;&#x60;json
{ &quot;action&quot;: &quot;EXECUTE&quot;, &quot;transaction_id&quot;: &quot;&lt;uuid&gt;&quot;,
  &quot;transaction_type&quot;: &quot;RECEIPT&quot; }
&#x60;&#x60;&#x60;`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UnifiedActionRequest,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/v1/unified-transaction/approval',
    alias:
      'process_transaction_approval_api_v1_unified_transaction_approval_post',
    description: `Approve or Reject a transaction.

**Actions:**
- APPROVE: Approve the transaction and move to next level or execute
- REJECT: Reject the transaction

**Request Format:**
&#x60;&#x60;&#x60;json
{
    &quot;transaction_id&quot;: &quot;uuid&quot;,
    &quot;action&quot;: &quot;APPROVE&quot;,
    &quot;approval_level&quot;: 1,
    &quot;notes&quot;: &quot;Optional notes&quot;
}
&#x60;&#x60;&#x60;

**Workflow:**
1. Validate user has approval permission for this level
2. For APPROVE: Move to next level or execute if final level
3. For REJECT: Mark transaction as rejected
4. Update transaction status and log action`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ApprovalRequest,
      },
    ],
    response: APIResponse_ApprovalProcessResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/v1/unified-transaction/approval/levels',
    alias: 'get_approval_levels_api_v1_unified_transaction_approval_levels_get',
    description: `Get available approval levels for the current user.

Returns the approval levels that the current user can act on
based on their transaction allowances and permissions.`,
    requestFormat: 'json',
    response: APIResponse_dict_,
  },
  {
    method: 'get',
    path: '/api/v1/unified-transaction/approval/pending',
    alias:
      'get_pending_approvals_api_v1_unified_transaction_approval_pending_get',
    description: `Get list of transactions pending approval for a specific level.

**Parameters:**
- level: Approval level (1, 2, or 3)

**Returns:**
- List of transactions pending approval at the specified level
- Transaction details including amount, submitter, and payload data`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'level',
        type: 'Query',
        schema: z.number().int().gte(1).lte(3).optional().default(1),
      },
    ],
    response: APIResponse_PendingApprovalsListResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/v1/unified-transaction/cache/:transaction_type/:state',
    alias:
      'get_user_cached_transactions_api_v1_unified_transaction_cache__transaction_type___state__get',
    description: `Get cached transactions for a user.
States: pending, approve, reject`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'transaction_type',
        type: 'Path',
        schema: z.enum(['RECEIPT', 'PAYMENT', 'WITHDRAWAL', 'JOURNAL']),
      },
      {
        name: 'state',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/v1/unified-transaction/execute-approved',
    alias:
      'execute_approved_transaction_endpoint_api_v1_unified_transaction_execute_approved_post',
    description: `Manually execute a fully approved transaction from the user&#x27;s approve cache.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ExecuteApprovedRequest,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/v1/unified-transaction/status/:transaction_id',
    alias:
      'get_transaction_status_api_v1_unified_transaction_status__transaction_id__get',
    description: `Get the current status of a transaction.

Returns transaction status, approval history, and current approval level
if the transaction is pending approval.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'transaction_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/cache/health',
    alias: 'cache_health_cache_health_get',
    description: `Check Redis cache health and statistics.
Returns cache connection status and performance metrics.`,
    requestFormat: 'json',
    response: APIResponse_dict_,
  },
  {
    method: 'get',
    path: '/cache/test',
    alias: 'cache_test_cache_test_get',
    description: `Test endpoint to demonstrate caching.
Call this multiple times and watch the logs!

First call: 🔵 DATABASE QUERY (simulated)
Subsequent calls: 🔴 REDIS HIT`,
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/health',
    alias: 'health_check_health_get',
    requestFormat: 'json',
    response: APIResponse_dict_,
  },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
